import './MeetingSchedulingMeetingDetailsForm.scss';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { Input } from '@availity/form';
import { Formik } from 'formik';
import {
    Button,
    Col,
    InputGroup,
    InputGroupAddon,
    Label,
    Row,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import { TimeZone, TimeZoneHelper } from '../../../../utils/timeZoneHelper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarAlt,
    faDownload,
    faTrash,
    faEdit,
    faCheck,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { DateTimeHelper } from '../../../../utils/dateTimeHelper';
import StateSelectField from '../../../../components/FormFields/StateSelectField/StateSelectField';
import { stateToCodeMapper } from '../../../../utils/locationsHelper';
import { Multiselect } from 'react-widgets';
import ReactSwitch from 'react-switch';
import { ArrowDown } from '../../../../components/arrow-down/arrow-down';
import { EngagementBuyer } from '../../../../slices/marketplace/timeline/models/engagement-buyer.model';
import {
    MeetingFormat,
    MeetingFormatTypesOptions,
} from '../../../../slices/marketplace/meetings/models/enums/meeting-format.enum';
import { MeetingProposalStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-proposal-status.enum';
import { EngagementMeetingStatus } from '../../../../slices/marketplace/meetings/models/enums/meeting-status.enum';
import {
    TeamMemberType,
    InternalPermissions,
    ExternalPermissions,
} from '../../../../containers/Admin/BuyerMarketplace/MyTeamMembers/Models/Enums';
import { ConferenceMeetingPopup } from '../../../PracticeManagementGroups/Conferences/ConferenceMeetingPopup';
import { VideoPlayerPopup } from '../../../VideoPlayerPopup/VideoPlayerPopup';
import {
    VideoClip,
    VideoClipPopup,
} from '../../../PracticeManagementGroups/Conferences/SaveVideoClipPopup/SaveVideoClipPopup';
import createNotification from '../../../../utils/createNotification';
import { useStateSelector } from '../../../../store/selectors';
import { TimelineActionType } from '../../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventStatus } from '../../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementProcessStatus } from '../../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { TimelineEvent } from '../../../../slices/marketplace/timeline/models/timeline-event.model';
import MeetingClipsModal from './MeetingClipsModal/MeetingClipsModal';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import RefundMeetingModal from './RefundMeetingModal/RefundMeetingModal';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import { USER_ROLES } from '../../../../utils/constants';
import { useCurrentMarketplaceParty } from '../../../../custom-hooks/useCurrentMarketplaceParty';
import { useFeatureFlag } from '../../../../custom-hooks/useFeatureFlag';
import { FeatureFlagsEnum } from '../../../FeatureFlags/FeatureFlags.enum';
import { TimelineParty } from '../../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { LevelOfService } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { MeetingParticipantsType } from '../../../../slices/marketplace/meetings/models/enums/meeting-participant-type.enum';
import { TimePickerField } from './TimePickerField/TimePickerField';
import {
    MeetingTypeSelector,
    MeetingType,
} from './MeetingTypeSelector/MeetingTypeSelector';
import { BuyersSelector, Buyer } from './BuyersSelector/BuyersSelector';
import BuyerProfilePreviewPopup from '../../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import SaveMeetingCardModal, { MeetingPaymentMethodData } from '../SaveMeetingCardModal/SaveMeetingCardModal';
import ConfirmationDialogWithReason from '../../../../components/ConfirmationDialog/confirmation-dialog-with-reason';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { CalendarObject } from '../../../../slices/marketplace/meetings/models/calendar-object.model';
import { MeetingObject } from '../../../../slices/marketplace/meetings/models/meeting-object.model';
import { AvailabilityBlockObject } from '../../../../slices/marketplace/meetings/models/availability-block-object.model';
import { CalendarObjectAddressContract } from '../../../../slices/marketplace/meetings/models/engagement-meeting-proposal-address-contract';
import {
    isAvailabilityBlockObject,
    canBuyerTeamMemberManageMeetings,
    canSellerTeamMemberManageMeetings,
    isSellerTeamMemberForEngagement,
} from '../../../../utils/meetingSchedulingHelper';
import {
    MeetingLocationEditForm,
    MeetingLocationEditFormValues,
} from './MeetingLocationEditForm/MeetingLocationEditForm';
import EngagementMeetingCancelModal from '../MeetingShedulingWeekView/EngagementMeetingCancelModal/EngagementMeetingCancelModal';

function isGhostMeetingObject(obj?: CalendarObject | null): boolean {
    if (!obj || isAvailabilityBlockObject(obj)) return false;
    const meeting = obj as MeetingObject;
    return meeting.isOtherGhostMeeting === true;
}

interface MeetingSchedulingMeetingDetailsFormProps {
    startDate: Date;
    endDate: Date;
    selectedDate: Date;
    onDeselectDate?: () => void;
    selectedEvent?: CalendarObject;
    onDeselectEvent?: () => void;
    selectedBuyers?: EngagementBuyer[];
    forceVisible?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    mode: 'buyerTopLevel' | 'sellerTopLevel' | 'engagement' | 'opportunity';
    viewMode: 'availability' | 'meetingSeller' | 'meetingBuyer';
    onRefreshMeetings?: (
        createdBlockId?: string,
        createdProposalId?: string,
        createdMeetingId?: string
    ) => void;
    clickedTimeSlot?: { startTime: Date; endTime: Date };
    onEditModeChange?: (isEditMode: boolean) => void;
    onDateReset?: (originalDate: Date) => void;
    /** When set (buyer AgreeOnMeetingTimes phase), used for mismatch highlighting later. */
    referenceAvailabilityBlock?: AvailabilityBlockObject | null;
    /** Copy of the block for the form to work on; max meeting length is read from this. When set, selecting a meeting type shows length from this copy or 0:00. */
    availabilityBlockFormCopy?: AvailabilityBlockObject | null;
    /** When seller/buyer is editing a proposal and clicked an availability block, use this block to populate form values (times, formats, address). */
    blockToPopulateFormWhenEditingProposal?: AvailabilityBlockObject | null;
    /** When form is open with no block selected (e.g. user clicked a day), use first block on this date for mismatch validation. */
    availabilityBlockForSelectedDate?: AvailabilityBlockObject | null;
    /** Engagement id for API calls (e.g. Schedule Meeting). */
    engagementId?: number;
    /** All calendar objects (meetings, proposals, etc.) for overlap detection. */
    calendarObjects?: CalendarObject[];
    /** All availability blocks for dynamic lookup when date/time changes (buyer agree phase). */
    availabilityBlocksForLookup?: AvailabilityBlockObject[];
    /** Called when form date/time changes: pass the block that intersects the form range, or null if none. */
    onSelectBlockByDateTime?: (block: AvailabilityBlockObject | null) => void;
    /** When true, buyer has a pending proposal (waiting for seller); show Proposed Meeting view and disable calendar selection. */
    buyerHasPendingProposal?: boolean;
    /** When true, there's a confirmed meeting; show Confirmed Meeting info view. */
    hasConfirmedMeeting?: boolean;
    /** Called when buyer clicks "Propose New Meeting" from the pending proposal view. */
    onProposeNewMeeting?: () => void;
    onAcceptProposal?: (meetingProposalId: string) => Promise<void>;
    onBuyerAcceptProposal?: (proposalId: string) => void;
    /** Called when buyer clicks "Cancel Meeting" to cancel the proposal; pass meetingProposalId and optional reason. */
    onCancelProposal?: (meetingProposalId: string, reason?: string) => Promise<void>;
    /** Called when admin cancels a confirmed meeting (issues full refund via RefundBuyerSellerMeetingFee). */
    onCancelConfirmedMeeting?: (meetingProposalId: string, reason: string) => Promise<void>;
    /** Prefill data for new meeting proposals when user clicks "Propose New Meeting" */
    meetingPrefillData?: any;
    /** Called to clear prefill data after form is initialized */
    onClearPrefillData?: () => void;
    /** When set, form should enter edit mode for the selected event matching this id (e.g. after edit icon on meeting tile) */
    requestEditModeForEventId?: number | string | null;
    /** Called when user confirms a refund on a cancelled meeting */
    onRefund?: (amount: number) => Promise<void>;
}

const DateControlInput = forwardRef(({ value, onClick }: any, ref: any) => {
    return (
        <div
            ref={ref}
            className={'btn date-control-input'}
            onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                if (onClick) {
                    onClick(e);
                }
            }}>
            {value || 'Select time'}
        </div>
    );
});

export const MeetingSchedulingMeetingDetailsForm = (
    props: MeetingSchedulingMeetingDetailsFormProps
) => {
    const navigate = useNavigate();
    const axios = useStateSelector((state) => state.core.axios);
    const summary = useStateSelector((state) => state.userSummary.summary);
    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities?.selectedOpportuity
    );
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const isEngagementLocked = (() => {
        const status = currentEngagement?.engagementProcess?.status;
        return (
            status === EngagementProcessStatus.Suspended ||
            status === EngagementProcessStatus.Terminated ||
            status === EngagementProcessStatus.Completed
        );
    })();
    const availabilityBlocks = useStateSelector(
        (state) => state.engagementMeetings.availabilityBlocks
    );
    const pricing = useStateSelector(
        (state) => state.marketplacePricing.pricing
    );
    const party = useCurrentMarketplaceParty(props.selectedEvent?.engagementId);
    const isCancellationFeesEnabled = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcSellers_2_14
    );
    const formRef = useRef<any>();

    const isAdmin = summary?.roleId === USER_ROLES.ADMIN;
    const isSfas = (currentEngagement?.levelOfService ?? selectedOpportunity?.levelOfService) === LevelOfService.StrategicFinancialAdvisoryService;
    const isBroker = summary?.isMarketplaceBroker;
    const isObserver = party === TimelineParty.Observer;
    const startDatePickerRef = useRef(null);

    const hasMeetingStarted = (event?: CalendarObject | null): boolean => {
        if (!event) return false;
        const meetingStartTime = event.dateStart
            ? event.dateStart instanceof Date
                ? event.dateStart
                : new Date(event.dateStart)
            : null;
        return meetingStartTime ? meetingStartTime <= new Date() : false;
    };
    const endDatePickerRef = useRef(null);
    /** Keep last block used for validation so we don't lose orange highlight when a render temporarily has no block (e.g. parent state update). */
    const lastBlockForValidationRef = useRef<AvailabilityBlockObject | null>(
        null
    );
    /** Latest form start/end time for dynamic block lookup (updated in Formik render). */
    const formDateTimeRef = useRef<{ startTime: string; endTime: string }>({
        startTime: '',
        endTime: '',
    });
    /** Bump when start/end time changes so effect can re-run block lookup. */
    const [formDateTimeVersion, setFormDateTimeVersion] = useState(0);
    const [canBuyerTMJoinMeeting, setCanBuyerTMJoinMeeting] = useState(true);
    const roundUpToNext15Minutes = (date: Date): Date => {
        const roundedDate = new Date(date);
        const minutes = roundedDate.getMinutes();
        const roundedMinutes = Math.ceil(minutes / 15) * 15;

        if (roundedMinutes === 60) {
            roundedDate.setHours(roundedDate.getHours() + 1);
            roundedDate.setMinutes(0);
        } else {
            roundedDate.setMinutes(roundedMinutes);
        }

        roundedDate.setSeconds(0);
        roundedDate.setMilliseconds(0);

        return roundedDate;
    };

    const hasEngagementMeetingsFunctionalAccess = (): boolean => {
        if (!selectedOpportunity) {
            return false;
        }

        if (isAdmin) {
            return true;
        }

        if (isBroker) {
            return true;
        }

        if (selectedOpportunity.isPrimaryBuyer) {
            return true;
        }

        if (party === TimelineParty.Seller) {
            return true;
        }

        if (selectedOpportunity.teamMemberType === TeamMemberType.Internal) {
            return (
                selectedOpportunity.internalPermissions ===
                    InternalPermissions.ReadOnlyAndCommunicate ||
                selectedOpportunity.internalPermissions ===
                    InternalPermissions.ReadOnlyAndCommunicateAndExecute
            );
        } else if (
            selectedOpportunity.teamMemberType === TeamMemberType.External
        ) {
            return (
                selectedOpportunity.externalPermissions ===
                    ExternalPermissions.VdrAndCommunicate
            );
        }

        return false;
    };

    const hasGlobalMeetingAccess = (): boolean => {
        if (isAdmin || isBroker) {
            return true;
        }

        if (party === TimelineParty.Seller) {
            return true;
        }

        if (
            party === TimelineParty.Buyer &&
            summary?.roleId === USER_ROLES.USER
        ) {
            return true;
        }

        if (party === TimelineParty.Buyer && summary?.associateMember?.length) {
            const associateMember = summary.associateMember[0];
            if (!associateMember?.associateMemberMarketplaceMappings?.length) {
                return false;
            }

            const globalMapping =
                associateMember.associateMemberMarketplaceMappings.find(
                    (mapping: any) => mapping.opportunityId == null
                );

            if (!globalMapping) {
                return false;
            }

            if (globalMapping.type === TeamMemberType.Internal) {
                return (
                    globalMapping.internalPermissions !==
                    InternalPermissions.ReadOnly
                );
            } else if (globalMapping.type === TeamMemberType.External) {
                return (
                    globalMapping.externalPermissions !==
                    ExternalPermissions.Vdr
                );
            }
        }

        return false;
    };

    const canViewRecording = (): boolean => {
        if (props.selectedEvent?.engagementId) {
            return hasEngagementMeetingsFunctionalAccess();
        } else {
            return hasGlobalMeetingAccess();
        }
    };

    const roundedSelectedDateNow = roundUpToNext15Minutes(
        props.selectedDate || new Date()
    );
    const roundedOneHourLater = new Date(
        roundedSelectedDateNow.getTime() + 60 * 60 * 1000
    );

    const initialStartDate = new Date(roundedSelectedDateNow);
    initialStartDate.setHours(roundedSelectedDateNow.getHours());
    initialStartDate.setMinutes(roundedSelectedDateNow.getMinutes());
    initialStartDate.setSeconds(0);
    initialStartDate.setMilliseconds(0);

    const initialEndDate = new Date(roundedOneHourLater);
    initialEndDate.setHours(roundedOneHourLater.getHours());
    initialEndDate.setMinutes(roundedOneHourLater.getMinutes());
    initialEndDate.setSeconds(0);
    initialEndDate.setMilliseconds(0);

    const initialStartTimeZone =
        TimeZoneHelper.getTimeZoneByDate(initialStartDate);
    const initialEndTimeZone = TimeZoneHelper.getTimeZoneByDate(initialEndDate);
    const startDateWithOffset = TimeZoneHelper.parseZoneDate(
        initialStartDate,
        initialStartTimeZone
    );
    const endDateWithOffset = TimeZoneHelper.parseZoneDate(
        initialEndDate,
        initialEndTimeZone
    );

    const [startDate, setStartDate] = useState<Date>(startDateWithOffset);
    const [startTimeZone, setStartTimeZone] =
        useState<TimeZone>(initialStartTimeZone);
    const [startDateTimeState, setStartDateTimeState] = useState({
        time: true,
        zone: true,
    });

    const [endDate, setEndDate] = useState<Date>(endDateWithOffset);
    const [endTimeZone, setEndTimeZone] =
        useState<TimeZone>(initialEndTimeZone);
    const [endDateTimeState, setEndDateTimeState] = useState({
        time: true,
        zone: true,
    });

    const [dateErrorMessage, setDateErrorMessage] = useState<
        string | undefined
    >();

    const [isBuyersListOpened, setIsBuyersListOpened] = useState(false);
    const [buyerSelectionError, setBuyerSelectionError] = useState(false);
    const [meetingTypeError, setMeetingTypeError] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaveCardModalVisible, setIsSaveCardModalVisible] = useState(false);
    const [pendingProposalBody, setPendingProposalBody] = useState<Record<
        string,
        unknown
    > | null>(null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [
        isConferenceMeetingPopupVisible,
        setIsConferenceMeetingPopupVisible,
    ] = useState(false);
    const [videoSource, setVideoSource] = useState<string>();
    const [videoClip, setVideoClip] = useState<VideoClip>();
    const [isClipsPopupVisible, setIsClipsPopupVisible] = useState(false);
    const [loadingTrigger, setLoadingTrigger] = useState(0);
    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);
    const [selectedBuyerId, setSelectedBuyerId] = useState<number>(0);
    const [showAcceptProposalConfirmation, setShowAcceptProposalConfirmation] =
        useState(false);
    const [showCancelProposalConfirmation, setShowCancelProposalConfirmation] =
        useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showCancelConfirmedMeetingModal, setShowCancelConfirmedMeetingModal] = useState(false);
    const [cancelConfirmedMeetingReason, setCancelConfirmedMeetingReason] = useState('');
    const [isCancelConfirmedMeetingLoading, setIsCancelConfirmedMeetingLoading] = useState(false);

    // Clear prefill data when user selects an event (not when selection was cleared for "Propose New Meeting")
    useEffect(() => {
        if (props.selectedEvent && props.meetingPrefillData) {
            props.onClearPrefillData?.();
        }
    }, [
        props.selectedEvent,
        props.meetingPrefillData,
        props.onClearPrefillData,
    ]);

    const EditableDescriptionField = ({
        message,
        meetingProposalId,
        meetingEngagementId,
        participantsType,
    }: {
        message?: string;
        meetingProposalId?: string;
        meetingEngagementId?: number;
        participantsType?: MeetingParticipantsType;
    }) => {
        const [isEditing, setIsEditing] = useState(false);
        const [editedValue, setEditedValue] = useState('');
        const [isSaving, setIsSaving] = useState(false);

        const isSameEngagement = meetingEngagementId === props.engagementId;
        const isSellerBuyerMeeting =
            participantsType === MeetingParticipantsType.SellerBuyer;

        const canEdit =
            !isAdmin &&
            !isBroker &&
            !isObserver &&
            !(props.selectedEvent as MeetingObject)?.isCancelled &&
            props.mode !== 'buyerTopLevel' &&
            (canBuyerTeamMemberManageMeetings(summary, props.engagementId) ||
                canSellerTeamMemberManageMeetings(
                    summary,
                    props.engagementId
                )) &&
            isSameEngagement &&
            isSellerBuyerMeeting;

        useEffect(() => {
            setIsEditing(false);
            setEditedValue('');
        }, [meetingProposalId]);

        const handleEditClick = () => {
            setIsEditing(true);
            setEditedValue(message || '');
        };

        const handleCancelEdit = () => {
            setIsEditing(false);
            setEditedValue(message || '');
        };

        const handleSaveClick = async () => {
            const engagementId = props.engagementId ?? currentEngagement?.id;
            if (!meetingProposalId || !engagementId) {
                createNotification(
                    'Missing proposal ID or engagement ID',
                    'error'
                );
                return;
            }

            setIsSaving(true);
            try {
                const response = await axios.put(
                    `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling/${meetingProposalId}/Description`,
                    { Description: editedValue }
                );
                if (response.status === 200 || response.status === 204) {
                    createNotification(
                        'Description updated successfully',
                        'success'
                    );
                    setIsEditing(false);
                    props.onRefreshMeetings?.();
                } else {
                    createNotification('Failed to update description', 'error');
                }
            } catch (error: any) {
                console.error('Error updating description:', error);
                createNotification(
                    error.response?.data?.message ||
                        'Failed to update description',
                    'error'
                );
            } finally {
                setIsSaving(false);
            }
        };

        return (
            <div className="proposed-meeting-row proposed-meeting-row-description">
                <div className="editable-description-header">
                    <span className="proposed-meeting-label">Description:</span>
                    {canEdit && !isEditing && (
                        <FontAwesomeIcon
                            icon={faEdit}
                            className="editable-description-icon edit-icon"
                            onClick={handleEditClick}
                            title="Edit description"
                        />
                    )}
                    {canEdit && isEditing && (
                        <>
                            <FontAwesomeIcon
                                icon={faCheck}
                                className={`editable-description-icon save-icon${(editedValue || '') === (message || '') ? ' description-save-disabled' : ''}`}
                                onClick={() => {
                                    if (
                                        (editedValue || '') !== (message || '')
                                    ) {
                                        handleSaveClick();
                                    }
                                }}
                                title={
                                    (editedValue || '') !== (message || '')
                                        ? 'Save description'
                                        : 'No changes to save'
                                }
                            />
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="editable-description-icon cancel-edit-icon"
                                onClick={handleCancelEdit}
                                title="Cancel"
                            />
                        </>
                    )}
                </div>
                {!isEditing ? (
                    message ? (
                        <span className="proposed-meeting-value">
                            {message}
                        </span>
                    ) : (
                        <span className="proposed-meeting-value editable-description-empty">
                            No description
                        </span>
                    )
                ) : (
                    <textarea
                        name="description"
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        disabled={isSaving}
                        maxLength={140}
                        placeholder="Enter description here"
                        className="form-control editable-description-textarea"
                        rows={3}
                    />
                )}
            </div>
        );
    };

    const formatAddressForTooltip = (addr: CalendarObjectAddressContract) => {
        const parts = [addr.city, addr.state, addr.address].filter(Boolean);
        return parts.length ? parts.join(', ') : 'No address';
    };

    const EditableAddressField = ({
        address,
        alternativeAddress,
        confirmedAddress,
        dateStart,
        meetingProposalId,
        meetingEngagementId,
        participantsType,
        proposalStatus,
    }: {
        address?: CalendarObjectAddressContract;
        alternativeAddress?: CalendarObjectAddressContract;
        confirmedAddress?: CalendarObjectAddressContract;
        dateStart?: Date | string;
        meetingProposalId?: string;
        meetingEngagementId?: number;
        participantsType?: MeetingParticipantsType;
        proposalStatus?: MeetingProposalStatus;
    }) => {
        const locationFormSubmitRef = useRef<(() => void) | null>(null);

        const [isEditing, setIsEditing] = useState(false);
        const [isSaving, setIsSaving] = useState(false);
        const [hasLocationChanges, setHasLocationChanges] = useState(false);
        const [showApproveModal, setShowApproveModal] = useState(false);
        const [showDeclineModal, setShowDeclineModal] = useState(false);
        const [isApproving, setIsApproving] = useState(false);
        const [isDeclining, setIsDeclining] = useState(false);

        const isSameEngagement = meetingEngagementId === props.engagementId;
        const isSellerBuyerMeeting =
            participantsType === MeetingParticipantsType.SellerBuyer;

        const meetingStartDate = dateStart
            ? dateStart instanceof Date
                ? dateStart
                : new Date(dateStart)
            : null;
        const meetingNotStarted = meetingStartDate
            ? meetingStartDate > new Date()
            : true;

        // Same party/TM rules as EditableDescriptionField: seller/buyer + canBuyerTeamMemberManageMeetings for buyer TMs
        const canEdit =
            !isAdmin &&
            !isBroker &&
            !(props.selectedEvent as MeetingObject)?.isCancelled &&
            props.mode !== 'buyerTopLevel' &&
            (canBuyerTeamMemberManageMeetings(summary, props.engagementId) ||
                canSellerTeamMemberManageMeetings(
                    summary,
                    props.engagementId
                )) &&
            isSameEngagement &&
            isSellerBuyerMeeting &&
            proposalStatus === MeetingProposalStatus.Confirmed &&
            !alternativeAddress &&
            meetingNotStarted;

        // Other party proposed the alternative address; current party needs to respond
        const pendingMyResponse =
            !!alternativeAddress &&
            alternativeAddress.proposedByParty !== party;

        const canApproveDeclineLocation =
            !isAdmin &&
            !isBroker &&
            !(props.selectedEvent as MeetingObject)?.isCancelled &&
            pendingMyResponse &&
            (party !== TimelineParty.Buyer ||
                canBuyerTeamMemberManageMeetings(summary, props.engagementId) ||
                canSellerTeamMemberManageMeetings(summary, props.engagementId));

        useEffect(() => {
            setIsEditing(false);
            setHasLocationChanges(false);
        }, [meetingProposalId]);

        const handleEditClick = () => {
            setIsEditing(true);
            setHasLocationChanges(false);
        };

        const handleCancelEdit = () => {
            setIsEditing(false);
        };

        const handleLocationValuesChange = useCallback(
            (values: MeetingLocationEditFormValues) => {
                const origCity = (address?.city || '').trim();
                const origState = (address?.state || '').trim();
                const origAddress = (address?.address || '').trim();
                const same =
                    (values.city || '').trim() === origCity &&
                    (values.state || '').trim() === origState &&
                    (values.address || '').trim() === origAddress;
                setHasLocationChanges(!same);
            },
            [address?.city, address?.state, address?.address]
        );

        const handleLocationSubmit = async (
            values: MeetingLocationEditFormValues
        ) => {
            const engagementId = props.engagementId ?? currentEngagement?.id;
            if (!meetingProposalId || !engagementId) {
                createNotification(
                    'Missing proposal ID or engagement ID',
                    'error'
                );
                return;
            }
            setIsSaving(true);
            try {
                const response = await axios.post(
                    `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling/${meetingProposalId}/AlternativeAddress`,
                    {
                        City: values.city,
                        State: values.state,
                        Address: values.address || null,
                    }
                );
                if (response.status === 200 || response.status === 204) {
                    createNotification(
                        'Location updated successfully',
                        'success'
                    );
                    setIsEditing(false);
                    props.onRefreshMeetings?.();
                } else {
                    createNotification('Failed to update location', 'error');
                }
            } catch (error: any) {
                console.error('Error updating location:', error);
                createNotification(
                    error.response?.data?.message ||
                        'Failed to update location',
                    'error'
                );
            } finally {
                setIsSaving(false);
            }
        };

        const handleApprove = async () => {
            const engagementId = props.engagementId ?? currentEngagement?.id;
            if (!meetingProposalId || !engagementId) return;
            setIsApproving(true);
            try {
                const response = await axios.post(
                    `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling/${meetingProposalId}/AlternativeAddress/Approve`
                );
                if (response.status === 200 || response.status === 204) {
                    createNotification('Location approved', 'success');
                    props.onRefreshMeetings?.();
                } else {
                    createNotification('Failed to approve location', 'error');
                }
            } catch (error: any) {
                createNotification(
                    error.response?.data?.message ||
                        'Failed to approve location',
                    'error'
                );
            } finally {
                setIsApproving(false);
            }
        };

        const handleDecline = async () => {
            const engagementId = props.engagementId ?? currentEngagement?.id;
            if (!meetingProposalId || !engagementId) return;
            setIsDeclining(true);
            try {
                const response = await axios.post(
                    `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling/${meetingProposalId}/AlternativeAddress/Decline`
                );
                if (response.status === 200 || response.status === 204) {
                    createNotification('Location declined', 'success');
                    props.onRefreshMeetings?.();
                } else {
                    createNotification('Failed to decline location', 'error');
                }
            } catch (error: any) {
                createNotification(
                    error.response?.data?.message ||
                        'Failed to decline location',
                    'error'
                );
            } finally {
                setIsDeclining(false);
            }
        };

        if (!address && !canEdit && !alternativeAddress) return null;

        const valueClass = [
            'proposed-meeting-value',
            alternativeAddress ? 'location-value-pending' : '',
            pendingMyResponse ? 'location-value-blink' : '',
        ]
            .filter(Boolean)
            .join(' ');

        const confirmedAddressTooltip =
            alternativeAddress && confirmedAddress
                ? `Currently confirmed: ${formatAddressForTooltip(confirmedAddress)}`
                : undefined;

        return (
            <div className="proposed-meeting-location-section">
                <div className="editable-address-header proposed-meeting-row">
                    <span className="proposed-meeting-label">Location:</span>
                    {confirmedAddressTooltip && (
                        <InfoTooltip
                            text={confirmedAddressTooltip}
                            idText={`location-confirmed-${meetingProposalId ?? 'unknown'}`}
                            place="right"
                            className="location-confirmed-tooltip"
                        />
                    )}
                    {canEdit && !isEditing && (
                        <FontAwesomeIcon
                            icon={faEdit}
                            className="editable-description-icon edit-icon"
                            onClick={handleEditClick}
                            title="Edit location"
                        />
                    )}
                    {canEdit && isEditing && (
                        <>
                            <FontAwesomeIcon
                                icon={faCheck}
                                className={`editable-description-icon save-icon${!hasLocationChanges ? ' location-save-disabled' : ''}`}
                                onClick={() => {
                                    if (!hasLocationChanges) return;
                                    locationFormSubmitRef.current?.();
                                }}
                                title={
                                    hasLocationChanges
                                        ? 'Save location'
                                        : 'No changes to save'
                                }
                            />
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="editable-description-icon cancel-edit-icon"
                                onClick={handleCancelEdit}
                                title="Cancel"
                            />
                        </>
                    )}
                    {canApproveDeclineLocation && !isEditing && (
                        <>
                            <FontAwesomeIcon
                                icon={faCheck}
                                className="editable-description-icon location-approve-icon"
                                onClick={() => setShowApproveModal(true)}
                                title="Approve location"
                            />
                            <FontAwesomeIcon
                                icon={faTimes}
                                className="editable-description-icon location-decline-icon"
                                onClick={() => setShowDeclineModal(true)}
                                title="Decline location"
                            />
                        </>
                    )}
                </div>
                {!isEditing ? (
                    <>
                        {address?.city && (
                            <div className="proposed-meeting-row proposed-meeting-address-item">
                                <span className="proposed-meeting-label">
                                    City:
                                </span>{' '}
                                <span className={valueClass}>
                                    {address.city}
                                </span>
                            </div>
                        )}
                        {address?.state && (
                            <div className="proposed-meeting-row proposed-meeting-address-item">
                                <span className="proposed-meeting-label">
                                    State:
                                </span>{' '}
                                <span className={valueClass}>
                                    {address.state}
                                </span>
                            </div>
                        )}
                        {address?.address && (
                            <div className="proposed-meeting-row proposed-meeting-address-item">
                                <span className="proposed-meeting-label">
                                    Address:
                                </span>{' '}
                                <span className={valueClass}>
                                    {address.address}
                                </span>
                            </div>
                        )}
                        {!address && canEdit && (
                            <span className="proposed-meeting-value editable-description-empty">
                                No location set
                            </span>
                        )}
                    </>
                ) : (
                    <div className="editable-address-form">
                        <MeetingLocationEditForm
                            initialValues={{
                                city: address?.city || '',
                                state: address?.state || '',
                                address: address?.address || '',
                            }}
                            onSubmit={handleLocationSubmit}
                            onCancel={handleCancelEdit}
                            isSaving={isSaving}
                            onFormReady={({ submitForm }) => {
                                locationFormSubmitRef.current = submitForm;
                            }}
                            onValuesChange={handleLocationValuesChange}
                        />
                    </div>
                )}
                <ConfirmationModal
                    isVisible={showApproveModal}
                    setIsVisible={setShowApproveModal}
                    header="Approve Location"
                    confirmationText="Are you sure you want to approve the proposed location?"
                    nextButtonText="Approve"
                    cancelButtonText="Cancel"
                    onConfirm={handleApprove}
                    closeOnConfirm={true}
                    isLoading={isApproving}
                />
                <ConfirmationModal
                    isVisible={showDeclineModal}
                    setIsVisible={setShowDeclineModal}
                    header="Decline Location"
                    confirmationText="Are you sure you want to decline the proposed location?"
                    nextButtonText="Decline"
                    cancelButtonText="Cancel"
                    isConfirmationDanger={false}
                    onConfirm={handleDecline}
                    closeOnConfirm={true}
                    isLoading={isDeclining}
                />
            </div>
        );
    };

    const EditableCancellationFeesField = ({
        confirmedCancellationDeadlineDays,
        confirmedCancellationDeadlineHours,
        proposedCancellationDeadlineDays,
        proposedCancellationDeadlineHours,
        meetingFormat,
        meetingProposalId,
        meetingEngagementId,
        proposalStatus,
        dateStart,
        participantsType,
    }: {
        confirmedCancellationDeadlineDays?: number | null;
        confirmedCancellationDeadlineHours?: number | null;
        proposedCancellationDeadlineDays?: number | null;
        proposedCancellationDeadlineHours?: number | null;
        meetingFormat?: MeetingFormat;
        meetingProposalId?: string;
        meetingEngagementId?: number;
        proposalStatus?: MeetingProposalStatus;
        dateStart?: Date | string | null;
        participantsType?: MeetingParticipantsType;
    }) => {
        const isVirtual = meetingFormat === MeetingFormat.Virtual;
        const proposedDeadline = isVirtual
            ? proposedCancellationDeadlineHours
            : proposedCancellationDeadlineDays;
        const confirmedDeadline = isVirtual
            ? confirmedCancellationDeadlineHours
            : confirmedCancellationDeadlineDays;
        const isPending = proposedDeadline != null;
        const currentDeadline = isPending
            ? proposedDeadline
            : confirmedDeadline;
        const unit = isVirtual ? 'hrs' : 'days';
        const maxValue = isVirtual ? 96 : 14;

        const [isEditing, setIsEditing] = useState(false);
        const [hasFee, setHasFee] = useState(currentDeadline != null);
        const [editValue, setEditValue] = useState<number>(
            currentDeadline ?? 1
        );
        const [isSaving, setIsSaving] = useState(false);
        const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
        const [showApproveModal, setShowApproveModal] = useState(false);
        const [showDeclineModal, setShowDeclineModal] = useState(false);
        const [isApproving, setIsApproving] = useState(false);
        const [isDeclining, setIsDeclining] = useState(false);

        const originalHasFee = currentDeadline != null;
        const originalValue = currentDeadline ?? 1;
        const hasChanges =
            hasFee !== originalHasFee ||
            (hasFee && editValue !== originalValue);

        const isSameEngagement = meetingEngagementId === props.engagementId;
        const isSellerBuyerMeeting =
            participantsType === MeetingParticipantsType.SellerBuyer;
        const meetingStartDate = dateStart
            ? dateStart instanceof Date
                ? dateStart
                : new Date(dateStart)
            : null;
        const meetingNotStarted = meetingStartDate
            ? meetingStartDate > new Date()
            : true;

        const canEdit =
            !isAdmin &&
            !isBroker &&
            !(props.selectedEvent as MeetingObject)?.isCancelled &&
            props.mode !== 'buyerTopLevel' &&
            party === TimelineParty.Seller &&
            canSellerTeamMemberManageMeetings(summary, props.engagementId) &&
            isSameEngagement &&
            isSellerBuyerMeeting &&
            proposalStatus === MeetingProposalStatus.Confirmed &&
            meetingNotStarted;

        const canApproveDecline =
            !isAdmin &&
            !isBroker &&
            !(props.selectedEvent as MeetingObject)?.isCancelled &&
            isPending &&
            party === TimelineParty.Buyer &&
            canBuyerTeamMemberManageMeetings(summary, props.engagementId) &&
            isSameEngagement &&
            isSellerBuyerMeeting;

        useEffect(() => {
            setIsEditing(false);
            setHasFee(currentDeadline != null);
            setEditValue(currentDeadline ?? 1);
        }, [meetingProposalId]);

        const handleApprove = async () => {
            const engagementId = props.engagementId ?? currentEngagement?.id;
            if (!meetingProposalId || !engagementId) return;
            setIsApproving(true);
            try {
                const response = await axios.post(
                    `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling/${meetingProposalId}/ProposedCancellationDeadline/Confirm`
                );
                if (response.status === 200 || response.status === 204) {
                    createNotification('Cancellation fee approved', 'success');
                    props.onRefreshMeetings?.();
                } else {
                    createNotification(
                        'Failed to approve cancellation fee',
                        'error'
                    );
                }
            } catch (error: any) {
                createNotification(
                    error.response?.data?.message ||
                        'Failed to approve cancellation fee',
                    'error'
                );
            } finally {
                setIsApproving(false);
            }
        };

        const handleDecline = async () => {
            const engagementId = props.engagementId ?? currentEngagement?.id;
            if (!meetingProposalId || !engagementId) return;
            setIsDeclining(true);
            try {
                const response = await axios.post(
                    `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling/${meetingProposalId}/ProposedCancellationDeadline/Reject`
                );
                if (response.status === 200 || response.status === 204) {
                    createNotification('Cancellation fee declined', 'success');
                    props.onRefreshMeetings?.();
                } else {
                    createNotification(
                        'Failed to decline cancellation fee',
                        'error'
                    );
                }
            } catch (error: any) {
                createNotification(
                    error.response?.data?.message ||
                        'Failed to decline cancellation fee',
                    'error'
                );
            } finally {
                setIsDeclining(false);
            }
        };

        const handleEditClick = () => {
            setIsEditing(true);
            setHasFee(currentDeadline != null);
            setEditValue(currentDeadline ?? 1);
        };

        const handleCancelEdit = () => {
            setIsEditing(false);
            setHasFee(currentDeadline != null);
            setEditValue(currentDeadline ?? 1);
        };

        const handleSaveClick = async () => {
            const engagementId = props.engagementId ?? currentEngagement?.id;
            if (!meetingProposalId || !engagementId) {
                createNotification(
                    'Missing proposal ID or engagement ID',
                    'error'
                );
                return;
            }

            setIsSaving(true);
            try {
                const response = await axios.post(
                    `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling/${meetingProposalId}/ProposedCancellationDeadline`,
                    {
                        Days: hasFee && !isVirtual ? (editValue ?? 1) : null,
                        Hours: hasFee && isVirtual ? (editValue ?? 1) : null,
                    }
                );
                if (response.status === 200 || response.status === 204) {
                    createNotification(
                        'Cancellation fee updated successfully',
                        'success'
                    );
                    setIsEditing(false);
                    props.onRefreshMeetings?.();
                } else {
                    createNotification(
                        'Failed to update cancellation fee',
                        'error'
                    );
                }
            } catch (error: any) {
                console.error('Error updating cancellation fee:', error);
                createNotification(
                    error.response?.data?.message ||
                        'Failed to update cancellation fee',
                    'error'
                );
            } finally {
                setIsSaving(false);
            }
        };

        if (
            !canEdit &&
            !canApproveDecline &&
            currentDeadline == null &&
            !isPending
        )
            return null;

        return (
            <>
                <div className="proposed-meeting-row proposed-meeting-row-cancellation-fees">
                    <div className="editable-cancellation-fees-header">
                        <span className="proposed-meeting-label">
                            Cancellation Deadline:
                        </span>
                        {isPending && confirmedDeadline != null && (
                            <InfoTooltip
                                text={`Currently confirmed: ${confirmedDeadline} ${unit}`}
                                idText={`cancellation-fee-confirmed-${meetingProposalId ?? 'unknown'}`}
                                place="right"
                                className="location-confirmed-tooltip"
                            />
                        )}
                        {canApproveDecline && !isEditing && (
                            <>
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className="editable-description-icon location-approve-icon"
                                    onClick={() => setShowApproveModal(true)}
                                    title="Approve cancellation fee"
                                />
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className="editable-description-icon location-decline-icon"
                                    onClick={() => setShowDeclineModal(true)}
                                    title="Decline cancellation deadline"
                                />
                            </>
                        )}
                        {canEdit && !isEditing && (
                            <FontAwesomeIcon
                                icon={faEdit}
                                className="editable-description-icon edit-icon"
                                onClick={handleEditClick}
                                title="Edit cancellation deadline"
                            />
                        )}
                        {canEdit && isEditing && (
                            <>
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className={`editable-description-icon save-icon${!hasChanges ? ' description-save-disabled' : ''}`}
                                    onClick={() => {
                                        if (hasChanges)
                                            setShowSaveConfirmModal(true);
                                    }}
                                    title={
                                        hasChanges
                                            ? 'Save'
                                            : 'No changes to save'
                                    }
                                />
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    className="editable-description-icon cancel-edit-icon"
                                    onClick={handleCancelEdit}
                                    title="Cancel"
                                />
                            </>
                        )}
                    </div>
                    {!isEditing ? (
                        <span
                            className={[
                                'proposed-meeting-value',
                                isPending ? 'cancellation-fee-pending' : '',
                                isPending && party === TimelineParty.Buyer
                                    ? 'location-value-blink'
                                    : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}>
                            {currentDeadline != null
                                ? `${currentDeadline} ${unit}`
                                : 'None'}
                        </span>
                    ) : (
                        <div className="cancellation-fees-edit">
                            <div className="switcher-wrapper">
                                <div className="d-flex align-items-center">
                                    <ReactSwitch
                                        className="mr-2"
                                        offColor="#d92550"
                                        checked={hasFee}
                                        onChange={(checked) => {
                                            setHasFee(checked);
                                            if (checked && !editValue)
                                                setEditValue(1);
                                        }}
                                        height={20}
                                        width={42}
                                        handleDiameter={14}
                                        disabled={isSaving}
                                    />
                                    <span className="cancellation-fee-label">
                                        Cancellation Deadline
                                    </span>
                                </div>
                            </div>
                            {hasFee && (
                                <div className="cancellation-deadline-section">
                                    <div className="cancellation-deadline-header">
                                        <div className="cancellation-deadline-format-label">
                                            Meeting Type
                                        </div>
                                        <div className="cancellation-deadline-value-label">
                                            Cancellation Deadline
                                        </div>
                                    </div>
                                    <div className="cancellation-deadline-options">
                                        <div className="cancellation-deadline-row">
                                            <div className="cancellation-deadline-format-name">
                                                {meetingFormat ===
                                                MeetingFormat.Virtual
                                                    ? 'Virtual'
                                                    : meetingFormat ===
                                                        MeetingFormat.Mixed
                                                      ? 'Hybrid'
                                                      : 'In-Person'}
                                            </div>
                                            <div className="cancellation-deadline-input-item">
                                                <div className="duration-input-group">
                                                    <button
                                                        type="button"
                                                        className="duration-button decrement"
                                                        onClick={() =>
                                                            setEditValue(
                                                                Math.max(
                                                                    1,
                                                                    (editValue ||
                                                                        1) - 1
                                                                )
                                                            )
                                                        }
                                                        disabled={
                                                            isSaving ||
                                                            (editValue || 1) <=
                                                                1
                                                        }>
                                                        −
                                                    </button>
                                                    <input
                                                        type="text"
                                                        className="duration-input"
                                                        value={editValue ?? ''}
                                                        onChange={(e) => {
                                                            const v = parseInt(
                                                                e.target.value,
                                                                10
                                                            );
                                                            setEditValue(
                                                                isNaN(v)
                                                                    ? 1
                                                                    : Math.min(
                                                                          maxValue,
                                                                          Math.max(
                                                                              1,
                                                                              v
                                                                          )
                                                                      )
                                                            );
                                                        }}
                                                        disabled={isSaving}
                                                        placeholder="1"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="duration-button increment"
                                                        onClick={() =>
                                                            setEditValue(
                                                                Math.min(
                                                                    maxValue,
                                                                    (editValue ||
                                                                        0) + 1
                                                                )
                                                            )
                                                        }
                                                        disabled={
                                                            isSaving ||
                                                            (editValue || 0) >=
                                                                maxValue
                                                        }>
                                                        +
                                                    </button>
                                                </div>
                                                <span className="cancellation-deadline-unit">
                                                    {unit}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <ConfirmationModal
                    isVisible={showSaveConfirmModal}
                    setIsVisible={setShowSaveConfirmModal}
                    header="Propose Cancellation Deadline"
                    confirmationText={`Are you sure you want to propose ${hasFee ? `${editValue} ${unit}` : 'no cancellation deadline'}? The buyer will need to approve this change.`}
                    nextButtonText="Propose"
                    cancelButtonText="Cancel"
                    onConfirm={handleSaveClick}
                    closeOnConfirm={true}
                    isLoading={isSaving}
                />
                <ConfirmationModal
                    isVisible={showApproveModal}
                    setIsVisible={setShowApproveModal}
                    header="Approve Cancellation Fee"
                    confirmationText="Are you sure you want to approve the proposed cancellation fee?"
                    nextButtonText="Approve"
                    cancelButtonText="Cancel"
                    onConfirm={handleApprove}
                    closeOnConfirm={true}
                    isLoading={isApproving}
                />
                <ConfirmationModal
                    isVisible={showDeclineModal}
                    setIsVisible={setShowDeclineModal}
                    header="Decline Cancellation Fee"
                    confirmationText="Are you sure you want to decline the proposed cancellation fee?"
                    nextButtonText="Decline"
                    cancelButtonText="Cancel"
                    isConfirmationDanger={false}
                    onConfirm={handleDecline}
                    closeOnConfirm={true}
                    isLoading={isDeclining}
                />
            </>
        );
    };

    // Reset edit mode when selectedEvent changes to a different event
    // Don't reset when only clickedTimeSlot changes (allows updating date/time in edit mode)
    const [previousSelectedEventId, setPreviousSelectedEventId] = useState<
        number | undefined
    >();

    useEffect(() => {
        const currentEventId = props.selectedEvent?.id;
        if (currentEventId !== previousSelectedEventId) {
            setIsEditMode(false);
            setPreviousSelectedEventId(currentEventId);
        }
    }, [props.selectedEvent, previousSelectedEventId]);

    // When parent requests edit mode (e.g. edit icon on meeting tile), enter edit mode for the matching selected event
    useEffect(() => {
        const id = props.requestEditModeForEventId;
        if (id == null || id === '' || !props.selectedEvent) return;
        const ev = props.selectedEvent as MeetingObject;
        const idStr = String(id);
        const matches =
            ev.id == id ||
            ev.meetingProposalId == id ||
            String(ev.id) === idStr ||
            String(ev.meetingProposalId) === idStr;
        if (matches) {
            setIsEditMode(true);
            props.onEditModeChange?.(true);
        }
    }, [
        props.requestEditModeForEventId,
        props.selectedEvent,
        props.onEditModeChange,
    ]);

    // Notify parent about edit mode changes
    useEffect(() => {
        props.onEditModeChange?.(isEditMode);
    }, [isEditMode, props.onEditModeChange]);

    // Handle clickedTimeSlot updates: in edit mode (existing event) or in propose-new flow (no selectedEvent, prefill)
    useEffect(() => {
        if (!props.clickedTimeSlot || !formRef.current) return;
        const formatTimeString = (date: Date): string => {
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes}`;
        };
        const newStartTime = formatTimeString(props.clickedTimeSlot.startTime);
        const newEndTime = formatTimeString(props.clickedTimeSlot.endTime);
        // Update when editing existing event, or when proposing new (no selectedEvent) so form shows selected slot
        if (props.selectedEvent || props.meetingPrefillData) {
            formRef.current.setFieldValue('startTime', newStartTime);
            formRef.current.setFieldValue('endTime', newEndTime);
        }

        // Update startDate and endDate when clickedTimeSlot changes (e.g., user selects different block)
        if (props.clickedTimeSlot.startTime && props.clickedTimeSlot.endTime) {
            const newStartDate = new Date(props.clickedTimeSlot.startTime);
            const newEndDate = new Date(props.clickedTimeSlot.endTime);
            setStartDate(newStartDate);
            setEndDate(newEndDate);
        }
    }, [props.clickedTimeSlot, props.selectedEvent, props.meetingPrefillData]);

    useEffect(() => {
        if (props.selectedEvent) {
            const eventStartDate =
                props.selectedEvent.dateStart instanceof Date
                    ? props.selectedEvent.dateStart
                    : new Date(props.selectedEvent.dateStart);
            const eventEndDate =
                props.selectedEvent.dateEnd instanceof Date
                    ? props.selectedEvent.dateEnd
                    : new Date(props.selectedEvent.dateEnd);

            const eventStartTimeZone =
                TimeZoneHelper.getTimeZoneByDate(eventStartDate);
            const eventEndTimeZone =
                TimeZoneHelper.getTimeZoneByDate(eventEndDate);
            const startDateWithOffset = TimeZoneHelper.parseZoneDate(
                eventStartDate,
                eventStartTimeZone
            );
            const endDateWithOffset = TimeZoneHelper.parseZoneDate(
                eventEndDate,
                eventEndTimeZone
            );

            setStartDate(startDateWithOffset);
            setStartTimeZone(eventStartTimeZone);
            setEndDate(endDateWithOffset);
            setEndTimeZone(eventEndTimeZone);
        }
    }, [
        props.selectedEvent?.id,
        props.selectedEvent?.dateStart,
        props.selectedEvent?.dateEnd,
    ]);

    useEffect(() => {
        if (props.selectedEvent) {
            setLoadingTrigger(new Date().getTime());
        }
    }, [props.selectedEvent?.id]);

    const activeBuyers = useMemo(() => {
        // Use props.selectedBuyers as the base list
        const baseBuyers = props.selectedBuyers || [];

        // Get NDA status from currentEngagement if available
        const engagementBuyers =
            currentEngagement?.engagementProcess?.buyers || [];

        // Create a map of buyerId to NDA status from engagement
        const ndaStatusMap = new Map(
            engagementBuyers.map((buyer) => [
                buyer.buyerId,
                buyer.isDataNdaSigned,
            ])
        );

        // Filter buyers that are active and have signed NDA
        return baseBuyers.filter((buyer) => {
            // Check if buyer is active
            if (!buyer.isActive) return false;

            // Check NDA status from engagement first, fallback to buyer's own status
            const hasSignedNda = ndaStatusMap.has(buyer.buyerId)
                ? ndaStatusMap.get(buyer.buyerId)
                : buyer.isDataNdaSigned;

            return hasSignedNda;
        });
    }, [currentEngagement, props.selectedBuyers]);

    const buyerOptions = useMemo(() => {
        return activeBuyers.map((buyer) => ({
            id: buyer.buyerId,
            name:
                buyer.companyName ||
                buyer.buyerAccountName ||
                `Buyer ${buyer.buyerId}`,
        }));
    }, [activeBuyers]);

    useEffect(() => {
        const canBuyerTMJoinMeeting = CanBuyerTMJoinMeeting(
            props.selectedEvent
        );
        setCanBuyerTMJoinMeeting(canBuyerTMJoinMeeting);
    }, [props.selectedEvent, summary]);

    const CanBuyerTMJoinMeeting = (
        meeting: CalendarObject | undefined
    ): boolean => {
        if (summary.roleId !== USER_ROLES.PRACTICE_SECONDARY_USER) {
            return true;
        }

        if (!meeting || !summary?.associateMember?.length) {
            return false;
        }

        const mappings =
            summary.associateMember[0]?.associateMemberMarketplaceMappings;

        if (!mappings?.length) {
            return false;
        }

        const hasPermission = (mapping: any) =>
            (mapping.type === TeamMemberType.Internal &&
                (mapping.internalPermissions ===
                    InternalPermissions.ReadOnlyAndCommunicate ||
                    mapping.internalPermissions ===
                        InternalPermissions.ReadOnlyAndCommunicateAndExecute)) ||
            (mapping.type === TeamMemberType.External &&
                (mapping.externalPermissions ===
                    ExternalPermissions.VdrAndCommunicate));

        const isOpportunityMatch =
            meeting.engagementId != null && meeting.engagementId > 0
                ? (mapping: any) =>
                      mapping.opportunityId === meeting.engagementId
                : (mapping: any) => mapping.opportunityId == null;

        return mappings.some(
            (mapping: any) =>
                isOpportunityMatch(mapping) && hasPermission(mapping)
        );
    };

    const minutesToHoursMinutes = (minutes: number): string => {
        if (minutes == null || minutes === 0 || isNaN(minutes)) return '0:00';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}:${mins.toString().padStart(2, '0')}`;
    };

    const hoursMinutesToMinutes = (hoursMinutes: string): number => {
        if (!hoursMinutes || hoursMinutes.trim() === '') return 0;
        const parts = hoursMinutes.split(':');
        if (parts.length === 1) {
            // If no colon, treat as hours only
            const hours = parseInt(parts[0]) || 0;
            return hours * 60;
        }
        if (parts.length === 2) {
            const hours = parseInt(parts[0]) || 0;
            const minutes = parseInt(parts[1]) || 0;
            return hours * 60 + minutes;
        }
        return 0;
    };

    /** Parse duration to minutes: number (minutes or seconds if > 720), or string "H:MM" / "HH:MM". */
    const parseDurationToMinutes = (duration: unknown): number => {
        if (duration == null) return 0;
        if (typeof duration === 'string' && duration.includes(':')) {
            const parts = duration.trim().split(':');
            const hours = parseInt(parts[0] || '0', 10) || 0;
            const minutes = parseInt(parts[1] || '0', 10) || 0;
            return hours * 60 + minutes;
        }
        const n = Number(duration);
        if (isNaN(n) || n <= 0) return 0;
        if (n > 720) return Math.round(n / 60); // assume seconds
        return n;
    };

    /** Get max duration in minutes from block (copy or selected) for a meeting format. Handles format as enum or string; supports format/Format and duration/Duration. */
    const getDurationMinutesFromBlockCopy = (
        block: AvailabilityBlockObject | null | undefined,
        format: MeetingFormat
    ): number => {
        const formats = block?.formats ?? (block as any)?.Formats ?? [];
        if (!Array.isArray(formats) || !formats.length) return 0;
        const formatMatches = (x: any) => {
            const v = x?.format ?? x?.Format;
            if (v === format) return true;
            if (typeof v === 'number' && v === format) return true;
            if (typeof v === 'string') {
                const s = String(v).toLowerCase().replace(/\s+/g, '');
                if (
                    format === MeetingFormat.Virtual &&
                    (s === 'virtual' || s === '1')
                )
                    return true;
                if (
                    format === MeetingFormat.InPerson &&
                    (s === 'inperson' || s === 'in-person' || s === '2')
                )
                    return true;
                if (
                    format === MeetingFormat.Mixed &&
                    (s === 'mixed' || s === 'combo' || s === '3')
                )
                    return true;
            }
            return false;
        };
        const f = formats.find(formatMatches);
        const duration = f?.duration ?? (f as any)?.Duration;
        return parseDurationToMinutes(duration);
    };

    const validateConferenceFields = () => {
        let dateStart = TimeZoneHelper.parseUtcDate(startDate, startTimeZone);
        let dateEnd = TimeZoneHelper.parseUtcDate(endDate, endTimeZone);

        const difference = DateTimeHelper.differenceInMinutes(
            dateEnd,
            dateStart
        );

        if (dateStart > dateEnd || difference == 0 || dateStart < new Date()) {
            setStartDateTimeState({ time: false, zone: false });
            setEndDateTimeState({ time: false, zone: false });
            setDateErrorMessage(undefined);
            return;
        }

        if ((dateEnd.getTime() - dateStart.getTime()) / 36e5 > 12) {
            setStartDateTimeState({ time: false, zone: false });
            setEndDateTimeState({ time: false, zone: false });
            setDateErrorMessage(
                'The maximum possible duration of the Meeting is 12 hours'
            );
            return;
        }

        setStartDateTimeState({ time: true, zone: true });
        setEndDateTimeState({ time: true, zone: true });
        setDateErrorMessage(undefined);
    };

    const checkMeetingOverlap = useCallback(
        (meetingStartDate: Date, meetingEndDate: Date): string | null => {
            if (!props.calendarObjects || props.calendarObjects.length === 0) {
                return null;
            }

            const selectedEv =
                props.selectedEvent &&
                !isAvailabilityBlockObject(props.selectedEvent)
                    ? (props.selectedEvent as MeetingObject & {
                          isMeetingProposal?: boolean;
                          proposalStatus?: MeetingProposalStatus;
                          buyerId?: number;
                      })
                    : null;
            const currentMeetingId = selectedEv?.id ?? null;
            const isUnconfirmedProposal =
                selectedEv?.isMeetingProposal &&
                selectedEv?.proposalStatus !== MeetingProposalStatus.Confirmed;
            const isConfirmedMeeting = (
                m: MeetingObject & {
                    isOtherGhostMeeting?: boolean;
                    isMeetingProposal?: boolean;
                    proposalStatus?: MeetingProposalStatus;
                    buyerId?: number;
                }
            ) =>
                m.id != null &&
                m.id !== 0 &&
                !m.isOtherGhostMeeting &&
                (!m.isMeetingProposal ||
                    m.proposalStatus === MeetingProposalStatus.Confirmed);

            const otherMeetings = props.calendarObjects.filter((obj) => {
                if (isAvailabilityBlockObject(obj)) return false;

                const meeting = obj as MeetingObject & {
                    isOtherGhostMeeting?: boolean;
                    isMeetingProposal?: boolean;
                    proposalStatus?: MeetingProposalStatus;
                    buyerId?: number;
                    isCancelled?: boolean;
                };
                if (currentMeetingId && meeting.id === currentMeetingId)
                    return false;
                if (meeting.isCancelled) return false;
                if (
                    isUnconfirmedProposal &&
                    party === TimelineParty.Buyer &&
                    isConfirmedMeeting(meeting)
                )
                    return false;
                if (
                    isUnconfirmedProposal &&
                    party === TimelineParty.Seller &&
                    isConfirmedMeeting(meeting) &&
                    meeting.buyerId != null &&
                    selectedEv?.buyerId != null &&
                    meeting.buyerId === selectedEv.buyerId
                )
                    return false;
                if (!meeting.id || meeting.id === 0) return false;
                return true;
            }) as MeetingObject[];

            if (otherMeetings.length === 0) {
                return null;
            }

            const newMeetingStart = new Date(meetingStartDate);
            newMeetingStart.setHours(
                meetingStartDate.getHours(),
                meetingStartDate.getMinutes(),
                0,
                0
            );
            const newMeetingEnd = new Date(meetingEndDate);
            newMeetingEnd.setHours(
                meetingEndDate.getHours(),
                meetingEndDate.getMinutes(),
                0,
                0
            );

            const newMeetingStartTime = newMeetingStart.getTime();
            const newMeetingEndTime = newMeetingEnd.getTime();

            const overlappingMeetings: MeetingObject[] = [];
            for (const meeting of otherMeetings) {
                const otherMeetingStart =
                    meeting.dateStart instanceof Date
                        ? meeting.dateStart
                        : new Date(meeting.dateStart);
                const otherMeetingEnd =
                    meeting.dateEnd instanceof Date
                        ? meeting.dateEnd
                        : new Date(meeting.dateEnd);

                const otherStartTime = otherMeetingStart.getTime();
                const otherEndTime = otherMeetingEnd.getTime();

                if (
                    newMeetingStartTime < otherEndTime &&
                    newMeetingEndTime > otherStartTime
                ) {
                    overlappingMeetings.push(meeting);
                }
            }

            if (overlappingMeetings.length > 0) {
                const meetingNames = overlappingMeetings
                    .map((m) => m.name || `Meeting #${m.id}`)
                    .join(', ');
                return `This meeting overlaps with: ${meetingNames}`;
            }

            return null;
        },
        [
            props.calendarObjects,
            props.selectedEvent,
            party,
            startTimeZone,
            endTimeZone,
        ]
    );

    const overlapWarning = useMemo(() => {
        if (
            props.viewMode !== 'availability' &&
            (!props.selectedEvent ||
                !isAvailabilityBlockObject(props.selectedEvent)) &&
            startDate &&
            endDate
        ) {
            return checkMeetingOverlap(startDate, endDate);
        } else if (
            props.viewMode === 'availability' ||
            (props.selectedEvent &&
                isAvailabilityBlockObject(props.selectedEvent))
        ) {
            return null;
        }
        return null;
    }, [
        props.viewMode,
        props.selectedEvent,
        props.selectedDate,
        props.clickedTimeSlot,
        props.calendarObjects,
        startDate?.getTime(),
        endDate?.getTime(),
        startTimeZone,
        endTimeZone,
        checkMeetingOverlap,
    ]);

    const confirmedMeetingOverlapWarning = useMemo(() => {
        if (
            !props.selectedEvent ||
            isAvailabilityBlockObject(props.selectedEvent)
        ) {
            return null;
        }
        if (!props.calendarObjects || props.calendarObjects.length === 0) {
            return null;
        }
        const ev = props.selectedEvent as MeetingObject;
        if (!ev.dateStart || !ev.dateEnd) {
            return null;
        }
        const meetingStart =
            ev.dateStart instanceof Date
                ? ev.dateStart
                : new Date(ev.dateStart);
        const meetingEnd =
            ev.dateEnd instanceof Date ? ev.dateEnd : new Date(ev.dateEnd);

        const currentMeetingId = ev.id;
        const currentProposalId = ev.meetingProposalId;
        const isUnconfirmedProposal =
            ev.isMeetingProposal &&
            ev.proposalStatus !== MeetingProposalStatus.Confirmed;
        const proposalBuyerId = ev.buyerId;
        const isConfirmedMeeting = (
            m: MeetingObject & {
                isOtherGhostMeeting?: boolean;
                isMeetingProposal?: boolean;
                proposalStatus?: MeetingProposalStatus;
                buyerId?: number;
            }
        ) =>
            m.id != null &&
            m.id !== 0 &&
            !m.isOtherGhostMeeting &&
            (!m.isMeetingProposal ||
                m.proposalStatus === MeetingProposalStatus.Confirmed);

        const otherMeetings = props.calendarObjects.filter((obj) => {
            if (isAvailabilityBlockObject(obj)) return false;
            const meeting = obj as MeetingObject & {
                isOtherGhostMeeting?: boolean;
                isMeetingProposal?: boolean;
                proposalStatus?: MeetingProposalStatus;
                buyerId?: number;
                isCancelled?: boolean;
            };
            if (currentMeetingId && meeting.id === currentMeetingId)
                return false;
            if (meeting.isCancelled) return false;
            if (
                currentProposalId &&
                meeting.meetingProposalId === currentProposalId
            )
                return false;
            if (
                currentProposalId &&
                meeting.isMeetingProposal &&
                meeting.meetingProposalId === currentProposalId
            )
                return false;
            if (
                isUnconfirmedProposal &&
                party === TimelineParty.Buyer &&
                isConfirmedMeeting(meeting)
            )
                return false;
            if (
                isUnconfirmedProposal &&
                party === TimelineParty.Seller &&
                isConfirmedMeeting(meeting) &&
                meeting.buyerId != null &&
                proposalBuyerId != null &&
                meeting.buyerId === proposalBuyerId
            )
                return false;
            if (!meeting.id || meeting.id === 0) return false;
            return true;
        }) as MeetingObject[];

        if (otherMeetings.length === 0) {
            return null;
        }

        const meetingStartTime = meetingStart.getTime();
        const meetingEndTime = meetingEnd.getTime();

        const overlappingMeetings: MeetingObject[] = [];
        for (const meeting of otherMeetings) {
            const otherMeetingStart =
                meeting.dateStart instanceof Date
                    ? meeting.dateStart
                    : new Date(meeting.dateStart);
            const otherMeetingEnd =
                meeting.dateEnd instanceof Date
                    ? meeting.dateEnd
                    : new Date(meeting.dateEnd);

            const otherStartTime = otherMeetingStart.getTime();
            const otherEndTime = otherMeetingEnd.getTime();

            if (
                meetingStartTime < otherEndTime &&
                meetingEndTime > otherStartTime
            ) {
                overlappingMeetings.push(meeting);
            }
        }

        if (overlappingMeetings.length > 0) {
            const meetingNames = overlappingMeetings
                .map((m) => m.name || `Meeting #${m.id}`)
                .join(', ');
            return `This meeting overlaps with: ${meetingNames}`;
        }

        return null;
    }, [props.selectedEvent, props.calendarObjects, party]);

    const handleStartDateChange = (value: Date) => {
        const mergedDate = new Date(startDate);
        mergedDate.setHours(value.getHours());
        mergedDate.setMinutes(value.getMinutes());
        mergedDate.setSeconds(0);
        mergedDate.setMilliseconds(0);

        setStartDate(mergedDate);
        const newTimeZone = TimeZoneHelper.getTimeZoneByDate(mergedDate);
        setStartTimeZone(newTimeZone);
        if (!TimeZoneHelper.hasTimeZone(mergedDate, endTimeZone)) {
            setEndTimeZone(newTimeZone);
        }
        validateConferenceFields();

        // Overlap check will be handled by useEffect that reacts to startDate/endDate changes
    };

    const handleStartTimeZoneChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const zone = TimeZoneHelper.getTimeZoneByAbbreviation(
            event.target.value
        );
        setStartTimeZone(zone);
        validateConferenceFields();
    };

    const handleEndDateChange = (value: Date) => {
        const mergedDate = new Date(endDate);
        mergedDate.setHours(value.getHours());
        mergedDate.setMinutes(value.getMinutes());
        mergedDate.setSeconds(0);
        mergedDate.setMilliseconds(0);

        setEndDate(mergedDate);
        const newTimeZone = TimeZoneHelper.getTimeZoneByDate(mergedDate);
        if (TimeZoneHelper.hasTimeZone(mergedDate, newTimeZone)) {
            setEndTimeZone(newTimeZone);
        }
        validateConferenceFields();

        // Overlap check will be handled by useEffect that reacts to startDate/endDate changes
    };

    const handleEndTimeZoneChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        const zone = TimeZoneHelper.getTimeZoneByAbbreviation(
            event.target.value
        );
        if (TimeZoneHelper.hasTimeZone(endDate, zone)) {
            setEndTimeZone(zone);
        }
        validateConferenceFields();
    };

    const getPrimaryButtonText = (): string => {
        switch (props.viewMode) {
            case 'availability':
                return 'Add availability';
            case 'meetingSeller':
                return 'Schedule meeting';
            case 'meetingBuyer':
                return 'Propose meeting';
        }
    };

    const handleJoinMeeting = () => {
        if (props.selectedEvent) {
            setIsConferenceMeetingPopupVisible(true);
        }
    };

    const fetchRecordingAndOpen = (
        meetingId: number,
        engagementId?: number
    ) => {
        const url = engagementId
            ? `/api/Marketplace/Engagements/${engagementId}/EngagementMeetings/${meetingId}/recording`
            : `/api/Marketplace/MarketplaceMeetings/${meetingId}/recording`;

        axios
            .get(url)
            .then((response: any) => {
                if (response.status === 200) {
                    setVideoSource(response.data);
                } else {
                    createNotification(
                        'Error occurred while getting Virtual Meeting Record',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Error occurred while loading Virtual Meeting Record',
                    'error'
                );
            });
    };

    const handleClipSave = () => {
        setLoadingTrigger(new Date().getTime());
        if (props.onRefreshMeetings) {
            props.onRefreshMeetings();
        }
    };

    const handleDownloadFile = (eventId: number, fileId: number) => {
        const meeting = props.selectedEvent as MeetingObject;
        if (!meeting) {
            createNotification(
                'Unable to download file. Meeting information is missing.',
                'error'
            );
            return;
        }

        axios
            .get(
                `/api/calendars/${meeting.commsId}/events/${eventId}/files/${fileId}`
            )
            .then((response: any) => {
                if (response.status === 200) {
                    window.open(response.data, '_blank');
                } else {
                    createNotification(
                        'Some unexpected error occurred while downloading file.',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Some unexpected error occurred while downloading file.',
                    'error'
                );
            });
    };

    const handleDeleteAvailabilityBlock = async () => {
        const selectedEvent = props.selectedEvent as AvailabilityBlockObject;
        const blockId = selectedEvent.availabilityBlockId;

        if (!blockId || !currentEngagement?.id) {
            createNotification('Unable to delete availability block', 'error');
            return;
        }

        setIsDeleting(true);

        try {
            const response = await axios.delete(
                `/api/Marketplace/Engagements/${currentEngagement.id}/EngagementMeetingAvailabilityBlocks/${blockId}`
            );

            if (response.status === 200) {
                createNotification(
                    'Availability block deleted successfully',
                    'success'
                );
                setShowDeleteConfirmation(false);

                // Close the form and refresh
                props.onDeselectEvent?.();
                props.onDeselectDate?.();
                props.onRefreshMeetings?.();
            } else {
                createNotification(
                    'Failed to delete availability block',
                    'error'
                );
            }
        } catch (error) {
            console.error('Error deleting availability block:', error);
            createNotification('Failed to delete availability block', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const canCreateOrEditAvailability = useMemo(() => {
        if (isAdmin || isBroker || isObserver) {
            return false;
        }

        // Allow creation/editing during SellerProvideMeetingAvailability, SellerBuyerAgreeOnMeetingTimes, and SellerBuyerMeetingsPeriod
        const timelineEvents = currentEngagement?.events || [];
        return timelineEvents.some(
            (event) =>
                (event.linkedAction?.actionType ===
                    TimelineActionType.SellerProvideMeetingAvailability ||
                    event.linkedAction?.actionType ===
                        TimelineActionType.SellerBuyerAgreeOnMeetingTimes ||
                    event.linkedAction?.actionType ===
                        TimelineActionType.SellerBuyerMeetingsPeriod) &&
                event.status === TimelineEventStatus.InProgress
        );
    }, [currentEngagement, isAdmin, isBroker]);

    // Detect if we're in SellerBuyerAgreeOnMeetingTimes phase
    const isAgreeOnMeetingTimesPhase = useMemo(() => {
        const timelineEvents =
            (party === TimelineParty.Buyer
                ? selectedOpportunity
                : currentEngagement
            )?.events || [];

        const agreeOnMeetingTimesEvent = timelineEvents.find(
            (event: TimelineEvent) =>
                event.linkedAction?.actionType ===
                    TimelineActionType.SellerBuyerAgreeOnMeetingTimes &&
                event.status === TimelineEventStatus.InProgress
        );

        return !!agreeOnMeetingTimesEvent;
    }, [party, currentEngagement, selectedOpportunity]);

    // Determine if buyer is in AgreeOnMeetingTimes phase (proposing or viewing availability)
    const isBuyerInAgreeOnMeetingTimesPhase = useMemo(() => {
        return isAgreeOnMeetingTimesPhase && party === TimelineParty.Buyer;
    }, [isAgreeOnMeetingTimesPhase, party]);

    // Determine if buyer is proposing (not viewing existing event) during AgreeOnMeetingTimes phase
    const isBuyerProposingMeeting = useMemo(() => {
        return isBuyerInAgreeOnMeetingTimesPhase && !props.selectedEvent;
    }, [isBuyerInAgreeOnMeetingTimesPhase, props.selectedEvent]);

    const isGhostMeeting = useMemo(() => {
        return isGhostMeetingObject(props.selectedEvent);
    }, [props.selectedEvent]);

    // Dynamic block selection: when date or start/end time changes, find intersecting block and notify parent (or clear).
    // When user has already selected a block by clicking, do not overwrite it from form state (form can show previous block's times and would flip between blocks).
    // When user has selected a meeting (not a proposal), do not overwrite with a block (clicking meeting should show meeting details, not switch to block).
    // When buyer is editing a proposal, still run this so we can set the intersecting block for comparison/mismatch (parent keeps selectedEvent as proposal).
    // When in "Propose new meeting" flow (prefill set, no selectedEvent), do not call onSelectBlockByDateTime so parent does not set selectedEvent(block) and wipe prefill.
    useEffect(() => {
        if (
            !isBuyerInAgreeOnMeetingTimesPhase ||
            !props.onSelectBlockByDateTime ||
            !props.availabilityBlocksForLookup?.length
        ) {
            return;
        }
        if (props.meetingPrefillData && !props.selectedEvent) {
            return; // Propose-new flow: keep prefill; do not trigger block selection
        }
        const currentBlock = isAvailabilityBlockObject(props.selectedEvent)
            ? props.selectedEvent
            : null;
        if (currentBlock) return; // User clicked a block; never overwrite selection from form-time lookup
        const isEditingProposal =
            props.selectedEvent &&
            (props.selectedEvent as MeetingObject).isMeetingProposal === true;
        if (props.selectedEvent && !isEditingProposal) return; // User clicked a meeting (not proposal); never overwrite with block so meeting details stay visible
        // When user is viewing a proposal (not editing), do not overwrite their selection with a block from date/time lookup
        if (isEditingProposal && !isEditMode) return;
        const selectedDate = props.selectedDate;
        let formStartT: number;
        let formEndT: number;
        const slot = props.clickedTimeSlot;
        if (selectedDate && slot?.startTime && slot?.endTime) {
            // Use the slot the user actually clicked so we don't re-select from stale form ref (fixes needing 2 clicks to de-select)
            formStartT = new Date(slot.startTime).getTime();
            formEndT = new Date(slot.endTime).getTime();
            if (formStartT >= formEndT) {
                if (!currentBlock) props.onSelectBlockByDateTime(null);
                return;
            }
        } else {
            const { startTime, endTime } = formDateTimeRef.current;
            if (!selectedDate) {
                if (!currentBlock) props.onSelectBlockByDateTime(null);
                return;
            }
            if (!startTime?.trim() || !endTime?.trim()) {
                if (!currentBlock) props.onSelectBlockByDateTime(null);
                return;
            }
            const partsStart = startTime.trim().split(':');
            const partsEnd = endTime.trim().split(':');
            const sh = parseInt(partsStart[0] || '0', 10) || 0;
            const sm =
                parseInt(partsStart[1]?.replace(/\D/g, '') || '0', 10) || 0;
            const eh = parseInt(partsEnd[0] || '0', 10) || 0;
            const em =
                parseInt(partsEnd[1]?.replace(/\D/g, '') || '0', 10) || 0;
            const formStart = new Date(selectedDate);
            formStart.setHours(sh, sm, 0, 0);
            const formEnd = new Date(selectedDate);
            formEnd.setHours(eh, em, 0, 0);
            if (formStart.getTime() >= formEnd.getTime()) {
                if (!currentBlock) props.onSelectBlockByDateTime(null);
                return;
            }
            formStartT = formStart.getTime();
            formEndT = formEnd.getTime();
        }
        const found = props.availabilityBlocksForLookup.find((block) => {
            const bStart = new Date(
                (block as any).dateStart ?? (block as any).DateStart ?? 0
            ).getTime();
            const bEnd = new Date(
                (block as any).dateEnd ?? (block as any).DateEnd ?? 0
            ).getTime();
            return formStartT <= bEnd && formEndT >= bStart;
        });
        const sameEmpty = !found && !currentBlock;
        if (sameEmpty) return;
        props.onSelectBlockByDateTime(found ?? null);
    }, [
        isBuyerInAgreeOnMeetingTimesPhase,
        isEditMode,
        props.selectedDate,
        props.selectedEvent,
        props.meetingPrefillData,
        props.clickedTimeSlot,
        formDateTimeVersion,
        props.availabilityBlocksForLookup,
        props.onSelectBlockByDateTime,
    ]);

    const shouldShowForm = () => {
        if (props.forceVisible) return true;

        switch (props.viewMode) {
            case 'meetingBuyer':
                // Show form if event is selected, or if buyer is in AgreeOnMeetingTimes phase and has selected date/time slot or is proposing alternative (prefill)
                if (props.selectedEvent) return true;
                if (
                    isAgreeOnMeetingTimesPhase &&
                    party === TimelineParty.Buyer
                ) {
                    if (
                        !(
                            canBuyerTeamMemberManageMeetings(
                                summary,
                                props.engagementId
                            ) ||
                            canSellerTeamMemberManageMeetings(
                                summary,
                                props.engagementId
                            )
                        )
                    ) {
                        return false;
                    }
                    return !!(
                        props.selectedDate ||
                        props.clickedTimeSlot ||
                        props.meetingPrefillData
                    );
                }
                return false;
            case 'availability':
                // Show form for viewing existing events (always allowed)
                if (props.selectedEvent) return true;

                // During SellerBuyerAgreeOnMeetingTimes phase, Buyer should see form when selecting date/time slot or proposing alternative (prefill)
                if (
                    isAgreeOnMeetingTimesPhase &&
                    party === TimelineParty.Buyer
                ) {
                    // Check if buyer team member has permission to manage meetings
                    if (
                        !(
                            canBuyerTeamMemberManageMeetings(
                                summary,
                                props.engagementId
                            ) ||
                            canSellerTeamMemberManageMeetings(
                                summary,
                                props.engagementId
                            )
                        )
                    ) {
                        return false;
                    }
                    return !!(
                        props.selectedDate ||
                        props.clickedTimeSlot ||
                        props.meetingPrefillData
                    );
                }

                // Seller "Propose New Meeting" from existing meeting: show form with prefill (no selectedEvent)
                if (
                    party === TimelineParty.Seller &&
                    props.meetingPrefillData
                ) {
                    return true;
                }

                // Show form for creating new availability blocks only if action is in progress (for sellers)
                if (props.selectedDate) {
                    return canCreateOrEditAvailability;
                }
                return false;
            default:
                return props.forceVisible;
        }
    };

    // Get location data from practice with max FTE DVMs
    const getMaxFteDvmPracticeLocation = useMemo(() => {
        const practices = currentEngagement?.engagementProcess?.practices || [];

        if (!practices || practices.length === 0) {
            return { city: '', state: '' };
        }

        // Find practice with maximum FTE DVMs
        const maxFteDvmPractice = practices.reduce(
            (max: any, practice: any) => {
                return (practice.fteDvms || 0) > (max.fteDvms || 0)
                    ? practice
                    : max;
            },
            practices[0]
        );

        // Get the first location data if available
        if (
            maxFteDvmPractice.practiceLocationData &&
            maxFteDvmPractice.practiceLocationData.length > 0
        ) {
            const location = maxFteDvmPractice.practiceLocationData[0];
            const stateValue = location.state || '';
            // Convert state name to code if needed (e.g., "New York" -> "NY")
            const stateCode =
                stateValue.length > 2
                    ? stateToCodeMapper(stateValue)
                    : stateValue;
            return {
                city: location.city || '',
                state: stateCode || '',
            };
        }

        // Fallback to practice-level data if no practiceLocationData
        const stateValue = maxFteDvmPractice.state || '';
        // Convert state name to code if needed (e.g., "New York" -> "NY")
        const stateCode =
            stateValue.length > 2 ? stateToCodeMapper(stateValue) : stateValue;
        return {
            city: maxFteDvmPractice.city || '',
            state: stateCode || '',
        };
    }, [currentEngagement]);

    // Seller "Propose New Meeting" from existing meeting: form is editable (not readonly)
    const isSellerProposingAlternativeForm =
        props.mode === 'engagement' &&
        party === TimelineParty.Seller &&
        !!props.meetingPrefillData &&
        props.meetingPrefillData.posterId != null &&
        !props.selectedEvent;
    const effectiveReadonly =
        props.readonly && !isSellerProposingAlternativeForm;

    // Unified details view: when viewing any meeting or proposal (not editing, not an availability block), show details + Edit button. Edit toggles into form.
    const showMeetingDetailsView =
        !!props.selectedEvent &&
        !isAvailabilityBlockObject(props.selectedEvent) &&
        !isEditMode;

    const isCancelledProposal =
        !!props.selectedEvent &&
        !isAvailabilityBlockObject(props.selectedEvent) &&
        (props.selectedEvent as MeetingObject).isCancelled === true;

    const isConfirmedMeeting =
        !!props.selectedEvent &&
        !isAvailabilityBlockObject(props.selectedEvent) &&
        !isCancelledProposal &&
        ((props.selectedEvent as MeetingObject).isMeetingProposal === false ||
            (props.selectedEvent as MeetingObject).proposalStatus ===
                MeetingProposalStatus.Confirmed);

    const isPendingProposal =
        !!props.selectedEvent &&
        !isAvailabilityBlockObject(props.selectedEvent) &&
        !isCancelledProposal &&
        (props.selectedEvent as MeetingObject).isMeetingProposal === true &&
        ((props.selectedEvent as MeetingObject).proposalStatus ===
            MeetingProposalStatus.PendingSellerAnswer ||
            (props.selectedEvent as MeetingObject).proposalStatus ===
                MeetingProposalStatus.PendingBuyerAnswer);

    const isPendingSellerAnswerProposal =
        isPendingProposal &&
        (props.selectedEvent as MeetingObject).proposalStatus ===
            MeetingProposalStatus.PendingSellerAnswer;

    const isPendingBuyerAnswerProposal =
        isPendingProposal &&
        (props.selectedEvent as MeetingObject).proposalStatus ===
            MeetingProposalStatus.PendingBuyerAnswer;

    // Edit button only for proposal: buyer when PendingBuyerAnswer, seller when PendingSellerAnswer
    // Seller TM should not see Edit Proposal when status is PendingBuyerAnswer
    const isSellerTM = canSellerTeamMemberManageMeetings(
        summary,
        props.engagementId
    );
    const isSellerTMWithoutCommsHub =
        isSellerTeamMemberForEngagement(summary, props.engagementId) &&
        !isSellerTM;
    const showEditButton =
        isPendingProposal &&
        ((party === TimelineParty.Buyer &&
            !isSellerTeamMemberForEngagement(summary, props.engagementId) &&
            isPendingBuyerAnswerProposal) ||
            (party === TimelineParty.Seller &&
                !isSellerTMWithoutCommsHub &&
                isPendingSellerAnswerProposal));

    const brokeragePricing =
        (currentEngagement?.levelOfService ??
            selectedOpportunity?.levelOfService) ===
        LevelOfService.DiscountBrokerage
            ? pricing.discountBrokeragePricing
            : pricing.fullServiceBrokeragePricing;
    const meetingFee =
        pendingProposalBody?.Format === MeetingFormat.Virtual
            ? brokeragePricing?.buyersFees?.sellerMeetingFeeVirtual
            : brokeragePricing?.buyersFees?.sellerMeetingFeeInPerson;

    return (
        <div className="meeting-scheduling-meeting-details-form">
            {showMeetingDetailsView ? (
                <div className="proposed-meeting-view form-container">
                    {(() => {
                        const ev = props.selectedEvent as MeetingObject;
                        const dateStart = ev?.dateStart
                            ? ev.dateStart instanceof Date
                                ? ev.dateStart
                                : new Date(ev.dateStart)
                            : null;
                        const dateEnd = ev?.dateEnd
                            ? ev.dateEnd instanceof Date
                                ? ev.dateEnd
                                : new Date(ev.dateEnd)
                            : null;
                        const dateLabel = dateStart
                            ? dateStart.toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                              })
                            : '';
                        const startTimeLabel = dateStart
                            ? dateStart.toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                              })
                            : '';
                        const endTimeLabel = dateEnd
                            ? dateEnd.toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                              })
                            : '';
                        const typeLabel =
                            ev?.format != null
                                ? MeetingFormatTypesOptions[
                                      ev.format as MeetingFormat
                                  ] || 'Virtual'
                                : 'Virtual';
                        const meetingHasStarted =
                            dateStart != null &&
                            new Date().getTime() >= dateStart.getTime();
                        return (
                            <>
                                <h3 className="proposed-meeting-title">
                                    {ev.isCancelled
                                        ? 'Cancelled Meeting'
                                        : isConfirmedMeeting
                                          ? 'Confirmed Meeting'
                                          : 'Proposed Meeting'}
                                </h3>
                                {ev.isCancelled && (
                                    <div className="proposed-meeting-cancelled-row">
                                        <p className="proposed-meeting-awaiting proposed-meeting-cancelled">
                                            Cancelled
                                        </p>
                                        {!isSfas && (isAdmin || party === TimelineParty.Buyer) && (() => {
                                            const paid = (props.selectedEvent as MeetingObject)?.pricePaid;
                                            const refunded = (props.selectedEvent as MeetingObject)?.refundedAmount;
                                            const isFullyRefunded = paid != null && paid > 0 && refunded != null && refunded >= paid;
                                            const isPartiallyRefunded = refunded != null && refunded > 0 && refunded < paid;
                                            if (isFullyRefunded) return (
                                                <span className="refunded-badge">
                                                    <span className="refunded-badge-icon">
                                                        <FontAwesomeIcon icon={faCheck} />
                                                    </span>
                                                    Refunded
                                                </span>
                                            );
                                            if (isPartiallyRefunded) return (
                                                <span className="refunded-badge">
                                                    Partially Refunded
                                                </span>
                                            );
                                            return null;
                                        })()}
                                    </div>
                                )}
                                {!isConfirmedMeeting && isPendingProposal && (
                                    <p className="proposed-meeting-awaiting">
                                        {isPendingSellerAnswerProposal
                                            ? 'Awaiting Seller Response'
                                            : 'Awaiting Buyer Response'}
                                    </p>
                                )}
                                <Row className="proposed-meeting-details">
                                    <Col md={12}>
                                        {confirmedMeetingOverlapWarning && !isCancelledProposal && (
                                            <Row className="overlap-warning-row">
                                                <Col md={12}>
                                                    <div className="overlap-warning-message">
                                                        {
                                                            confirmedMeetingOverlapWarning
                                                        }
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}
                                        <div className="proposed-meeting-row">
                                            <span className="proposed-meeting-label">
                                                Date:
                                            </span>{' '}
                                            <span className="proposed-meeting-value">
                                                {dateLabel}
                                            </span>
                                        </div>
                                        <div className="proposed-meeting-row">
                                            <span className="proposed-meeting-label">
                                                {props.viewMode ===
                                                'availability'
                                                    ? 'Start Time:'
                                                    : 'Meeting Start:'}
                                            </span>{' '}
                                            <span className="proposed-meeting-value">
                                                {startTimeLabel}
                                            </span>
                                        </div>
                                        <div className="proposed-meeting-row">
                                            <span className="proposed-meeting-label">
                                                {props.viewMode ===
                                                'availability'
                                                    ? 'End Time:'
                                                    : 'Meeting End:'}
                                            </span>{' '}
                                            <span className="proposed-meeting-value">
                                                {endTimeLabel}
                                            </span>
                                        </div>
                                        <div className="proposed-meeting-row">
                                            <span className="proposed-meeting-label">
                                                Type:
                                            </span>{' '}
                                            <span className="proposed-meeting-value">
                                                {typeLabel}
                                            </span>
                                        </div>
                                        {ev.isCancelled && ev.refundedAmount != null && ev.refundedAmount > 0 && (
                                            <div className="proposed-meeting-row">
                                                <span className="proposed-meeting-label">
                                                    Refunded:
                                                </span>{' '}
                                                <span className="proposed-meeting-value">
                                                    ${ev.refundedAmount.toFixed(2)}
                                                </span>
                                                {ev.cancellationReason && ev.cancellationReason.trim() !== '' && (
                                                    <InfoTooltip
                                                        text={ev.cancellationReason}
                                                        idText={`refund-reason-${ev.meetingProposalId ?? 'unknown'}`}
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {party === TimelineParty.Seller &&
                                            (ev.buyerId ?? ev.posterId) !=
                                                null && (
                                                <div className="proposed-meeting-row">
                                                    <span className="proposed-meeting-label">
                                                        Buyer:
                                                    </span>{' '}
                                                    <span className="proposed-meeting-value">
                                                        {(() => {
                                                            const buyerId =
                                                                ev.buyerId ??
                                                                ev.posterId;
                                                            const buyer =
                                                                activeBuyers?.find(
                                                                    (b) =>
                                                                        Number(
                                                                            b.buyerId
                                                                        ) ===
                                                                        Number(
                                                                            buyerId
                                                                        )
                                                                );
                                                            const buyerName =
                                                                buyer?.companyName ||
                                                                buyer?.buyerAccountName ||
                                                                ev.posterName ||
                                                                `Buyer ${buyerId}`;
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0 m-0 border-0 align-baseline proposed-meeting-buyer-link"
                                                                    onClick={() => {
                                                                        setSelectedBuyerId(
                                                                            Number(
                                                                                buyerId
                                                                            )
                                                                        );
                                                                        setIsBuyerProfilePopupVisible(
                                                                            true
                                                                        );
                                                                    }}>
                                                                    {buyerName}
                                                                </button>
                                                            );
                                                        })()}
                                                    </span>
                                                </div>
                                            )}
                                        {(ev?.format ===
                                            MeetingFormat.InPerson ||
                                            ev?.format ===
                                                MeetingFormat.Mixed) && (
                                            <EditableAddressField
                                                address={ev?.address}
                                                alternativeAddress={
                                                    ev?.alternativeAddress
                                                }
                                                confirmedAddress={
                                                    ev?.confirmedAddress
                                                }
                                                dateStart={ev?.dateStart}
                                                meetingProposalId={
                                                    ev?.meetingProposalId
                                                }
                                                meetingEngagementId={
                                                    ev?.engagementId
                                                }
                                                participantsType={
                                                    ev?.participantsType
                                                }
                                                proposalStatus={
                                                    ev?.proposalStatus
                                                }
                                            />
                                        )}
                                        <EditableDescriptionField
                                            message={ev?.message}
                                            meetingProposalId={
                                                ev?.meetingProposalId
                                            }
                                            meetingEngagementId={
                                                ev?.engagementId
                                            }
                                            participantsType={
                                                ev?.participantsType
                                            }
                                        />
                                        {ev?.files?.length > 0 && (
                                            <div className="proposed-meeting-row">
                                                <span className="proposed-meeting-label">
                                                    Documents:
                                                </span>{' '}
                                                <span className="proposed-meeting-value">
                                                    {ev.files
                                                        .map((f: any) => f.name)
                                                        .join(', ')}
                                                </span>
                                            </div>
                                        )}
                                        {isCancellationFeesEnabled && (
                                            <EditableCancellationFeesField
                                                confirmedCancellationDeadlineDays={
                                                    ev.confirmedCancellationDeadlineDays
                                                }
                                                confirmedCancellationDeadlineHours={
                                                    ev.confirmedCancellationDeadlineHours
                                                }
                                                proposedCancellationDeadlineDays={
                                                    ev.proposedCancellationDeadlineDays
                                                }
                                                proposedCancellationDeadlineHours={
                                                    ev.proposedCancellationDeadlineHours
                                                }
                                                meetingFormat={ev?.format}
                                                meetingProposalId={
                                                    ev?.meetingProposalId
                                                }
                                                meetingEngagementId={
                                                    ev?.engagementId
                                                }
                                                proposalStatus={
                                                    ev?.proposalStatus
                                                }
                                                dateStart={ev?.dateStart}
                                                participantsType={
                                                    ev?.participantsType
                                                }
                                            />
                                        )}
                                    </Col>
                                </Row>
                                {props.selectedEvent?.engagementId &&
                                    props.mode !== 'opportunity' &&
                                    props.mode !== 'engagement' &&
                                    (props.viewMode === 'meetingSeller' ||
                                        props.viewMode === 'meetingBuyer') && (
                                        <Row>
                                            <Col>
                                                <span
                                                    className={`link-text ${props.disabled || isGhostMeeting ? 'disabled' : ''}`}
                                                    onClick={() => {
                                                        if (
                                                            !props.disabled &&
                                                            !isGhostMeeting
                                                        ) {
                                                            navigate(
                                                                `/marketplace/meetings?engagementId=${props.selectedEvent?.engagementId}&meetingId=${props.selectedEvent?.id}`
                                                            );
                                                        }
                                                    }}>
                                                    Go to Opportunity
                                                </span>
                                            </Col>
                                        </Row>
                                    )}
                                <Row className="proposed-meeting-actions">
                                    <Col
                                        md={12}
                                        className="proposed-meeting-actions-buttons">
                                        {!isGhostMeeting && (
                                            <>
                                                {isPendingProposal &&
                                                    showEditButton &&
                                                    (canBuyerTeamMemberManageMeetings(
                                                        summary,
                                                        props.engagementId
                                                    ) ||
                                                        canSellerTeamMemberManageMeetings(
                                                            summary,
                                                            props.engagementId
                                                        )) &&
                                                    props.mode !==
                                                        'buyerTopLevel' && (
                                                        <Button
                                                            type="button"
                                                            className="btn btn-primary proposed-meeting-btn-propose"
                                                            onClick={() => {
                                                                if (
                                                                    isCancellationFeesEnabled &&
                                                                    party ===
                                                                        TimelineParty.Buyer &&
                                                                    summary?.roleId !==
                                                                        USER_ROLES.PRACTICE_SECONDARY_USER &&
                                                                    isPendingBuyerAnswerProposal
                                                                ) {
                                                                    const id =
                                                                        (
                                                                            props.selectedEvent as MeetingObject
                                                                        )
                                                                            ?.meetingProposalId ||
                                                                        props
                                                                            .selectedEvent
                                                                            ?.id;
                                                                    if (
                                                                        id &&
                                                                        props.onBuyerAcceptProposal
                                                                    ) {
                                                                        props.onBuyerAcceptProposal(
                                                                            String(
                                                                                id
                                                                            )
                                                                        );
                                                                    }
                                                                } else {
                                                                    setShowAcceptProposalConfirmation(
                                                                        true
                                                                    );
                                                                }
                                                            }}
                                                            disabled={
                                                                props.disabled
                                                            }>
                                                            Accept Meeting
                                                        </Button>
                                                    )}
                                                {!meetingHasStarted &&
                                                    !isObserver &&
                                                    !isBroker &&
                                                    !isCancelledProposal &&
                                                    (!isAdmin || isConfirmedMeeting) &&
                                                    (canBuyerTeamMemberManageMeetings(
                                                        summary,
                                                        props.engagementId
                                                    ) ||
                                                        canSellerTeamMemberManageMeetings(
                                                            summary,
                                                            props.engagementId
                                                        )) &&
                                                    props.mode !==
                                                        'buyerTopLevel' && (
                                                        <Button
                                                            type="button"
                                                            className="btn btn-outline-danger proposed-meeting-btn-cancel"
                                                            onClick={() => {
                                                                if (isAdmin && isConfirmedMeeting && !isSfas) {
                                                                    setShowCancelConfirmedMeetingModal(true);
                                                                } else {
                                                                    setShowCancelProposalConfirmation(true);
                                                                }
                                                            }}>
                                                            {!isPendingProposal
                                                                ? 'Cancel Meeting'
                                                                : (party ===
                                                                        TimelineParty.Buyer &&
                                                                        isPendingBuyerAnswerProposal) ||
                                                                    (party ===
                                                                        TimelineParty.Seller &&
                                                                        isPendingSellerAnswerProposal)
                                                                  ? 'Decline Proposal'
                                                                  : 'Cancel Proposal'}
                                                        </Button>
                                                    )}
                                                {isCancelledProposal &&
                                                    isAdmin &&
                                                    !isSfas &&
                                                    props.onRefund &&
                                                    (() => {
                                                        const paid = (props.selectedEvent as MeetingObject)?.pricePaid;
                                                        const refunded = (props.selectedEvent as MeetingObject)?.refundedAmount;
                                                        const isFullyRefunded = paid != null && paid > 0 && refunded != null && refunded >= paid;
                                                        return !isFullyRefunded;
                                                    })() && (
                                                        <Button
                                                            type="button"
                                                            className="btn btn-primary proposed-meeting-btn-propose"
                                                            onClick={() =>
                                                                setShowRefundModal(
                                                                    true
                                                                )
                                                            }>
                                                            Refund
                                                        </Button>
                                                    )}
                                                {showEditButton && (
                                                    <Button
                                                        type="button"
                                                        className="btn btn-primary proposed-meeting-btn-propose"
                                                        onClick={() => {
                                                            setIsEditMode(true);
                                                            props.onEditModeChange?.(
                                                                true
                                                            );
                                                        }}
                                                        disabled={
                                                            props.disabled
                                                        }>
                                                        Edit
                                                    </Button>
                                                )}
                                                {!showEditButton &&
                                                !meetingHasStarted &&
                                                isAgreeOnMeetingTimesPhase &&
                                                ((isConfirmedMeeting &&
                                                    props.mode ===
                                                        'opportunity' &&
                                                    party ===
                                                        TimelineParty.Buyer &&
                                                    (canBuyerTeamMemberManageMeetings(
                                                        summary,
                                                        props.engagementId
                                                    ) ||
                                                        canSellerTeamMemberManageMeetings(
                                                            summary,
                                                            props.engagementId
                                                        ))) ||
                                                    ((isConfirmedMeeting ||
                                                        (isPendingProposal &&
                                                            (
                                                                props.selectedEvent as MeetingObject
                                                            ).proposalStatus !==
                                                                MeetingProposalStatus.PendingBuyerAnswer)) &&
                                                        props.mode ===
                                                            'engagement' &&
                                                        party ===
                                                            TimelineParty.Seller &&
                                                        !isSellerTMWithoutCommsHub)) ? (
                                                    <Button
                                                        type="button"
                                                        className="btn btn-primary proposed-meeting-btn-propose"
                                                        onClick={() =>
                                                            props.onProposeNewMeeting?.()
                                                        }>
                                                        Propose New Meeting
                                                    </Button>
                                                ) : null}
                                                {(props.viewMode ===
                                                    'meetingBuyer' ||
                                                    props.viewMode ===
                                                        'meetingSeller' ||
                                                    props.viewMode ===
                                                        'availability') && (
                                                    <>
                                                        {!isEngagementLocked &&
                                                            ev?.status ===
                                                                EngagementMeetingStatus.InProgress &&
                                                            ((props.viewMode ===
                                                                'meetingSeller' &&
                                                                !isSellerTMWithoutCommsHub) ||
                                                                (props.viewMode ===
                                                                    'availability' &&
                                                                    party ===
                                                                        TimelineParty.Seller &&
                                                                    !isSellerTMWithoutCommsHub) ||
                                                                (props.viewMode ===
                                                                    'meetingBuyer' &&
                                                                    canBuyerTMJoinMeeting)) && (
                                                                <Button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    disabled={
                                                                        props.disabled
                                                                    }
                                                                    onClick={
                                                                        handleJoinMeeting
                                                                    }>
                                                                    Join Meeting
                                                                </Button>
                                                            )}
                                                        {!isEngagementLocked &&
                                                            ev?.status ===
                                                                EngagementMeetingStatus.Completed &&
                                                            (
                                                                ev as MeetingObject
                                                            )
                                                                ?.isRecordingAvailable &&
                                                            canViewRecording() &&
                                                            !isSellerTMWithoutCommsHub && (
                                                                <>
                                                                    <Button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        disabled={
                                                                            props.disabled
                                                                        }
                                                                        onClick={() =>
                                                                            fetchRecordingAndOpen(
                                                                                props
                                                                                    .selectedEvent
                                                                                    ?.id ||
                                                                                    0,
                                                                                props
                                                                                    .selectedEvent
                                                                                    ?.engagementId
                                                                            )
                                                                        }>
                                                                        View
                                                                        Recording
                                                                    </Button>
                                                                    <Button
                                                                        type="button"
                                                                        className="btn btn-primary"
                                                                        disabled={
                                                                            props.disabled
                                                                        }
                                                                        onClick={() =>
                                                                            setIsClipsPopupVisible(
                                                                                true
                                                                            )
                                                                        }>
                                                                        See
                                                                        Meeting
                                                                        Clips
                                                                    </Button>
                                                                </>
                                                            )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <Button
                                            type="button"
                                            className="proposed-meeting-btn-close btn btn-secondary"
                                            onClick={() => {
                                                props.onDeselectEvent?.();
                                                props.onDeselectDate?.();
                                            }}>
                                            Close
                                        </Button>
                                    </Col>
                                </Row>
                                {showAcceptProposalConfirmation && (
                                    <ConfirmationModal
                                        isVisible={
                                            showAcceptProposalConfirmation
                                        }
                                        setIsVisible={
                                            setShowAcceptProposalConfirmation
                                        }
                                        header={'Accept Meeting Proposal'}
                                        confirmationText={
                                            'Are you sure you want to accept this meeting proposal?'
                                        }
                                        nextButtonText="Confirm"
                                        cancelButtonText="Cancel"
                                        onConfirm={async () => {
                                            const id =
                                                (
                                                    props.selectedEvent as MeetingObject
                                                )?.meetingProposalId ||
                                                props.selectedEvent?.id;
                                            if (id && props.onAcceptProposal) {
                                                await props.onAcceptProposal(
                                                    String(id)
                                                );
                                                setShowAcceptProposalConfirmation(
                                                    false
                                                );
                                                props.onRefreshMeetings?.();
                                            }
                                        }}
                                    />
                                )}
                                {showCancelProposalConfirmation && (
                                    <EngagementMeetingCancelModal
                                        engagementId={props.engagementId}
                                        isBuyer={party === TimelineParty.Buyer}
                                        isSfas={isSfas}
                                        proposal={
                                            props.selectedEvent as MeetingObject
                                        }
                                        isCancelling={isCancelling}
                                        isVisible={
                                            showCancelProposalConfirmation
                                        }
                                        onClose={() =>
                                            setShowCancelProposalConfirmation(
                                                false
                                            )
                                        }
                                        onCancel={async () => {
                                            setIsCancelling(true);
                                            const id =
                                                (
                                                    props.selectedEvent as MeetingObject
                                                )?.meetingProposalId ||
                                                (
                                                    props.selectedEvent as MeetingObject
                                                )?.id;
                                            if (id && props.onCancelProposal) {
                                                await props.onCancelProposal(
                                                    String(id)
                                                );
                                                setShowCancelProposalConfirmation(
                                                    false
                                                );
                                                props.onRefreshMeetings?.();
                                            }
                                            setIsCancelling(false);
                                        }}
                                    />
                                )}
                                {showCancelConfirmedMeetingModal && (
                                    <ConfirmationDialogWithReason
                                    header='Cancel Meeting'
                                        isVisible={showCancelConfirmedMeetingModal}
                                        setIsVisible={(val) => {
                                            setShowCancelConfirmedMeetingModal(val as boolean);
                                            if (!val) setCancelConfirmedMeetingReason('');
                                        }}
                                        title="Reason"
                                        description={(() => {
                                            const price = (props.selectedEvent as MeetingObject)?.pricePaid;
                                            return price > 0
                                                ? `Cancelling this meeting will issue a full refund of $${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} to the buyer.`
                                                : 'Cancelling this meeting will issue a full refund to the buyer.';
                                        })()}
                                        reasonForAction={cancelConfirmedMeetingReason}
                                        setReasonForAction={setCancelConfirmedMeetingReason}
                                        loader={isCancelConfirmedMeetingLoading}
                                        action={async () => {
                                            const id =
                                                (props.selectedEvent as MeetingObject)?.meetingProposalId ||
                                                (props.selectedEvent as MeetingObject)?.id;
                                            if (id && props.onCancelConfirmedMeeting) {
                                                setIsCancelConfirmedMeetingLoading(true);
                                                try {
                                                    await props.onCancelConfirmedMeeting(String(id), cancelConfirmedMeetingReason);
                                                    setShowCancelConfirmedMeetingModal(false);
                                                    setCancelConfirmedMeetingReason('');
                                                } finally {
                                                    setIsCancelConfirmedMeetingLoading(false);
                                                }
                                            }
                                        }}
                                    />
                                )}
                                {showRefundModal && (
                                    <RefundMeetingModal
                                        isVisible={showRefundModal}
                                        onClose={() =>
                                            setShowRefundModal(false)
                                        }
                                        pricePaid={
                                            (props.selectedEvent as MeetingObject)
                                                ?.pricePaid
                                        }
                                        refundedAmount={
                                            (props.selectedEvent as MeetingObject)
                                                ?.refundedAmount
                                        }
                                        onRefund={async (amount) => {
                                            if (props.onRefund) {
                                                await props.onRefund(amount);
                                            }
                                        }}
                                    />
                                )}
                            </>
                        );
                    })()}
                </div>
            ) : shouldShowForm() ? (
                <div className="form-container">
                    <Formik
                        innerRef={formRef}
                        enableReinitialize={true}
                        initialValues={(() => {
                            const selectedEvent = props.selectedEvent;

                            // Helper to format date to time string (HH:MM) - convert UTC to local time
                            const formatTimeString = (
                                date: Date | string | undefined
                            ): string => {
                                if (!date) return '';
                                try {
                                    const d =
                                        date instanceof Date
                                            ? date
                                            : new Date(date);
                                    if (isNaN(d.getTime())) return '';

                                    // The date from API is in UTC (e.g., "2026-02-16T15:00:00Z")
                                    // JavaScript Date automatically converts to local timezone
                                    // Use getHours() to get the local time
                                    const hours = d
                                        .getHours()
                                        .toString()
                                        .padStart(2, '0');
                                    const minutes = d
                                        .getMinutes()
                                        .toString()
                                        .padStart(2, '0');

                                    return `${hours}:${minutes}`;
                                } catch (error) {
                                    console.error(
                                        'Error formatting time:',
                                        error
                                    );
                                    return '';
                                }
                            };

                            // Default values for regular meetings
                            // If clickedTimeSlot is provided, use it; otherwise default to 12:00-13:00
                            const getDefaultStartTime = () => {
                                if (props.clickedTimeSlot) {
                                    const hours =
                                        props.clickedTimeSlot.startTime
                                            .getHours()
                                            .toString()
                                            .padStart(2, '0');
                                    const minutes =
                                        props.clickedTimeSlot.startTime
                                            .getMinutes()
                                            .toString()
                                            .padStart(2, '0');
                                    return `${hours}:${minutes}`;
                                }
                                return '12:00';
                            };

                            const getDefaultEndTime = () => {
                                if (props.clickedTimeSlot) {
                                    const hours = props.clickedTimeSlot.endTime
                                        .getHours()
                                        .toString()
                                        .padStart(2, '0');
                                    const minutes =
                                        props.clickedTimeSlot.endTime
                                            .getMinutes()
                                            .toString()
                                            .padStart(2, '0');
                                    return `${hours}:${minutes}`;
                                }
                                return '13:00';
                            };

                            // Convert duration to minutes
                            const getValidDuration = (
                                duration: string | number
                            ) => {
                                // If duration is a string in "HH:MM:SS" or "HH:MM" format, convert to minutes
                                if (
                                    typeof duration === 'string' &&
                                    duration.includes(':')
                                ) {
                                    const parts = duration.split(':');
                                    const hours = parseInt(parts[0]) || 0;
                                    const minutes = parseInt(parts[1]) || 0;
                                    const totalMinutes = hours * 60 + minutes;
                                    return totalMinutes;
                                }

                                // If it's already a number, return it
                                const numDuration = Number(duration);
                                return isNaN(numDuration) ? 0 : numDuration;
                            };

                            const isAvailabilityBlock =
                                isAvailabilityBlockObject(props.selectedEvent);
                            const meeting =
                                props.selectedEvent as MeetingObject;
                            const avBlock =
                                props.selectedEvent as AvailabilityBlockObject;

                            // When editing a proposal and user clicked an availability block, use that block to populate the form
                            const isEditingProposalWithBlock =
                                props.blockToPopulateFormWhenEditingProposal &&
                                selectedEvent &&
                                !isAvailabilityBlockObject(selectedEvent) &&
                                (selectedEvent as MeetingObject)
                                    .isMeetingProposal === true;
                            const blockForProposalEdit =
                                isEditingProposalWithBlock
                                    ? props.blockToPopulateFormWhenEditingProposal
                                    : null;

                            // Check if all buyers are selected
                            const allBuyerIds = activeBuyers.map(
                                (b) => b.buyerId
                            );
                            const selectedBuyerIds = avBlock?.buyers || [];
                            const isSelectAllBuyers =
                                selectedBuyerIds.length ===
                                    allBuyerIds.length &&
                                selectedBuyerIds.every((id: number) =>
                                    allBuyerIds.includes(id)
                                );

                            const sourceBlock =
                                blockForProposalEdit ||
                                props.availabilityBlockFormCopy ||
                                (isAvailabilityBlock ? avBlock : null);

                            const blockFormats = sourceBlock?.formats ?? [];
                            const useOnlyFirstBlockFormat =
                                blockFormats.length > 1 &&
                                !(
                                    props.viewMode === 'availability' &&
                                    isAvailabilityBlock
                                );
                            const firstBlockFormatRaw =
                                useOnlyFirstBlockFormat && blockFormats[0]
                                    ? (blockFormats[0].format ??
                                      (blockFormats[0] as any).Format)
                                    : null;
                            const firstBlockFormat =
                                firstBlockFormatRaw != null
                                    ? typeof firstBlockFormatRaw === 'number'
                                        ? firstBlockFormatRaw
                                        : ((MeetingFormat as any)[
                                              firstBlockFormatRaw
                                          ] ?? firstBlockFormatRaw)
                                    : null;

                            // Use prefill data when creating a new meeting from "Propose New Meeting" button
                            const prefillData = props.meetingPrefillData;
                            const isUsingPrefillData =
                                prefillData && !props.selectedEvent;

                            // When user clicked a slot (e.g. after "Propose New Meeting") or a block during proposal edit, prefer that for times
                            const startTimeValue = props.clickedTimeSlot
                                ? formatTimeString(
                                      props.clickedTimeSlot.startTime
                                  )
                                : blockForProposalEdit?.dateStart
                                  ? formatTimeString(
                                        blockForProposalEdit.dateStart
                                    )
                                  : isUsingPrefillData && prefillData.dateStart
                                    ? formatTimeString(prefillData.dateStart)
                                    : selectedEvent?.dateStart
                                      ? formatTimeString(
                                            selectedEvent.dateStart
                                        )
                                      : getDefaultStartTime();
                            const endTimeValue = props.clickedTimeSlot
                                ? formatTimeString(
                                      props.clickedTimeSlot.endTime
                                  )
                                : blockForProposalEdit?.dateEnd
                                  ? formatTimeString(
                                        blockForProposalEdit.dateEnd
                                    )
                                  : isUsingPrefillData && prefillData.dateEnd
                                    ? formatTimeString(prefillData.dateEnd)
                                    : selectedEvent?.dateEnd
                                      ? formatTimeString(selectedEvent.dateEnd)
                                      : getDefaultEndTime();

                            const defaultValues = {
                                meetingTitle: isUsingPrefillData
                                    ? prefillData.name
                                    : props.selectedEvent?.name || '',
                                message: isUsingPrefillData
                                    ? prefillData.message
                                    : props.selectedEvent?.message || '',
                                startTime: startTimeValue,
                                endTime: endTimeValue,
                                isVirtualMeeting: isUsingPrefillData
                                    ? prefillData.isVirtualMeeting || false
                                    : sourceBlock
                                      ? useOnlyFirstBlockFormat
                                          ? firstBlockFormat ===
                                                MeetingFormat.Virtual ||
                                            firstBlockFormat === 1
                                          : !!sourceBlock.formats?.find(
                                                (f: any) =>
                                                    f.format ===
                                                        MeetingFormat.Virtual ||
                                                    f.format === 1
                                            )
                                      : meeting?.format ===
                                        MeetingFormat.Virtual,
                                maxVirtualMeetingDuration: sourceBlock
                                    ? useOnlyFirstBlockFormat
                                        ? firstBlockFormat ===
                                              MeetingFormat.Virtual ||
                                          firstBlockFormat === 1
                                            ? getDurationMinutesFromBlockCopy(
                                                  sourceBlock,
                                                  MeetingFormat.Virtual
                                              )
                                            : 0
                                        : getDurationMinutesFromBlockCopy(
                                              sourceBlock,
                                              MeetingFormat.Virtual
                                          )
                                    : isAvailabilityBlock
                                      ? getValidDuration(
                                            avBlock?.formats?.find(
                                                (f: any) =>
                                                    f.format ===
                                                    MeetingFormat.Virtual
                                            )?.duration || 0
                                        )
                                      : 0,
                                isInPersonMeeting: isUsingPrefillData
                                    ? prefillData.isInPersonMeeting || false
                                    : sourceBlock
                                      ? useOnlyFirstBlockFormat
                                          ? firstBlockFormat ===
                                                MeetingFormat.InPerson ||
                                            firstBlockFormat === 2
                                          : !!sourceBlock.formats?.find(
                                                (f: any) =>
                                                    f.format ===
                                                        MeetingFormat.InPerson ||
                                                    f.format === 2
                                            )
                                      : meeting?.format ===
                                        MeetingFormat.InPerson,
                                maxInPersonMeetingDuration: sourceBlock
                                    ? useOnlyFirstBlockFormat
                                        ? firstBlockFormat ===
                                              MeetingFormat.InPerson ||
                                          firstBlockFormat === 2
                                            ? getDurationMinutesFromBlockCopy(
                                                  sourceBlock,
                                                  MeetingFormat.InPerson
                                              )
                                            : 0
                                        : getDurationMinutesFromBlockCopy(
                                              sourceBlock,
                                              MeetingFormat.InPerson
                                          )
                                    : isAvailabilityBlock
                                      ? getValidDuration(
                                            avBlock?.formats?.find(
                                                (f: any) =>
                                                    f.format ===
                                                    MeetingFormat.InPerson
                                            )?.duration || 0
                                        )
                                      : 0,
                                isMixedMeeting: isUsingPrefillData
                                    ? prefillData.isMixedMeeting || false
                                    : sourceBlock
                                      ? useOnlyFirstBlockFormat
                                          ? firstBlockFormat ===
                                                MeetingFormat.Mixed ||
                                            firstBlockFormat === 3
                                          : !!sourceBlock.formats?.find(
                                                (f: any) =>
                                                    f.format ===
                                                        MeetingFormat.Mixed ||
                                                    f.format === 3
                                            )
                                      : meeting?.format === MeetingFormat.Mixed,
                                maxMixedMeetingDuration: sourceBlock
                                    ? useOnlyFirstBlockFormat
                                        ? firstBlockFormat ===
                                              MeetingFormat.Mixed ||
                                          firstBlockFormat === 3
                                            ? getDurationMinutesFromBlockCopy(
                                                  sourceBlock,
                                                  MeetingFormat.Mixed
                                              )
                                            : 0
                                        : getDurationMinutesFromBlockCopy(
                                              sourceBlock,
                                              MeetingFormat.Mixed
                                          )
                                    : isAvailabilityBlock
                                      ? getValidDuration(
                                            avBlock?.formats?.find(
                                                (f: any) =>
                                                    f.format ===
                                                    MeetingFormat.Mixed
                                            )?.duration || 0
                                        )
                                      : 0,
                                city:
                                    blockForProposalEdit?.address?.city != null
                                        ? blockForProposalEdit.address.city
                                        : isUsingPrefillData &&
                                            prefillData.address
                                          ? prefillData.address.city
                                          : selectedEvent?.address?.city ||
                                            getMaxFteDvmPracticeLocation.city,
                                state:
                                    blockForProposalEdit?.address?.state != null
                                        ? blockForProposalEdit.address.state
                                        : isUsingPrefillData &&
                                            prefillData.address
                                          ? prefillData.address.state
                                          : selectedEvent?.address?.state ||
                                            getMaxFteDvmPracticeLocation.state,
                                address:
                                    blockForProposalEdit?.address?.address !=
                                    null
                                        ? blockForProposalEdit.address.address
                                        : isUsingPrefillData &&
                                            prefillData.address
                                          ? prefillData.address.address
                                          : selectedEvent?.address?.address ||
                                            '',
                                isSelectAllBuyers: isSelectAllBuyers,
                                selectedBuyerIds: isAvailabilityBlock
                                    ? avBlock?.buyers || []
                                    : isUsingPrefillData &&
                                        prefillData.posterId != null
                                      ? [prefillData.posterId]
                                      : props.selectedBuyers?.map(
                                            (buyer) => buyer.buyerId
                                        ) ||
                                        meeting?.participants?.map(
                                            (participant) => participant.id
                                        ) ||
                                        [],
                                hasCancellationFee: isAvailabilityBlock
                                    ? avBlock?.hasCancellationFee ||
                                      avBlock?.formats?.some(
                                          (f: any) =>
                                              (f.CancellationDeadlineHours ??
                                                  f.cancellationDeadlineHours) !=
                                                  null ||
                                              (f.CancellationDeadlineDays ??
                                                  f.cancellationDeadlineDays) !=
                                                  null
                                      ) ||
                                      false
                                    : isUsingPrefillData
                                      ? prefillData.confirmedCancellationDeadlineHours !=
                                            null ||
                                        prefillData.confirmedCancellationDeadlineDays !=
                                            null
                                      : (() => {
                                            return (
                                                (
                                                    props.selectedEvent as MeetingObject
                                                )
                                                    ?.confirmedCancellationDeadlineHours !=
                                                    null ||
                                                (
                                                    props.selectedEvent as MeetingObject
                                                )
                                                    ?.confirmedCancellationDeadlineDays !=
                                                    null
                                            );
                                        })(),
                                cancellationDeadlineVirtualHours:
                                    isAvailabilityBlock
                                        ? (() => {
                                              const f: any =
                                                  avBlock?.formats?.find(
                                                      (f: any) =>
                                                          (f.format ??
                                                              f.Format) ===
                                                          MeetingFormat.Virtual
                                                  );
                                              return f
                                                  ? (f.CancellationDeadlineHours ??
                                                        f.cancellationDeadlineHours ??
                                                        null)
                                                  : null;
                                          })()
                                        : isUsingPrefillData
                                          ? prefillData.format ===
                                            MeetingFormat.Virtual
                                              ? (prefillData.confirmedCancellationDeadlineHours ??
                                                null)
                                              : null
                                          : (() => {
                                                const ev =
                                                    props.selectedEvent as MeetingObject;
                                                if (
                                                    (ev?.format ??
                                                        (ev as any)?.Format) !==
                                                    MeetingFormat.Virtual
                                                )
                                                    return null;
                                                return (
                                                    ev?.confirmedCancellationDeadlineHours ??
                                                    null
                                                );
                                            })(),
                                cancellationDeadlineInPersonDays:
                                    isAvailabilityBlock
                                        ? (() => {
                                              const f: any =
                                                  avBlock?.formats?.find(
                                                      (f: any) =>
                                                          (f.format ??
                                                              f.Format) ===
                                                          MeetingFormat.InPerson
                                                  );
                                              return f
                                                  ? (f.CancellationDeadlineDays ??
                                                        f.cancellationDeadlineDays ??
                                                        null)
                                                  : null;
                                          })()
                                        : isUsingPrefillData
                                          ? prefillData.format ===
                                            MeetingFormat.InPerson
                                              ? (prefillData.confirmedCancellationDeadlineDays ??
                                                null)
                                              : null
                                          : (() => {
                                                const ev =
                                                    props.selectedEvent as MeetingObject;
                                                if (
                                                    (ev?.format ??
                                                        (ev as any)?.Format) !==
                                                    MeetingFormat.InPerson
                                                )
                                                    return null;
                                                return (
                                                    ev?.confirmedCancellationDeadlineDays ??
                                                    null
                                                );
                                            })(),
                                cancellationDeadlineMixedDays:
                                    isAvailabilityBlock
                                        ? (() => {
                                              const f: any =
                                                  avBlock?.formats?.find(
                                                      (f: any) =>
                                                          (f.format ??
                                                              f.Format) ===
                                                          MeetingFormat.Mixed
                                                  );
                                              return f
                                                  ? (f.CancellationDeadlineDays ??
                                                        f.cancellationDeadlineDays ??
                                                        null)
                                                  : null;
                                          })()
                                        : isUsingPrefillData
                                          ? prefillData.format ===
                                            MeetingFormat.Mixed
                                              ? (prefillData.confirmedCancellationDeadlineDays ??
                                                null)
                                              : null
                                          : (() => {
                                                const ev =
                                                    props.selectedEvent as MeetingObject;
                                                if (
                                                    (ev?.format ??
                                                        (ev as any)?.Format) !==
                                                    MeetingFormat.Mixed
                                                )
                                                    return null;
                                                return (
                                                    ev?.confirmedCancellationDeadlineDays ??
                                                    null
                                                );
                                            })(),
                            };

                            return defaultValues;
                        })()}
                        validationSchema={yup.object().shape({
                            startTime: yup
                                .string()
                                .required(
                                    props.viewMode === 'availability'
                                        ? 'Start Time is required'
                                        : 'Meeting Start is required'
                                ),
                            endTime: yup
                                .string()
                                .required(
                                    props.viewMode === 'availability'
                                        ? 'End Time is required'
                                        : 'Meeting End is required'
                                )
                                .test(
                                    'is-after-start',
                                    props.viewMode === 'availability'
                                        ? 'End Time must be after Start Time'
                                        : 'Meeting End must be after Meeting Start',
                                    function (endTime) {
                                        const { startTime } = this.parent;
                                        if (!startTime || !endTime) return true;

                                        const [startHours, startMinutes] =
                                            startTime.split(':').map(Number);
                                        const [endHours, endMinutes] = endTime
                                            .split(':')
                                            .map(Number);

                                        const startTotalMinutes =
                                            startHours * 60 + startMinutes;
                                        const endTotalMinutes =
                                            endHours * 60 + endMinutes;

                                        return (
                                            endTotalMinutes > startTotalMinutes
                                        );
                                    }
                                )
                                .test(
                                    'min-duration',
                                    'Meeting must be at least 30 minutes',
                                    function (endTime) {
                                        const { startTime } = this.parent;
                                        if (!startTime || !endTime) return true;

                                        const [startHours, startMinutes] =
                                            startTime.split(':').map(Number);
                                        const [endHours, endMinutes] = endTime
                                            .split(':')
                                            .map(Number);

                                        const startTotalMinutes =
                                            startHours * 60 + startMinutes;
                                        const endTotalMinutes =
                                            endHours * 60 + endMinutes;
                                        const duration =
                                            endTotalMinutes - startTotalMinutes;

                                        return duration >= 30;
                                    }
                                ),
                            maxInPersonMeetingDuration: yup
                                .number()
                                .test(
                                    'required-if-checked',
                                    'Duration is required',
                                    function (value) {
                                        // Skip validation for buyers proposing meetings during AgreeOnMeetingTimes phase
                                        if (isBuyerProposingMeeting) {
                                            return true;
                                        }
                                        const { isInPersonMeeting } =
                                            this.parent;
                                        if (
                                            isInPersonMeeting &&
                                            (!value || value === 0)
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    }
                                ),
                            maxVirtualMeetingDuration: yup
                                .number()
                                .test(
                                    'required-if-checked',
                                    'Duration is required',
                                    function (value) {
                                        // Skip validation for buyers proposing meetings during AgreeOnMeetingTimes phase
                                        if (isBuyerProposingMeeting) {
                                            return true;
                                        }
                                        const { isVirtualMeeting } =
                                            this.parent;
                                        if (
                                            isVirtualMeeting &&
                                            (!value || value === 0)
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    }
                                ),
                            maxMixedMeetingDuration: yup
                                .number()
                                .test(
                                    'required-if-checked',
                                    'Duration is required',
                                    function (value) {
                                        // Skip validation for buyers proposing meetings during AgreeOnMeetingTimes phase
                                        if (isBuyerProposingMeeting) {
                                            return true;
                                        }
                                        const { isMixedMeeting } = this.parent;
                                        if (
                                            isMixedMeeting &&
                                            (!value || value === 0)
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    }
                                ),
                            city: yup
                                .string()
                                .test(
                                    'required-if-in-person',
                                    'City is required',
                                    function (value) {
                                        const {
                                            isInPersonMeeting,
                                            isMixedMeeting,
                                        } = this.parent;
                                        if (
                                            (isInPersonMeeting ||
                                                isMixedMeeting) &&
                                            (!value || value.trim() === '')
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    }
                                ),
                            state: yup
                                .string()
                                .test(
                                    'required-if-in-person',
                                    'State is required',
                                    function (value) {
                                        const {
                                            isInPersonMeeting,
                                            isMixedMeeting,
                                        } = this.parent;
                                        if (
                                            (isInPersonMeeting ||
                                                isMixedMeeting) &&
                                            (!value || value.trim() === '')
                                        ) {
                                            return false;
                                        }
                                        return true;
                                    }
                                ),
                        })}
                        onSubmit={async (values) => {
                            // Validate meeting type selection
                            const hasAtLeastOneMeetingType =
                                values.isInPersonMeeting ||
                                values.isVirtualMeeting ||
                                values.isMixedMeeting;
                            if (!hasAtLeastOneMeetingType) {
                                setMeetingTypeError(true);
                                return;
                            }
                            setMeetingTypeError(false);

                            // Validate buyer selection only in availability mode
                            if (props.viewMode === 'availability') {
                                if (
                                    !values.isSelectAllBuyers &&
                                    (!values.selectedBuyerIds ||
                                        values.selectedBuyerIds.length === 0)
                                ) {
                                    setBuyerSelectionError(true);
                                    return;
                                }
                            }
                            setBuyerSelectionError(false);

                            // Only handle availability mode with internal API
                            if (props.viewMode !== 'availability') {
                                return;
                            }

                            // Get engagement ID
                            const engagementId = currentEngagement?.id;
                            if (!engagementId) {
                                createNotification(
                                    'No engagement selected',
                                    'error'
                                );
                                return;
                            }

                            setIsSubmitting(true);

                            // Clear prefill data when form is submitted
                            if (props.meetingPrefillData) {
                                props.onClearPrefillData?.();
                            }

                            try {
                                // Convert time strings to UTC ISO format
                                // User enters LOCAL time (e.g., 17:00 in GMT+0200)
                                // API expects UTC time (e.g., 15:00:00Z)
                                const dateStart = new Date(props.selectedDate);
                                const [startHours, startMinutes] =
                                    values.startTime.split(':').map(Number);
                                dateStart.setHours(
                                    startHours,
                                    startMinutes,
                                    0,
                                    0
                                );
                                const dateStartISO = dateStart.toISOString();

                                const dateEnd = new Date(props.selectedDate);
                                const [endHours, endMinutes] = values.endTime
                                    .split(':')
                                    .map(Number);
                                dateEnd.setHours(endHours, endMinutes, 0, 0);
                                const dateEndISO = dateEnd.toISOString();

                                // Validate that the new/updated block doesn't intersect with existing blocks
                                // Blocks can touch (one ends when another starts) but cannot overlap
                                const newBlockStart = dateStart.getTime();
                                const newBlockEnd = dateEnd.getTime();

                                // Check if we're editing an existing block
                                const existingBlockId = (
                                    props.selectedEvent as AvailabilityBlockObject
                                )?.availabilityBlockId;
                                const isEditing = isEditMode && existingBlockId;

                                const hasIntersection =
                                    availabilityBlocks?.some(
                                        (existingBlock) => {
                                            // Skip if this is the same block being edited
                                            if (
                                                isEditing &&
                                                existingBlock.id ===
                                                    existingBlockId
                                            ) {
                                                return false;
                                            }

                                            const existingStart = new Date(
                                                existingBlock.dateStart
                                            ).getTime();
                                            const existingEnd = new Date(
                                                existingBlock.dateEnd
                                            ).getTime();

                                            // Two blocks intersect if: startA < endB AND endA > startB
                                            // They can touch if: endA === startB OR startA === endB
                                            return (
                                                newBlockStart < existingEnd &&
                                                newBlockEnd > existingStart
                                            );
                                        }
                                    );

                                if (hasIntersection) {
                                    setIsSubmitting(false);
                                    createNotification(
                                        'This availability block overlaps with an existing one. Please adjust the time range.',
                                        'error'
                                    );
                                    return;
                                }

                                // Helper to convert minutes to TimeSpan format (HH:MM:SS)
                                const minutesToTimeSpan = (
                                    minutes: number
                                ): string => {
                                    const hours = Math.floor(minutes / 60);
                                    const mins = minutes % 60;
                                    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
                                };

                                // Build formats array
                                const formats = [];
                                if (
                                    values.isInPersonMeeting &&
                                    values.maxInPersonMeetingDuration
                                ) {
                                    formats.push({
                                        Format: MeetingFormat.InPerson,
                                        Duration: minutesToTimeSpan(
                                            values.maxInPersonMeetingDuration
                                        ),
                                        CancellationDeadlineDays:
                                            values.hasCancellationFee
                                                ? values.cancellationDeadlineInPersonDays ||
                                                  1
                                                : null,
                                    });
                                }
                                if (
                                    values.isVirtualMeeting &&
                                    values.maxVirtualMeetingDuration
                                ) {
                                    formats.push({
                                        Format: MeetingFormat.Virtual,
                                        Duration: minutesToTimeSpan(
                                            values.maxVirtualMeetingDuration
                                        ),
                                        CancellationDeadlineHours:
                                            values.hasCancellationFee
                                                ? values.cancellationDeadlineVirtualHours ||
                                                  1
                                                : null,
                                    });
                                }
                                if (
                                    values.isMixedMeeting &&
                                    values.maxMixedMeetingDuration
                                ) {
                                    formats.push({
                                        Format: MeetingFormat.Mixed,
                                        Duration: minutesToTimeSpan(
                                            values.maxMixedMeetingDuration
                                        ),
                                        CancellationDeadlineDays:
                                            values.hasCancellationFee
                                                ? values.cancellationDeadlineMixedDays ||
                                                  1
                                                : null,
                                    });
                                }

                                // Build buyers array
                                const allBuyerIds = activeBuyers.map(
                                    (b) => b.buyerId
                                );
                                const buyers = values.isSelectAllBuyers
                                    ? allBuyerIds
                                    : values.selectedBuyerIds || [];

                                // Build request body
                                const requestBody = {
                                    Id: isEditing
                                        ? existingBlockId
                                        : '00000000-0000-0000-0000-000000000000',
                                    DateStart: dateStartISO,
                                    DateEnd: dateEndISO,
                                    Formats: formats,
                                    Buyers: buyers,
                                    City: values.city || null,
                                    State: values.state || null,
                                    Address: values.address || null,
                                    HasCancellationFee:
                                        values.hasCancellationFee || false,
                                };

                                // Call API (POST for create, PUT for update)
                                const response = isEditing
                                    ? await axios.put(
                                          `/api/Marketplace/Engagements/${engagementId}/EngagementMeetingAvailabilityBlocks/${existingBlockId}`,
                                          requestBody
                                      )
                                    : await axios.post(
                                          `/api/Marketplace/Engagements/${engagementId}/EngagementMeetingAvailabilityBlocks`,
                                          requestBody
                                      );

                                if (
                                    response.status === 200 ||
                                    response.status === 201
                                ) {
                                    createNotification(
                                        isEditing
                                            ? 'Availability updated successfully'
                                            : 'Availability added successfully',
                                        'success'
                                    );

                                    // Get the block ID from response
                                    const blockId =
                                        response.data?.id ||
                                        response.data ||
                                        existingBlockId;

                                    // Exit edit mode
                                    setIsEditMode(false);

                                    // Refresh and keep the block selected
                                    props.onRefreshMeetings?.(blockId);
                                } else {
                                    createNotification(
                                        isEditing
                                            ? 'Failed to update availability'
                                            : 'Failed to add availability',
                                        'error'
                                    );
                                }
                            } catch (error: any) {
                                const existingBlockId = (
                                    props.selectedEvent as AvailabilityBlockObject
                                )?.availabilityBlockId;
                                const isEditing = isEditMode && existingBlockId;

                                console.error(
                                    isEditing
                                        ? 'Error updating availability:'
                                        : 'Error adding availability:',
                                    error
                                );
                                createNotification(
                                    error.response?.data?.message ||
                                        (isEditing
                                            ? 'Failed to update availability'
                                            : 'Failed to add availability'),
                                    'error'
                                );
                            } finally {
                                setIsSubmitting(false);
                            }
                        }}>
                        {({
                            values,
                            setFieldValue,
                            errors,
                            touched,
                            setFieldTouched,
                            validateForm,
                            resetForm,
                        }) => {
                            formDateTimeRef.current = {
                                startTime: values.startTime ?? '',
                                endTime: values.endTime ?? '',
                            };

                            // Helper to convert date to time string (HH:MM)
                            const dateToTimeString = (date: Date): string => {
                                const hours = date
                                    .getHours()
                                    .toString()
                                    .padStart(2, '0');
                                const minutes = date
                                    .getMinutes()
                                    .toString()
                                    .padStart(2, '0');
                                return `${hours}:${minutes}`;
                            };

                            // Helper to convert time string to date
                            const timeStringToDate = (
                                timeStr: string,
                                baseDate: Date
                            ): Date => {
                                if (!timeStr) return baseDate;
                                const [hours, minutes] = timeStr
                                    .split(':')
                                    .map(Number);
                                const newDate = new Date(baseDate);
                                newDate.setHours(hours, minutes, 0, 0);
                                return newDate;
                            };

                            // Initialize time values from startDate/endDate if not set
                            if (!values.startTime && startDate) {
                                setFieldValue(
                                    'startTime',
                                    dateToTimeString(startDate)
                                );
                            }
                            if (!values.endTime && endDate) {
                                setFieldValue(
                                    'endTime',
                                    dateToTimeString(endDate)
                                );
                            }

                            // Calculate max allowed duration from time difference
                            const calculateMaxAllowedDuration = (): number => {
                                if (!values.startTime || !values.endTime)
                                    return 0;

                                const [startHours, startMinutes] =
                                    values.startTime.split(':').map(Number);
                                const [endHours, endMinutes] = values.endTime
                                    .split(':')
                                    .map(Number);

                                const startTotalMinutes =
                                    startHours * 60 + startMinutes;
                                const endTotalMinutes =
                                    endHours * 60 + endMinutes;

                                return endTotalMinutes - startTotalMinutes;
                            };

                            const maxAllowedMinutes =
                                calculateMaxAllowedDuration();

                            // Check if duration exceeds max allowed
                            const checkDurationExceedsMax = (
                                durationMinutes: number
                            ): { error: boolean; message?: string } => {
                                if (
                                    maxAllowedMinutes > 0 &&
                                    durationMinutes > maxAllowedMinutes
                                ) {
                                    return {
                                        error: true,
                                        message: `Meeting duration cannot exceed the availability window`,
                                    };
                                }
                                return { error: false };
                            };

                            // When buyer has a block (copy or selected), use its durations for display; selecting a meeting type shows length from block or 0:00
                            const blockForDisplay =
                                props.availabilityBlockFormCopy ??
                                (isAvailabilityBlockObject(props.selectedEvent)
                                    ? props.selectedEvent
                                    : null);
                            // When no block is selected but form time falls inside an availability block, use that block for Maximum Meeting Length display (e.g. user clicked a slot then changed time into a block)
                            let blockForDisplayFromFormTime: AvailabilityBlockObject | null =
                                null;
                            if (
                                !blockForDisplay &&
                                isBuyerInAgreeOnMeetingTimesPhase &&
                                props.availabilityBlocksForLookup?.length &&
                                props.selectedDate &&
                                values.startTime?.trim() &&
                                values.endTime?.trim()
                            ) {
                                const formStart = new Date(props.selectedDate);
                                const [sh, sm] = (values.startTime || '')
                                    .trim()
                                    .split(':')
                                    .map(Number);
                                const [eh, em] = (values.endTime || '')
                                    .trim()
                                    .split(':')
                                    .map(Number);
                                formStart.setHours(sh || 0, sm || 0, 0, 0);
                                const formEnd = new Date(props.selectedDate);
                                formEnd.setHours(eh || 0, em || 0, 0, 0);
                                const formStartT = formStart.getTime();
                                const formEndT = formEnd.getTime();
                                if (formStartT < formEndT) {
                                    blockForDisplayFromFormTime =
                                        props.availabilityBlocksForLookup.find(
                                            (block) => {
                                                const bStart = new Date(
                                                    (block as any).dateStart ??
                                                        (block as any)
                                                            .DateStart ??
                                                        0
                                                ).getTime();
                                                const bEnd = new Date(
                                                    (block as any).dateEnd ??
                                                        (block as any)
                                                            .DateEnd ??
                                                        0
                                                ).getTime();
                                                return (
                                                    formStartT <= bEnd &&
                                                    formEndT >= bStart
                                                );
                                            }
                                        ) ?? null;
                                }
                            }
                            const effectiveBlockForDisplay =
                                blockForDisplay ?? blockForDisplayFromFormTime;
                            const useBlockForDisplay =
                                isBuyerInAgreeOnMeetingTimesPhase &&
                                effectiveBlockForDisplay;
                            const refInPersonMinutes = useBlockForDisplay
                                ? getDurationMinutesFromBlockCopy(
                                      effectiveBlockForDisplay!,
                                      MeetingFormat.InPerson
                                  )
                                : 0;
                            const refVirtualMinutes = useBlockForDisplay
                                ? getDurationMinutesFromBlockCopy(
                                      effectiveBlockForDisplay!,
                                      MeetingFormat.Virtual
                                  )
                                : 0;
                            const refMixedMinutes = useBlockForDisplay
                                ? getDurationMinutesFromBlockCopy(
                                      effectiveBlockForDisplay!,
                                      MeetingFormat.Mixed
                                  )
                                : 0;

                            // Compare form with availability block for mismatch highlighting (orange). Use effective block (includes block from form time when no selection), then copy, reference, selectedEvent, or first block on selected date.
                            const blockForComparison: AvailabilityBlockObject | null =
                                effectiveBlockForDisplay ??
                                props.referenceAvailabilityBlock ??
                                (isAvailabilityBlockObject(props.selectedEvent)
                                    ? (props.selectedEvent as AvailabilityBlockObject)
                                    : null) ??
                                props.availabilityBlockForSelectedDate ??
                                null;
                            // Always compute from current form times so it updates dynamically when date/time changes.
                            // Cannot use blockForComparison: it is frozen on the clicked block (via availabilityBlockFormCopy)
                            // even when the user edits the time pickers, because blockForDisplayFromFormTime is gated by !blockForDisplay.
                            let blockWithCancellationData: AvailabilityBlockObject | null =
                                null;
                            if (
                                isCancellationFeesEnabled &&
                                isBuyerInAgreeOnMeetingTimesPhase &&
                                props.availabilityBlocksForLookup?.length &&
                                props.selectedDate &&
                                values.startTime?.trim() &&
                                values.endTime?.trim()
                            ) {
                                const cdFormStart = new Date(
                                    props.selectedDate
                                );
                                const [cdSh, cdSm] = (values.startTime || '')
                                    .trim()
                                    .split(':')
                                    .map(Number);
                                const [cdEh, cdEm] = (values.endTime || '')
                                    .trim()
                                    .split(':')
                                    .map(Number);
                                cdFormStart.setHours(
                                    cdSh || 0,
                                    cdSm || 0,
                                    0,
                                    0
                                );
                                const cdFormEnd = new Date(props.selectedDate);
                                cdFormEnd.setHours(cdEh || 0, cdEm || 0, 0, 0);
                                if (
                                    cdFormStart.getTime() < cdFormEnd.getTime()
                                ) {
                                    blockWithCancellationData =
                                        props.availabilityBlocksForLookup.find(
                                            (block) => {
                                                const bStart = new Date(
                                                    (block as any).dateStart ??
                                                        (block as any)
                                                            .DateStart ??
                                                        0
                                                ).getTime();
                                                const bEnd = new Date(
                                                    (block as any).dateEnd ??
                                                        (block as any)
                                                            .DateEnd ??
                                                        0
                                                ).getTime();
                                                return (
                                                    cdFormStart.getTime() <=
                                                        bEnd &&
                                                    cdFormEnd.getTime() >=
                                                        bStart
                                                );
                                            }
                                        ) ?? null;
                                }
                            }

                            // Cancellation deadline to display for the currently selected meeting type
                            const cancellationDeadlineDisplay: {
                                value: number;
                                unit: 'hrs' | 'days';
                            } | null =
                                isCancellationFeesEnabled &&
                                isBuyerInAgreeOnMeetingTimesPhase &&
                                blockWithCancellationData
                                    ? (() => {
                                          const selectedFormat =
                                              values.isVirtualMeeting
                                                  ? MeetingFormat.Virtual
                                                  : values.isInPersonMeeting
                                                    ? MeetingFormat.InPerson
                                                    : values.isMixedMeeting
                                                      ? MeetingFormat.Mixed
                                                      : null;
                                          if (!selectedFormat) return null;
                                          const matchedFmt =
                                              blockWithCancellationData.formats?.find(
                                                  (f: any) =>
                                                      (f.format ?? f.Format) ===
                                                      selectedFormat
                                              );
                                          if (!matchedFmt) return null;
                                          const fAny = matchedFmt as any;
                                          if (
                                              selectedFormat ===
                                              MeetingFormat.Virtual
                                          ) {
                                              const hours =
                                                  fAny.CancellationDeadlineHours ??
                                                  fAny.cancellationDeadlineHours;
                                              return hours != null
                                                  ? {
                                                        value: hours,
                                                        unit: 'hrs' as const,
                                                    }
                                                  : null;
                                          }
                                          const days =
                                              fAny.CancellationDeadlineDays ??
                                              fAny.cancellationDeadlineDays;
                                          return days != null
                                              ? {
                                                    value: days,
                                                    unit: 'days' as const,
                                                }
                                              : null;
                                      })()
                                    : null;

                            // Stabilize: keep using last block when a render temporarily has none, so orange highlight doesn't flicker away
                            if (!isBuyerInAgreeOnMeetingTimesPhase) {
                                lastBlockForValidationRef.current = null;
                            } else if (blockForComparison) {
                                lastBlockForValidationRef.current =
                                    blockForComparison;
                            }
                            const effectiveBlockForValidation =
                                blockForComparison ??
                                (isBuyerInAgreeOnMeetingTimesPhase
                                    ? lastBlockForValidationRef.current
                                    : null);
                            const useBlockForValidation =
                                !!effectiveBlockForValidation;
                            const timeStrToMinutes = (s: string) => {
                                if (!s || !s.trim()) return 0;
                                const parts = s.trim().split(':');
                                const hours =
                                    parseInt(parts[0] || '0', 10) || 0;
                                const mins =
                                    parseInt(
                                        String(parts[1] || '0')
                                            .replace(/\D/g, '')
                                            .slice(0, 2) || '0',
                                        10
                                    ) || 0;
                                return hours * 60 + mins;
                            };
                            let startTimeMismatch = false;
                            let endTimeMismatch = false;
                            let inPersonNotInBlock = false;
                            let virtualNotInBlock = false;
                            let mixedNotInBlock = false;
                            let inPersonDurationExceeds = false;
                            let virtualDurationExceeds = false;
                            let mixedDurationExceeds = false;
                            const refInPersonForValidation =
                                useBlockForValidation
                                    ? getDurationMinutesFromBlockCopy(
                                          effectiveBlockForValidation,
                                          MeetingFormat.InPerson
                                      )
                                    : 0;
                            const refVirtualForValidation =
                                useBlockForValidation
                                    ? getDurationMinutesFromBlockCopy(
                                          effectiveBlockForValidation,
                                          MeetingFormat.Virtual
                                      )
                                    : 0;
                            const refMixedForValidation = useBlockForValidation
                                ? getDurationMinutesFromBlockCopy(
                                      effectiveBlockForValidation,
                                      MeetingFormat.Mixed
                                  )
                                : 0;
                            if (
                                useBlockForValidation &&
                                effectiveBlockForValidation
                            ) {
                                const blockStartRaw =
                                    (effectiveBlockForValidation as any)
                                        .dateStart ??
                                    (effectiveBlockForValidation as any)
                                        .DateStart;
                                const blockEndRaw =
                                    (effectiveBlockForValidation as any)
                                        .dateEnd ??
                                    (effectiveBlockForValidation as any)
                                        .DateEnd;
                                const blockStart = blockStartRaw
                                    ? new Date(blockStartRaw)
                                    : new Date(0);
                                const blockEnd = blockEndRaw
                                    ? new Date(blockEndRaw)
                                    : new Date(0);
                                const blockStartMinutes =
                                    blockStart.getHours() * 60 +
                                    blockStart.getMinutes();
                                const blockEndMinutes =
                                    blockEnd.getHours() * 60 +
                                    blockEnd.getMinutes();
                                const formStartMinutes = timeStrToMinutes(
                                    values.startTime || ''
                                );
                                const formEndMinutes = timeStrToMinutes(
                                    values.endTime || ''
                                );
                                startTimeMismatch =
                                    formStartMinutes > 0 &&
                                    formStartMinutes < blockStartMinutes;
                                endTimeMismatch =
                                    formEndMinutes > 0 &&
                                    formEndMinutes > blockEndMinutes;
                                inPersonNotInBlock =
                                    refInPersonForValidation <= 0;
                                virtualNotInBlock =
                                    refVirtualForValidation <= 0;
                                mixedNotInBlock = refMixedForValidation <= 0;
                                const formDurationMinutes = Math.max(
                                    0,
                                    formEndMinutes - formStartMinutes
                                );
                                inPersonDurationExceeds = !!(
                                    values.isInPersonMeeting &&
                                    refInPersonForValidation > 0 &&
                                    formDurationMinutes >
                                        refInPersonForValidation
                                );
                                virtualDurationExceeds = !!(
                                    values.isVirtualMeeting &&
                                    refVirtualForValidation > 0 &&
                                    formDurationMinutes >
                                        refVirtualForValidation
                                );
                                mixedDurationExceeds = !!(
                                    values.isMixedMeeting &&
                                    refMixedForValidation > 0 &&
                                    formDurationMinutes > refMixedForValidation
                                );
                            }

                            // When form time range does not touch/intersect any availability block, treat as no mutual terms (all orange)
                            let formRangeIntersectsNoBlock = false;
                            if (
                                isBuyerInAgreeOnMeetingTimesPhase &&
                                props.availabilityBlocksForLookup?.length &&
                                props.selectedDate &&
                                values.startTime?.trim() &&
                                values.endTime?.trim()
                            ) {
                                const partsStart = (values.startTime || '')
                                    .trim()
                                    .split(':');
                                const partsEnd = (values.endTime || '')
                                    .trim()
                                    .split(':');
                                const sh =
                                    parseInt(partsStart[0] || '0', 10) || 0;
                                const sm =
                                    parseInt(
                                        partsStart[1]?.replace(/\D/g, '') ||
                                            '0',
                                        10
                                    ) || 0;
                                const eh =
                                    parseInt(partsEnd[0] || '0', 10) || 0;
                                const em =
                                    parseInt(
                                        partsEnd[1]?.replace(/\D/g, '') || '0',
                                        10
                                    ) || 0;
                                const formStart = new Date(props.selectedDate);
                                formStart.setHours(sh, sm, 0, 0);
                                const formEnd = new Date(props.selectedDate);
                                formEnd.setHours(eh, em, 0, 0);
                                if (formStart.getTime() < formEnd.getTime()) {
                                    const formStartT = formStart.getTime();
                                    const formEndT = formEnd.getTime();
                                    const intersectsAny =
                                        props.availabilityBlocksForLookup.some(
                                            (block) => {
                                                const bStart = new Date(
                                                    (block as any).dateStart ??
                                                        (block as any)
                                                            .DateStart ??
                                                        0
                                                ).getTime();
                                                const bEnd = new Date(
                                                    (block as any).dateEnd ??
                                                        (block as any)
                                                            .DateEnd ??
                                                        0
                                                ).getTime();
                                                return (
                                                    formStartT <= bEnd &&
                                                    formEndT >= bStart
                                                );
                                            }
                                        );
                                    formRangeIntersectsNoBlock = !intersectsAny;
                                }
                            }

                            // When user selected empty slot/day (no block), or form range doesn't touch any block, highlight all terms in orange
                            const noBlockSelected =
                                isBuyerInAgreeOnMeetingTimesPhase &&
                                (!blockForComparison ||
                                    formRangeIntersectsNoBlock);
                            let startTimeMismatchFinal =
                                startTimeMismatch || noBlockSelected;
                            let endTimeMismatchFinal =
                                endTimeMismatch || noBlockSelected;
                            if (props.mode === 'buyerTopLevel') {
                                startTimeMismatchFinal = false;
                                endTimeMismatchFinal = false;
                            }
                            // In view/readonly mode: no orange (mismatch) highlighting
                            if (effectiveReadonly && !isEditMode) {
                                startTimeMismatchFinal = false;
                                endTimeMismatchFinal = false;
                            }
                            if (props.viewMode === 'availability') {
                                startTimeMismatchFinal = false;
                                endTimeMismatchFinal = false;
                            }

                            // Prepare meeting types for MeetingTypeSelector
                            const inPersonCheck = checkDurationExceedsMax(
                                values.maxInPersonMeetingDuration || 0
                            );
                            const virtualCheck = checkDurationExceedsMax(
                                values.maxVirtualMeetingDuration || 0
                            );
                            const mixedCheck = checkDurationExceedsMax(
                                values.maxMixedMeetingDuration || 0
                            );

                            const noMismatchForBuyerTopLevel =
                                props.mode === 'buyerTopLevel';
                            const noMismatchForSellerAvailability =
                                props.viewMode === 'availability';
                            const meetingTypes: MeetingType[] = [
                                {
                                    type: 'inPerson',
                                    label: 'In-Person',
                                    checked: values.isInPersonMeeting,
                                    maxDuration: minutesToHoursMinutes(
                                        (useBlockForDisplay
                                            ? refInPersonMinutes
                                            : (values.maxInPersonMeetingDuration ??
                                              0)) ?? 0
                                    ),
                                    error:
                                        inPersonCheck.error ||
                                        !!(
                                            touched.maxInPersonMeetingDuration &&
                                            errors.maxInPersonMeetingDuration
                                        ),
                                    errorMessage: undefined,
                                    mismatchTypeNotInBlock:
                                        noMismatchForBuyerTopLevel ||
                                        noMismatchForSellerAvailability
                                            ? false
                                            : noBlockSelected ||
                                              (useBlockForValidation &&
                                                  inPersonNotInBlock &&
                                                  !!values.isInPersonMeeting),
                                    mismatchDurationExceedsMax:
                                        noMismatchForBuyerTopLevel ||
                                        noMismatchForSellerAvailability
                                            ? false
                                            : inPersonDurationExceeds,
                                },
                                {
                                    type: 'virtual',
                                    label: 'Virtual',
                                    checked: values.isVirtualMeeting,
                                    maxDuration: minutesToHoursMinutes(
                                        (useBlockForDisplay
                                            ? refVirtualMinutes
                                            : (values.maxVirtualMeetingDuration ??
                                              0)) ?? 0
                                    ),
                                    error:
                                        virtualCheck.error ||
                                        !!(
                                            touched.maxVirtualMeetingDuration &&
                                            errors.maxVirtualMeetingDuration
                                        ),
                                    errorMessage: undefined,
                                    mismatchTypeNotInBlock:
                                        noMismatchForBuyerTopLevel ||
                                        noMismatchForSellerAvailability
                                            ? false
                                            : noBlockSelected ||
                                              (useBlockForValidation &&
                                                  virtualNotInBlock &&
                                                  !!values.isVirtualMeeting),
                                    mismatchDurationExceedsMax:
                                        noMismatchForBuyerTopLevel ||
                                        noMismatchForSellerAvailability
                                            ? false
                                            : virtualDurationExceeds,
                                },
                                {
                                    type: 'combo',
                                    label: 'In Person / Virtual Combo',
                                    checked: values.isMixedMeeting,
                                    maxDuration: minutesToHoursMinutes(
                                        (useBlockForDisplay
                                            ? refMixedMinutes
                                            : (values.maxMixedMeetingDuration ??
                                              0)) ?? 0
                                    ),
                                    error:
                                        mixedCheck.error ||
                                        !!(
                                            touched.maxMixedMeetingDuration &&
                                            errors.maxMixedMeetingDuration
                                        ),
                                    errorMessage: undefined,
                                    mismatchTypeNotInBlock:
                                        noMismatchForBuyerTopLevel ||
                                        noMismatchForSellerAvailability
                                            ? false
                                            : noBlockSelected ||
                                              (useBlockForValidation &&
                                                  mixedNotInBlock &&
                                                  !!values.isMixedMeeting),
                                    mismatchDurationExceedsMax:
                                        noMismatchForBuyerTopLevel ||
                                        noMismatchForSellerAvailability
                                            ? false
                                            : mixedDurationExceeds,
                                },
                            ];

                            // Terms match when we have a block, no time/duration/type mismatch for the selected meeting type
                            const termsMatch =
                                useBlockForValidation &&
                                !startTimeMismatch &&
                                !endTimeMismatch &&
                                ((values.isInPersonMeeting &&
                                    !inPersonNotInBlock &&
                                    !inPersonDurationExceeds) ||
                                    (values.isVirtualMeeting &&
                                        !virtualNotInBlock &&
                                        !virtualDurationExceeds) ||
                                    (values.isMixedMeeting &&
                                        !mixedNotInBlock &&
                                        !mixedDurationExceeds));

                            const handleScheduleMeeting = async () => {
                                const hasMeetingType =
                                    values.isInPersonMeeting ||
                                    values.isVirtualMeeting ||
                                    values.isMixedMeeting;
                                if (!hasMeetingType) {
                                    setMeetingTypeError(true);
                                    createNotification(
                                        'Please select a meeting type',
                                        'error'
                                    );
                                    return;
                                }
                                setMeetingTypeError(false);
                                setBuyerSelectionError(false);
                                const engagementId =
                                    props.engagementId ?? currentEngagement?.id;
                                if (!engagementId) {
                                    createNotification(
                                        'No engagement selected',
                                        'error'
                                    );
                                    return;
                                }
                                const meetingDate = props.selectedDate;
                                if (!meetingDate) {
                                    createNotification(
                                        'Please select a date',
                                        'error'
                                    );
                                    return;
                                }
                                const [startHours, startMins] = (
                                    values.startTime || '0:0'
                                )
                                    .split(':')
                                    .map(Number);
                                const [endHours, endMins] = (
                                    values.endTime || '0:0'
                                )
                                    .split(':')
                                    .map(Number);
                                const dateStart = new Date(meetingDate);
                                dateStart.setHours(
                                    startHours || 0,
                                    startMins || 0,
                                    0,
                                    0
                                );
                                const dateEnd = new Date(meetingDate);
                                dateEnd.setHours(
                                    endHours || 0,
                                    endMins || 0,
                                    0,
                                    0
                                );
                                let format = MeetingFormat.Virtual;
                                if (values.isInPersonMeeting)
                                    format = MeetingFormat.InPerson;
                                else if (values.isVirtualMeeting)
                                    format = MeetingFormat.Virtual;
                                else if (values.isMixedMeeting)
                                    format = MeetingFormat.Mixed;
                                const confirmedAddress =
                                    format === MeetingFormat.InPerson ||
                                    format === MeetingFormat.Mixed
                                        ? {
                                              City: values.city || '',
                                              State: values.state || '',
                                              Address:
                                                  values.address ?? undefined,
                                          }
                                        : undefined;
                                const buyerIdForCreate =
                                    values.selectedBuyerIds?.[0] != null
                                        ? Number(values.selectedBuyerIds[0])
                                        : undefined;
                                let cancellationDeadlineDays: number | null =
                                    null;
                                let cancellationDeadlineHours: number | null =
                                    null;
                                if (isCancellationFeesEnabled) {
                                    if (party === TimelineParty.Seller) {
                                        if (values.hasCancellationFee) {
                                            if (
                                                format === MeetingFormat.Virtual
                                            ) {
                                                cancellationDeadlineHours =
                                                    values.cancellationDeadlineVirtualHours ||
                                                    1;
                                            } else if (
                                                format ===
                                                MeetingFormat.InPerson
                                            ) {
                                                cancellationDeadlineDays =
                                                    values.cancellationDeadlineInPersonDays ||
                                                    1;
                                            } else if (
                                                format === MeetingFormat.Mixed
                                            ) {
                                                cancellationDeadlineDays =
                                                    values.cancellationDeadlineMixedDays ||
                                                    1;
                                            }
                                        }
                                    } else if (
                                        blockWithCancellationData?.formats
                                    ) {
                                        const matchedFmt =
                                            blockWithCancellationData.formats.find(
                                                (f: any) =>
                                                    (f.format ?? f.Format) ===
                                                    format
                                            );
                                        if (matchedFmt) {
                                            const fAny = matchedFmt as any;
                                            cancellationDeadlineDays =
                                                fAny.CancellationDeadlineDays ??
                                                fAny.cancellationDeadlineDays ??
                                                null;
                                            cancellationDeadlineHours =
                                                fAny.CancellationDeadlineHours ??
                                                fAny.cancellationDeadlineHours ??
                                                null;
                                        }
                                    }
                                }
                                const body = {
                                    DateStart: dateStart.toISOString(),
                                    DateEnd: dateEnd.toISOString(),
                                    Format: format,
                                    Status: MeetingProposalStatus.PendingSellerAnswer,
                                    ConfirmedAddress: confirmedAddress ?? null,
                                    EngagementId: engagementId,
                                    Description: values.message || null,
                                    CancellationDeadlineDays:
                                        cancellationDeadlineDays,
                                    CancellationDeadlineHours:
                                        cancellationDeadlineHours,
                                    ...(buyerIdForCreate != null &&
                                    !Number.isNaN(buyerIdForCreate)
                                        ? { BuyerId: buyerIdForCreate }
                                        : {}),
                                };
                                const levelOfService =
                                    currentEngagement?.levelOfService ??
                                    selectedOpportunity?.levelOfService;
                                if (
                                    isCancellationFeesEnabled &&
                                    party === TimelineParty.Buyer &&
                                    canBuyerTeamMemberManageMeetings(summary, props.engagementId) &&
                                    levelOfService !==
                                        LevelOfService.StrategicFinancialAdvisoryService
                                ) {
                                    setPendingProposalBody(body);
                                    setIsSaveCardModalVisible(true);
                                    return;
                                }
                                setIsSubmitting(true);
                                try {
                                    const response = await axios.post(
                                        `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling`,
                                        body
                                    );
                                    if (
                                        response.status === 200 ||
                                        response.status === 201
                                    ) {
                                        createNotification(
                                            'Meeting proposed successfully',
                                            'success'
                                        );
                                        const proposalId =
                                            response.data?.id != null
                                                ? String(response.data.id)
                                                : typeof response.data ===
                                                    'string'
                                                  ? response.data
                                                  : undefined;
                                        props.onRefreshMeetings?.(
                                            undefined,
                                            proposalId
                                        );
                                        // Don't deselect: parent will select the new proposal when refresh completes and show it in readonly (Proposed Meeting view)
                                        if (!proposalId) {
                                            props.onDeselectEvent?.();
                                            props.onDeselectDate?.();
                                        }
                                    } else {
                                        createNotification(
                                            'Could not submit meeting proposal',
                                            'error'
                                        );
                                    }
                                } catch (error: any) {
                                    const raw: string =
                                        (error?.response?.data?.title).toLowerCase();
                                    createNotification(
                                        raw.includes('payment method')
                                            ? 'Buyer does not have a saved payment method.'
                                            : 'An error occurred while proposing the meeting',
                                        'error'
                                    );
                                } finally {
                                    setIsSubmitting(false);
                                }
                            };

                            const handleUpdateProposal = async () => {
                                const hasMeetingType =
                                    values.isInPersonMeeting ||
                                    values.isVirtualMeeting ||
                                    values.isMixedMeeting;
                                if (!hasMeetingType) {
                                    setMeetingTypeError(true);
                                    createNotification(
                                        'Please select a meeting type',
                                        'error'
                                    );
                                    return;
                                }
                                setMeetingTypeError(false);
                                setBuyerSelectionError(false);
                                const engagementId =
                                    props.engagementId ?? currentEngagement?.id;
                                if (!engagementId) {
                                    createNotification(
                                        'No engagement selected',
                                        'error'
                                    );
                                    return;
                                }
                                const selectedEv =
                                    props.selectedEvent as MeetingObject;
                                const meetingProposalId =
                                    selectedEv?.meetingProposalId ??
                                    selectedEv?.id;
                                if (!meetingProposalId) {
                                    createNotification(
                                        'No meeting proposal selected',
                                        'error'
                                    );
                                    return;
                                }
                                const meetingDate = props.selectedDate;
                                if (!meetingDate) {
                                    createNotification(
                                        'Please select a date',
                                        'error'
                                    );
                                    return;
                                }
                                const [startHours, startMins] = (
                                    values.startTime || '0:0'
                                )
                                    .split(':')
                                    .map(Number);
                                const [endHours, endMins] = (
                                    values.endTime || '0:0'
                                )
                                    .split(':')
                                    .map(Number);
                                const dateStart = new Date(meetingDate);
                                dateStart.setHours(
                                    startHours || 0,
                                    startMins || 0,
                                    0,
                                    0
                                );
                                const dateEnd = new Date(meetingDate);
                                dateEnd.setHours(
                                    endHours || 0,
                                    endMins || 0,
                                    0,
                                    0
                                );
                                let format = MeetingFormat.Virtual;
                                if (values.isInPersonMeeting)
                                    format = MeetingFormat.InPerson;
                                else if (values.isVirtualMeeting)
                                    format = MeetingFormat.Virtual;
                                else if (values.isMixedMeeting)
                                    format = MeetingFormat.Mixed;
                                const confirmedAddress =
                                    format === MeetingFormat.InPerson ||
                                    format === MeetingFormat.Mixed
                                        ? {
                                              City: values.city || '',
                                              State: values.state || '',
                                              Address:
                                                  values.address ?? undefined,
                                          }
                                        : undefined;
                                let buyerId =
                                    selectedEv?.posterId != null
                                        ? Number(selectedEv.posterId)
                                        : Number(values.selectedBuyerIds?.[0]);
                                if (!buyerId || Number.isNaN(buyerId)) {
                                    // When buyer is editing their own proposal, posterId/selectedBuyerIds may be unset; use current buyer
                                    if (
                                        party === TimelineParty.Buyer &&
                                        activeBuyers?.length
                                    ) {
                                        buyerId = activeBuyers[0].buyerId;
                                    }
                                }
                                if (!buyerId || Number.isNaN(buyerId)) {
                                    createNotification(
                                        'Buyer is required',
                                        'error'
                                    );
                                    return;
                                }
                                let updateCancellationDeadlineDays:
                                    | number
                                    | null = null;
                                let updateCancellationDeadlineHours:
                                    | number
                                    | null = null;
                                if (
                                    isCancellationFeesEnabled &&
                                    values.hasCancellationFee
                                ) {
                                    if (format === MeetingFormat.Virtual) {
                                        updateCancellationDeadlineHours =
                                            values.cancellationDeadlineVirtualHours ||
                                            1;
                                    } else if (
                                        format === MeetingFormat.InPerson
                                    ) {
                                        updateCancellationDeadlineDays =
                                            values.cancellationDeadlineInPersonDays ||
                                            1;
                                    } else if (format === MeetingFormat.Mixed) {
                                        updateCancellationDeadlineDays =
                                            values.cancellationDeadlineMixedDays ||
                                            1;
                                    }
                                } else {
                                    // Buyer editing proposal: cancellation fee UI is not shown, preserve the seller's confirmed values
                                    updateCancellationDeadlineDays =
                                        selectedEv?.confirmedCancellationDeadlineDays ??
                                        null;
                                    updateCancellationDeadlineHours =
                                        selectedEv?.confirmedCancellationDeadlineHours ??
                                        null;
                                }
                                const body = {
                                    Id: meetingProposalId,
                                    DateStart: dateStart.toISOString(),
                                    DateEnd: dateEnd.toISOString(),
                                    Format: format,
                                    BuyerId: buyerId,
                                    Status:
                                        selectedEv?.proposalStatus ??
                                        MeetingProposalStatus.PendingSellerAnswer,
                                    ConfirmedAddress: confirmedAddress ?? null,
                                    Description: values.message || null,
                                    CancellationDeadlineDays:
                                        updateCancellationDeadlineDays,
                                    CancellationDeadlineHours:
                                        updateCancellationDeadlineHours,
                                };
                                setIsSubmitting(true);
                                try {
                                    const response = await axios.put(
                                        `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling/${meetingProposalId}`,
                                        body
                                    );
                                    if (
                                        response.status === 200 ||
                                        response.status === 204
                                    ) {
                                        createNotification(
                                            'Meeting proposal updated successfully',
                                            'success'
                                        );
                                        props.onRefreshMeetings?.(
                                            undefined,
                                            String(meetingProposalId)
                                        );
                                        setIsEditMode(false);
                                        props.onEditModeChange?.(false);
                                    } else {
                                        createNotification(
                                            'Could not update meeting proposal',
                                            'error'
                                        );
                                    }
                                } catch (error: any) {
                                    const raw: string = (
                                        error?.response?.data?.title ??
                                        error?.response?.data?.Message ??
                                        error?.response?.data?.message ??
                                        ''
                                    ).toLowerCase();
                                    createNotification(
                                        raw.includes('payment method')
                                            ? 'Buyer does not have a saved payment method.'
                                            : 'An error occurred while updating the meeting proposal',
                                        'error'
                                    );
                                } finally {
                                    setIsSubmitting(false);
                                }
                            };

                            // When seller is editing a proposal: show only the proposer (disabled), single meeting type
                            const isSellerEditingProposal =
                                props.mode === 'engagement' &&
                                party === TimelineParty.Seller &&
                                !!props.selectedEvent &&
                                !isAvailabilityBlockObject(
                                    props.selectedEvent
                                ) &&
                                (props.selectedEvent as MeetingObject)
                                    .isMeetingProposal === true;

                            // Seller "Propose New Meeting" from existing meeting: form prefilled, buyer readonly (single buyer from prefill)
                            const isSellerProposingAlternative =
                                props.mode === 'engagement' &&
                                party === TimelineParty.Seller &&
                                !!props.meetingPrefillData &&
                                props.meetingPrefillData.posterId != null &&
                                !props.selectedEvent;

                            // Prepare buyers for BuyersSelector (proposer = buyerId from proposal when available, else posterId; name from options or posterName)
                            const selectedEventAsCalendar =
                                props.selectedEvent as CalendarObject & {
                                    buyerId?: number;
                                };
                            const proposerIdFromEvent =
                                isSellerEditingProposal &&
                                (selectedEventAsCalendar?.buyerId ??
                                    selectedEventAsCalendar?.posterId) != null
                                    ? (selectedEventAsCalendar.buyerId ??
                                      selectedEventAsCalendar.posterId)
                                    : null;
                            const proposerIdFromPrefill =
                                isSellerProposingAlternative &&
                                props.meetingPrefillData?.posterId != null
                                    ? props.meetingPrefillData.posterId
                                    : null;
                            const proposerId =
                                proposerIdFromEvent ??
                                proposerIdFromPrefill ??
                                null;
                            const proposerName =
                                selectedEventAsCalendar?.posterName ??
                                props.meetingPrefillData?.posterName ??
                                '';
                            const proposerFromOptions =
                                proposerId != null
                                    ? buyerOptions.find(
                                          (b) =>
                                              Number(b.id) ===
                                              Number(proposerId)
                                      )
                                    : null;

                            const buyers: Buyer[] =
                                (isSellerEditingProposal ||
                                    isSellerProposingAlternative) &&
                                proposerId != null
                                    ? [
                                          {
                                              id: proposerId,
                                              name:
                                                  (proposerFromOptions?.name ??
                                                      proposerName) ||
                                                  `Buyer ${proposerId}`,
                                              checked: true,
                                          },
                                      ]
                                    : buyerOptions.map((buyer) => ({
                                          id: buyer.id,
                                          name: buyer.name,
                                          checked:
                                              values.selectedBuyerIds?.includes(
                                                  buyer.id
                                              ) || false,
                                      }));

                            const handleMeetingTypeChange = async (
                                type: 'inPerson' | 'virtual' | 'combo',
                                field: 'checked' | 'maxDuration',
                                value: boolean | string
                            ) => {
                                if (field === 'checked') {
                                    if (type === 'inPerson') {
                                        await setFieldValue(
                                            'isInPersonMeeting',
                                            value,
                                            true
                                        );
                                        setFieldTouched(
                                            'maxInPersonMeetingDuration',
                                            true,
                                            false
                                        );
                                        // Auto-set duration to max allowed when enabling (skip for buyer in AgreeOnMeetingTimes - use block values only)
                                        if (
                                            !isBuyerInAgreeOnMeetingTimesPhase &&
                                            value === true &&
                                            maxAllowedMinutes > 0
                                        ) {
                                            await setFieldValue(
                                                'maxInPersonMeetingDuration',
                                                maxAllowedMinutes,
                                                true
                                            );
                                        } else if (
                                            isBuyerInAgreeOnMeetingTimesPhase &&
                                            value === true &&
                                            props.availabilityBlockFormCopy
                                        ) {
                                            const mins =
                                                getDurationMinutesFromBlockCopy(
                                                    props.availabilityBlockFormCopy,
                                                    MeetingFormat.InPerson
                                                );
                                            await setFieldValue(
                                                'maxInPersonMeetingDuration',
                                                mins,
                                                true
                                            );
                                        }
                                    } else if (type === 'virtual') {
                                        await setFieldValue(
                                            'isVirtualMeeting',
                                            value,
                                            true
                                        );
                                        setFieldTouched(
                                            'maxVirtualMeetingDuration',
                                            true,
                                            false
                                        );
                                        // Auto-set duration to max allowed when enabling (skip for buyer in AgreeOnMeetingTimes - use block values only)
                                        if (
                                            !isBuyerInAgreeOnMeetingTimesPhase &&
                                            value === true &&
                                            maxAllowedMinutes > 0
                                        ) {
                                            await setFieldValue(
                                                'maxVirtualMeetingDuration',
                                                maxAllowedMinutes,
                                                true
                                            );
                                        } else if (
                                            isBuyerInAgreeOnMeetingTimesPhase &&
                                            value === true &&
                                            props.availabilityBlockFormCopy
                                        ) {
                                            const mins =
                                                getDurationMinutesFromBlockCopy(
                                                    props.availabilityBlockFormCopy,
                                                    MeetingFormat.Virtual
                                                );
                                            await setFieldValue(
                                                'maxVirtualMeetingDuration',
                                                mins,
                                                true
                                            );
                                        }
                                    } else if (type === 'combo') {
                                        await setFieldValue(
                                            'isMixedMeeting',
                                            value,
                                            true
                                        );
                                        setFieldTouched(
                                            'maxMixedMeetingDuration',
                                            true,
                                            false
                                        );
                                        // Auto-set duration to max allowed when enabling (skip for buyer in AgreeOnMeetingTimes - use block values only)
                                        if (
                                            !isBuyerInAgreeOnMeetingTimesPhase &&
                                            value === true &&
                                            maxAllowedMinutes > 0
                                        ) {
                                            await setFieldValue(
                                                'maxMixedMeetingDuration',
                                                maxAllowedMinutes,
                                                true
                                            );
                                        } else if (
                                            isBuyerInAgreeOnMeetingTimesPhase &&
                                            value === true &&
                                            props.availabilityBlockFormCopy
                                        ) {
                                            const mins =
                                                getDurationMinutesFromBlockCopy(
                                                    props.availabilityBlockFormCopy,
                                                    MeetingFormat.Mixed
                                                );
                                            await setFieldValue(
                                                'maxMixedMeetingDuration',
                                                mins,
                                                true
                                            );
                                        }
                                    }

                                    // Validate that at least one meeting type is selected
                                    // Get the current state after the change
                                    const isInPerson =
                                        type === 'inPerson'
                                            ? value
                                            : values.isInPersonMeeting;
                                    const isVirtual =
                                        type === 'virtual'
                                            ? value
                                            : values.isVirtualMeeting;
                                    const isMixed =
                                        type === 'combo'
                                            ? value
                                            : values.isMixedMeeting;

                                    const hasAtLeastOneType =
                                        isInPerson || isVirtual || isMixed;
                                    setMeetingTypeError(!hasAtLeastOneType);
                                } else if (field === 'maxDuration') {
                                    const minutes = hoursMinutesToMinutes(
                                        value as string
                                    );
                                    if (type === 'inPerson') {
                                        await setFieldValue(
                                            'maxInPersonMeetingDuration',
                                            minutes,
                                            true
                                        );
                                        setFieldTouched(
                                            'maxInPersonMeetingDuration',
                                            true,
                                            false
                                        );
                                    } else if (type === 'virtual') {
                                        await setFieldValue(
                                            'maxVirtualMeetingDuration',
                                            minutes,
                                            true
                                        );
                                        setFieldTouched(
                                            'maxVirtualMeetingDuration',
                                            true,
                                            false
                                        );
                                    } else if (type === 'combo') {
                                        await setFieldValue(
                                            'maxMixedMeetingDuration',
                                            minutes,
                                            true
                                        );
                                        setFieldTouched(
                                            'maxMixedMeetingDuration',
                                            true,
                                            false
                                        );
                                    }
                                }
                                setTimeout(() => validateForm(), 0);
                            };

                            const handleBuyerChange = (
                                buyerId: string | number,
                                checked: boolean
                            ) => {
                                const currentIds =
                                    values.selectedBuyerIds || [];
                                let newIds: any[];

                                if (checked) {
                                    newIds = [...currentIds, buyerId];
                                } else {
                                    newIds = currentIds.filter(
                                        (id: any) => id !== buyerId
                                    );
                                }

                                setFieldValue('selectedBuyerIds', newIds);

                                // Update error state based on selection
                                if (newIds.length > 0) {
                                    setBuyerSelectionError(false);
                                } else {
                                    setBuyerSelectionError(true);
                                }

                                // Check if all buyers are now selected
                                if (newIds.length === buyerOptions.length) {
                                    setFieldValue('isSelectAllBuyers', true);
                                } else {
                                    setFieldValue('isSelectAllBuyers', false);
                                }
                            };

                            const handleSelectAllChange = (
                                checked: boolean
                            ) => {
                                setFieldValue('isSelectAllBuyers', checked);
                                if (checked) {
                                    // Select all buyers
                                    setFieldValue(
                                        'selectedBuyerIds',
                                        buyerOptions.map((b) => b.id)
                                    );
                                    // Clear error
                                    setBuyerSelectionError(false);
                                } else {
                                    // Deselect all buyers
                                    setFieldValue('selectedBuyerIds', []);
                                    // Set error since no buyers are selected
                                    setBuyerSelectionError(true);
                                }
                            };

                            const handleBuyerNameClick = (
                                buyerId: string | number
                            ) => {
                                setSelectedBuyerId(Number(buyerId));
                                setIsBuyerProfilePopupVisible(true);
                            };

                            const handleTimeChange = async (
                                name: string,
                                value: string
                            ) => {
                                await setFieldValue(name, value, true);
                                setFieldTouched(name, true, false);
                                setFormDateTimeVersion((v) => v + 1);

                                // Also touch the other field to trigger cross-field validation
                                if (name === 'startTime') {
                                    setFieldTouched('endTime', true, false);
                                    const newStartDate = timeStringToDate(
                                        value,
                                        startDate
                                    );
                                    setStartDate(newStartDate);
                                    handleStartDateChange(newStartDate);
                                } else if (name === 'endTime') {
                                    setFieldTouched('startTime', true, false);
                                    const newEndDate = timeStringToDate(
                                        value,
                                        endDate
                                    );
                                    setEndDate(newEndDate);
                                    handleEndDateChange(newEndDate);
                                }

                                // Touch duration fields to revalidate them against new time range
                                setFieldTouched(
                                    'maxInPersonMeetingDuration',
                                    true,
                                    false
                                );
                                setFieldTouched(
                                    'maxVirtualMeetingDuration',
                                    true,
                                    false
                                );
                                setFieldTouched(
                                    'maxMixedMeetingDuration',
                                    true,
                                    false
                                );

                                // Trigger validation after all updates
                                setTimeout(() => validateForm(), 0);
                            };

                            return (
                                <>
                                    {/* Selected Date Display */}
                                    {props.selectedDate && (
                                        <Row className="selected-date-display">
                                            <Col md={12}>
                                                <div className="selected-date-value">
                                                    {props.selectedDate.toLocaleDateString(
                                                        'en-US',
                                                        {
                                                            weekday: 'long',
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                        }
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {overlapWarning &&
                                        props.viewMode !== 'availability' && (
                                            <Row className="overlap-warning-row">
                                                <Col md={12}>
                                                    <div className="overlap-warning-message">
                                                        {overlapWarning}
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}

                                    {/* Simplified Time Pickers */}
                                    <Row className="time-pickers-new">
                                        <Col md={6}>
                                            <TimePickerField
                                                label={
                                                    props.viewMode ===
                                                    'availability'
                                                        ? 'Start Time'
                                                        : 'Meeting Start'
                                                }
                                                name="startTime"
                                                value={values.startTime}
                                                onChange={handleTimeChange}
                                                disabled={
                                                    props.disabled ||
                                                    isGhostMeeting ||
                                                    (effectiveReadonly &&
                                                        !isEditMode) ||
                                                    (!canCreateOrEditAvailability &&
                                                        props.viewMode ===
                                                            'availability' &&
                                                        !isEditMode &&
                                                        !isSellerProposingAlternativeForm)
                                                }
                                                error={
                                                    !!(
                                                        touched.startTime &&
                                                        errors.startTime
                                                    )
                                                }
                                                errorMessage={
                                                    touched.startTime
                                                        ? (errors.startTime as string)
                                                        : undefined
                                                }
                                                mismatchWarning={
                                                    effectiveReadonly &&
                                                    !isEditMode
                                                        ? false
                                                        : startTimeMismatchFinal
                                                }
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <TimePickerField
                                                label={
                                                    props.viewMode ===
                                                    'availability'
                                                        ? 'End Time'
                                                        : 'Meeting End'
                                                }
                                                name="endTime"
                                                value={values.endTime}
                                                onChange={handleTimeChange}
                                                disabled={
                                                    props.disabled ||
                                                    isGhostMeeting ||
                                                    (effectiveReadonly &&
                                                        !isEditMode) ||
                                                    (!canCreateOrEditAvailability &&
                                                        props.viewMode ===
                                                            'availability' &&
                                                        !isEditMode &&
                                                        !isSellerProposingAlternativeForm)
                                                }
                                                error={
                                                    !!(
                                                        touched.endTime &&
                                                        errors.endTime
                                                    )
                                                }
                                                mismatchWarning={
                                                    effectiveReadonly &&
                                                    !isEditMode
                                                        ? false
                                                        : endTimeMismatchFinal
                                                }
                                                errorMessage={
                                                    touched.endTime
                                                        ? (errors.endTime as string)
                                                        : undefined
                                                }
                                            />
                                        </Col>
                                    </Row>

                                    {/* KEEPING OLD TIME PICKERS FOR REFERENCE - DELETE LATER */}
                                    <Row
                                        className="start-end-fields-container"
                                        style={{ display: 'none' }}>
                                        <Row className="time-row">
                                            <Col
                                                md={
                                                    3 +
                                                    Number(!!effectiveReadonly)
                                                }
                                                className="time-label">
                                                {props.viewMode ===
                                                'availability'
                                                    ? 'Start Time'
                                                    : 'Meeting Start'}
                                            </Col>
                                            <Col
                                                md={
                                                    9 -
                                                    Number(!!effectiveReadonly)
                                                }
                                                className={`time-input${
                                                    effectiveReadonly
                                                        ? ' display-mode'
                                                        : ''
                                                }`}>
                                                {!!effectiveReadonly ? (
                                                    <div>
                                                        {startDate.toLocaleTimeString(
                                                            'en-US',
                                                            {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true,
                                                            }
                                                        )}
                                                    </div>
                                                ) : (
                                                    <InputGroup
                                                        disabled={
                                                            props.disabled
                                                        }
                                                        className={
                                                            'small-border input-group-field' +
                                                            (startDateTimeState.time
                                                                ? ''
                                                                : ' required-field') +
                                                            (props.disabled
                                                                ? ' disabled'
                                                                : '')
                                                        }
                                                        aria-invalid={
                                                            !startDateTimeState.time
                                                        }>
                                                        <InputGroupAddon addonType="prepend">
                                                            <div className="input-group-text">
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faCalendarAlt
                                                                    }
                                                                />
                                                            </div>
                                                        </InputGroupAddon>
                                                        <DatePicker
                                                            className="form-control"
                                                            disabled={
                                                                props.disabled
                                                            }
                                                            ref={
                                                                startDatePickerRef
                                                            }
                                                            selected={startDate}
                                                            showTimeSelectOnly
                                                            timeIntervals={15}
                                                            onChange={
                                                                handleStartDateChange
                                                            }
                                                            onCalendarClose={() => {
                                                                validateConferenceFields();
                                                            }}
                                                            dateFormat="hh:mm a"
                                                            customInput={
                                                                <DateControlInput
                                                                    onClick={() => {
                                                                        if (
                                                                            startDatePickerRef.current
                                                                        ) {
                                                                            startDatePickerRef.current.setOpen(
                                                                                true
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    </InputGroup>
                                                )}

                                                <Col
                                                    md={4}
                                                    className={`time-zone-select ${
                                                        effectiveReadonly
                                                            ? 'display-mode'
                                                            : ''
                                                    }`}>
                                                    {!!effectiveReadonly ? (
                                                        <div>
                                                            {
                                                                startTimeZone.abbreviation
                                                            }
                                                        </div>
                                                    ) : (
                                                        <select
                                                            disabled={
                                                                props.disabled
                                                            }
                                                            value={
                                                                startTimeZone.abbreviation
                                                            }
                                                            name="start-time-zone"
                                                            onChange={
                                                                handleStartTimeZoneChange
                                                            }
                                                            className={
                                                                'form-control shadow-off' +
                                                                (startDateTimeState.zone
                                                                    ? ''
                                                                    : ' required-field')
                                                            }
                                                            aria-invalid={
                                                                !startDateTimeState.zone
                                                            }>
                                                            {TimeZoneHelper.getDateZones(
                                                                startDate
                                                            ).map(
                                                                (
                                                                    dateZone: TimeZone,
                                                                    index: number
                                                                ) => (
                                                                    <option
                                                                        key={
                                                                            'start-zone' +
                                                                            index
                                                                        }
                                                                        value={
                                                                            dateZone.abbreviation
                                                                        }>
                                                                        {
                                                                            dateZone.abbreviation
                                                                        }
                                                                    </option>
                                                                )
                                                            )}
                                                        </select>
                                                    )}
                                                </Col>
                                            </Col>
                                        </Row>

                                        <Row className="time-row">
                                            <Col
                                                md={
                                                    3 +
                                                    Number(!!effectiveReadonly)
                                                }
                                                className="time-label">
                                                {props.viewMode ===
                                                'availability'
                                                    ? 'End Time'
                                                    : 'Meeting End'}
                                            </Col>
                                            <Col
                                                md={
                                                    9 -
                                                    Number(!!effectiveReadonly)
                                                }
                                                className={`time-input${
                                                    effectiveReadonly
                                                        ? ' display-mode'
                                                        : ''
                                                }`}>
                                                {!!effectiveReadonly ? (
                                                    <div className="">
                                                        {endDate.toLocaleTimeString(
                                                            'en-US',
                                                            {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true,
                                                            }
                                                        )}
                                                    </div>
                                                ) : (
                                                    <InputGroup
                                                        disabled={
                                                            props.disabled
                                                        }
                                                        className={
                                                            'small-border input-group-field' +
                                                            (endDateTimeState.time
                                                                ? ''
                                                                : ' required-field') +
                                                            (props.disabled
                                                                ? ' disabled'
                                                                : '')
                                                        }
                                                        aria-invalid={
                                                            !endDateTimeState.time
                                                        }>
                                                        <InputGroupAddon addonType="prepend">
                                                            <div className="input-group-text">
                                                                <FontAwesomeIcon
                                                                    icon={
                                                                        faCalendarAlt
                                                                    }
                                                                />
                                                            </div>
                                                        </InputGroupAddon>
                                                        <DatePicker
                                                            disabled={
                                                                props.disabled
                                                            }
                                                            ref={
                                                                endDatePickerRef
                                                            }
                                                            className="form-control"
                                                            selected={endDate}
                                                            showTimeSelectOnly
                                                            timeIntervals={15}
                                                            onChange={
                                                                handleEndDateChange
                                                            }
                                                            onCalendarClose={() => {
                                                                validateConferenceFields();
                                                            }}
                                                            dateFormat="hh:mm a"
                                                            customInput={
                                                                <DateControlInput
                                                                    onClick={() => {
                                                                        if (
                                                                            endDatePickerRef.current
                                                                        ) {
                                                                            endDatePickerRef.current.setOpen(
                                                                                true
                                                                            );
                                                                        }
                                                                    }}
                                                                />
                                                            }
                                                        />
                                                    </InputGroup>
                                                )}

                                                <Col
                                                    md={4}
                                                    className={`time-zone-select ${
                                                        effectiveReadonly
                                                            ? 'display-mode'
                                                            : ''
                                                    }`}>
                                                    {!!effectiveReadonly ? (
                                                        <div>
                                                            {
                                                                endTimeZone.abbreviation
                                                            }
                                                        </div>
                                                    ) : (
                                                        <select
                                                            disabled={
                                                                props.disabled
                                                            }
                                                            value={
                                                                endTimeZone.abbreviation
                                                            }
                                                            name="end-time-zone"
                                                            onChange={
                                                                handleEndTimeZoneChange
                                                            }
                                                            className={
                                                                'form-control shadow-off' +
                                                                (endDateTimeState.zone
                                                                    ? ''
                                                                    : ' required-field')
                                                            }
                                                            aria-invalid={
                                                                !endDateTimeState.zone
                                                            }>
                                                            {TimeZoneHelper.getDateZones(
                                                                endDate
                                                            ).map(
                                                                (
                                                                    dateZone: TimeZone,
                                                                    index: number
                                                                ) => (
                                                                    <option
                                                                        key={
                                                                            'end-zone' +
                                                                            index
                                                                        }
                                                                        value={
                                                                            dateZone.abbreviation
                                                                        }>
                                                                        {
                                                                            dateZone.abbreviation
                                                                        }
                                                                    </option>
                                                                )
                                                            )}
                                                        </select>
                                                    )}
                                                </Col>
                                            </Col>
                                        </Row>
                                        {dateErrorMessage ? (
                                            <Row>
                                                <Col md={12}>
                                                    <label className="error-message">
                                                        {dateErrorMessage}
                                                    </label>
                                                </Col>
                                            </Row>
                                        ) : (
                                            []
                                        )}
                                    </Row>

                                    {/* New Meeting Type Selector */}
                                    <MeetingTypeSelector
                                        meetingTypes={
                                            effectiveReadonly && !isEditMode
                                                ? meetingTypes.map((t) => ({
                                                      ...t,
                                                      mismatchTypeNotInBlock: false,
                                                      mismatchDurationExceedsMax: false,
                                                  }))
                                                : meetingTypes
                                        }
                                        onChange={handleMeetingTypeChange}
                                        disabled={
                                            isGhostMeeting ||
                                            // In opportunity mode with existing meeting(s), when viewing a block without "Propose New Meeting" - treat as readonly (no meeting type selection)
                                            (props.mode === 'opportunity' &&
                                                (props.buyerHasPendingProposal ||
                                                    props.hasConfirmedMeeting) &&
                                                effectiveReadonly &&
                                                isAvailabilityBlockObject(
                                                    props.selectedEvent
                                                )) ||
                                            (props.viewMode ===
                                                'availability' &&
                                                isAvailabilityBlockObject(
                                                    props.selectedEvent
                                                ) &&
                                                !isEditMode) ||
                                            (isBuyerInAgreeOnMeetingTimesPhase
                                                ? false
                                                : props.disabled ||
                                                  (!canCreateOrEditAvailability &&
                                                      props.viewMode ===
                                                          'availability' &&
                                                      !isEditMode &&
                                                      !isSellerProposingAlternativeForm))
                                        }
                                        readonly={
                                            isGhostMeeting ||
                                            (effectiveReadonly &&
                                                !isEditMode) ||
                                            isBuyerInAgreeOnMeetingTimesPhase
                                        }
                                        singleSelect={
                                            isBuyerInAgreeOnMeetingTimesPhase ||
                                            isSellerEditingProposal ||
                                            isSellerProposingAlternative
                                        }
                                        maxAllowedMinutes={maxAllowedMinutes}
                                        error={
                                            meetingTypeError
                                                ? 'error'
                                                : undefined
                                        }
                                        simplified={
                                            props.mode === 'buyerTopLevel'
                                        }
                                        hideMaximumMeetingLength={
                                            isSellerEditingProposal ||
                                            isSellerProposingAlternative
                                        }
                                    />

                                    {cancellationDeadlineDisplay && (
                                        <div className="cancellation-deadline-info">
                                            Cancellation Deadline:{' '}
                                            {cancellationDeadlineDisplay.value}{' '}
                                            {cancellationDeadlineDisplay.unit}
                                        </div>
                                    )}

                                    {/* KEEPING OLD MEETING FORMAT FOR REFERENCE - DELETE LATER */}
                                    <Row
                                        className="meeting-format-container"
                                        style={{ display: 'none' }}>
                                        <Row className="meeting-format-header">
                                            <Col
                                                md={6}
                                                className="meeting-format-label">
                                                Meeting Format
                                            </Col>
                                            {!(
                                                !!effectiveReadonly &&
                                                props.viewMode ===
                                                    'meetingBuyer' &&
                                                !isBuyerInAgreeOnMeetingTimesPhase
                                            ) && (
                                                <Col
                                                    md={6}
                                                    className="maximum-meeting-length-label">
                                                    Maximum Meeting Length
                                                </Col>
                                            )}
                                        </Row>
                                        <Row className="meeting-format-inputs">
                                            <Row className="meeting-format-row">
                                                <Col
                                                    md={5}
                                                    className="meeting-format-checkbox-item">
                                                    <Input
                                                        type={
                                                            isBuyerInAgreeOnMeetingTimesPhase
                                                                ? 'radio'
                                                                : 'checkbox'
                                                        }
                                                        name={
                                                            isBuyerInAgreeOnMeetingTimesPhase
                                                                ? 'meetingType'
                                                                : 'isVirtualMeeting'
                                                        }
                                                        checked={
                                                            values.isVirtualMeeting
                                                        }
                                                        onChange={(e) => {
                                                            if (
                                                                isBuyerInAgreeOnMeetingTimesPhase
                                                            ) {
                                                                // Radio button behavior: only one can be selected
                                                                setFieldValue(
                                                                    'isVirtualMeeting',
                                                                    true
                                                                );
                                                                setFieldValue(
                                                                    'isInPersonMeeting',
                                                                    false
                                                                );
                                                                setFieldValue(
                                                                    'isMixedMeeting',
                                                                    false
                                                                );
                                                            } else {
                                                                // Checkbox behavior
                                                                setFieldValue(
                                                                    'isVirtualMeeting',
                                                                    e.target
                                                                        .checked
                                                                );
                                                                if (
                                                                    !e.target
                                                                        .checked
                                                                ) {
                                                                    setFieldValue(
                                                                        'maxVirtualMeetingDuration',
                                                                        0
                                                                    );
                                                                }
                                                            }
                                                        }}
                                                        disabled={
                                                            !!props.disabled ||
                                                            !!effectiveReadonly
                                                        }
                                                    />
                                                    <Label className="meeting-format-checkbox-label">
                                                        Virtual
                                                    </Label>
                                                </Col>

                                                {!(
                                                    !!effectiveReadonly &&
                                                    props.viewMode ===
                                                        'meetingBuyer' &&
                                                    !isBuyerInAgreeOnMeetingTimesPhase
                                                ) && (
                                                    <Col
                                                        md={6}
                                                        className="maximum-meeting-length-input-item">
                                                        {/* For buyers during AgreeOnMeetingTimes phase, ALWAYS show as text (read-only) */}
                                                        {isBuyerInAgreeOnMeetingTimesPhase ? (
                                                            <div>
                                                                {!values.maxVirtualMeetingDuration
                                                                    ? 'N/A'
                                                                    : minutesToHoursMinutes(
                                                                          values.maxVirtualMeetingDuration ||
                                                                              0
                                                                      )}
                                                            </div>
                                                        ) : !!effectiveReadonly ? (
                                                            <div>
                                                                {!values.maxVirtualMeetingDuration
                                                                    ? 'N/A'
                                                                    : minutesToHoursMinutes(
                                                                          values.maxVirtualMeetingDuration ||
                                                                              0
                                                                      )}
                                                            </div>
                                                        ) : (
                                                            <Input
                                                                type="text"
                                                                name="maxVirtualMeetingDuration"
                                                                value={minutesToHoursMinutes(
                                                                    values.maxVirtualMeetingDuration ||
                                                                        0
                                                                )}
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    const minutes =
                                                                        hoursMinutesToMinutes(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        );
                                                                    setFieldValue(
                                                                        'maxVirtualMeetingDuration',
                                                                        minutes
                                                                    );
                                                                }}
                                                                disabled={
                                                                    props.disabled ||
                                                                    !values.isVirtualMeeting
                                                                }
                                                                placeholder="0:00"
                                                                className="form-control"
                                                                pattern="[0-9]+:[0-5][0-9]"
                                                            />
                                                        )}
                                                    </Col>
                                                )}
                                            </Row>
                                            <Row className="meeting-format-row">
                                                <Col
                                                    md={5}
                                                    className="meeting-format-checkbox-item">
                                                    <Input
                                                        type={
                                                            isBuyerInAgreeOnMeetingTimesPhase
                                                                ? 'radio'
                                                                : 'checkbox'
                                                        }
                                                        name={
                                                            isBuyerInAgreeOnMeetingTimesPhase
                                                                ? 'meetingType'
                                                                : 'isInPersonMeeting'
                                                        }
                                                        checked={
                                                            values.isInPersonMeeting
                                                        }
                                                        onChange={(e) => {
                                                            if (
                                                                isBuyerInAgreeOnMeetingTimesPhase
                                                            ) {
                                                                // Radio button behavior: only one can be selected
                                                                setFieldValue(
                                                                    'isVirtualMeeting',
                                                                    false
                                                                );
                                                                setFieldValue(
                                                                    'isInPersonMeeting',
                                                                    true
                                                                );
                                                                setFieldValue(
                                                                    'isMixedMeeting',
                                                                    false
                                                                );
                                                            } else {
                                                                // Checkbox behavior
                                                                setFieldValue(
                                                                    'isInPersonMeeting',
                                                                    e.target
                                                                        .checked
                                                                );
                                                                if (
                                                                    !e.target
                                                                        .checked
                                                                ) {
                                                                    setFieldValue(
                                                                        'maxInPersonMeetingDuration',
                                                                        0
                                                                    );
                                                                }
                                                            }
                                                        }}
                                                        disabled={
                                                            !!props.disabled ||
                                                            !!effectiveReadonly
                                                        }
                                                    />
                                                    <Label className="meeting-format-checkbox-label">
                                                        In-Person
                                                    </Label>
                                                </Col>
                                                {!(
                                                    !!effectiveReadonly &&
                                                    props.viewMode ===
                                                        'meetingBuyer' &&
                                                    !isBuyerInAgreeOnMeetingTimesPhase
                                                ) && (
                                                    <Col
                                                        md={6}
                                                        className="maximum-meeting-length-input-item">
                                                        {/* For buyers during AgreeOnMeetingTimes phase, ALWAYS show as text (read-only) */}
                                                        {isBuyerInAgreeOnMeetingTimesPhase ? (
                                                            <div>
                                                                {!values.maxInPersonMeetingDuration
                                                                    ? 'N/A'
                                                                    : minutesToHoursMinutes(
                                                                          values.maxInPersonMeetingDuration ||
                                                                              0
                                                                      )}
                                                            </div>
                                                        ) : !!effectiveReadonly ? (
                                                            <div>
                                                                {!values.maxInPersonMeetingDuration
                                                                    ? 'N/A'
                                                                    : minutesToHoursMinutes(
                                                                          values.maxInPersonMeetingDuration ||
                                                                              0
                                                                      )}
                                                            </div>
                                                        ) : (
                                                            <Input
                                                                type="text"
                                                                name="maxInPersonMeetingDuration"
                                                                value={minutesToHoursMinutes(
                                                                    values.maxInPersonMeetingDuration ||
                                                                        0
                                                                )}
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    const minutes =
                                                                        hoursMinutesToMinutes(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        );
                                                                    setFieldValue(
                                                                        'maxInPersonMeetingDuration',
                                                                        minutes
                                                                    );
                                                                }}
                                                                disabled={
                                                                    !!props.disabled ||
                                                                    !values.isInPersonMeeting
                                                                }
                                                                placeholder="0:00"
                                                                className="form-control"
                                                                pattern="[0-9]+:[0-5][0-9]"
                                                            />
                                                        )}
                                                    </Col>
                                                )}
                                            </Row>
                                            <Row className="meeting-format-row">
                                                <Col
                                                    md={5}
                                                    className="meeting-format-checkbox-item">
                                                    <Input
                                                        type={
                                                            isBuyerInAgreeOnMeetingTimesPhase
                                                                ? 'radio'
                                                                : 'checkbox'
                                                        }
                                                        name={
                                                            isBuyerInAgreeOnMeetingTimesPhase
                                                                ? 'meetingType'
                                                                : 'isMixedMeeting'
                                                        }
                                                        checked={
                                                            values.isMixedMeeting
                                                        }
                                                        onChange={(e) => {
                                                            if (
                                                                isBuyerInAgreeOnMeetingTimesPhase
                                                            ) {
                                                                // Radio button behavior: only one can be selected
                                                                setFieldValue(
                                                                    'isVirtualMeeting',
                                                                    false
                                                                );
                                                                setFieldValue(
                                                                    'isInPersonMeeting',
                                                                    false
                                                                );
                                                                setFieldValue(
                                                                    'isMixedMeeting',
                                                                    true
                                                                );
                                                            } else {
                                                                // Checkbox behavior
                                                                setFieldValue(
                                                                    'isMixedMeeting',
                                                                    e.target
                                                                        .checked
                                                                );
                                                                if (
                                                                    !e.target
                                                                        .checked
                                                                ) {
                                                                    setFieldValue(
                                                                        'maxMixedMeetingDuration',
                                                                        0
                                                                    );
                                                                }
                                                            }
                                                        }}
                                                        disabled={
                                                            props.disabled ||
                                                            effectiveReadonly
                                                        }
                                                    />
                                                    <Label className="meeting-format-checkbox-label">
                                                        Mixed
                                                    </Label>
                                                </Col>
                                                {!(
                                                    !!effectiveReadonly &&
                                                    props.viewMode ===
                                                        'meetingBuyer' &&
                                                    !isBuyerInAgreeOnMeetingTimesPhase
                                                ) && (
                                                    <Col
                                                        md={6}
                                                        className="maximum-meeting-length-input-item">
                                                        {/* For buyers during AgreeOnMeetingTimes phase, ALWAYS show as text (read-only) */}
                                                        {isBuyerInAgreeOnMeetingTimesPhase ? (
                                                            <div>
                                                                {!values.maxMixedMeetingDuration
                                                                    ? 'N/A'
                                                                    : minutesToHoursMinutes(
                                                                          values.maxMixedMeetingDuration ||
                                                                              0
                                                                      )}
                                                            </div>
                                                        ) : !!effectiveReadonly ? (
                                                            <div>
                                                                {!values.maxMixedMeetingDuration
                                                                    ? 'N/A'
                                                                    : minutesToHoursMinutes(
                                                                          values.maxMixedMeetingDuration ||
                                                                              0
                                                                      )}
                                                            </div>
                                                        ) : (
                                                            <Input
                                                                type="text"
                                                                name="maxMixedMeetingDuration"
                                                                value={minutesToHoursMinutes(
                                                                    values.maxMixedMeetingDuration ||
                                                                        0
                                                                )}
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    const minutes =
                                                                        hoursMinutesToMinutes(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        );
                                                                    setFieldValue(
                                                                        'maxMixedMeetingDuration',
                                                                        minutes
                                                                    );
                                                                }}
                                                                disabled={
                                                                    props.disabled ||
                                                                    !values.isMixedMeeting
                                                                }
                                                                placeholder="0:00"
                                                                className="form-control"
                                                                pattern="[0-9]+:[0-5][0-9]"
                                                            />
                                                        )}
                                                    </Col>
                                                )}
                                            </Row>
                                        </Row>
                                    </Row>

                                    {(values.isInPersonMeeting ||
                                        values.isMixedMeeting) && (
                                        <Row className="meeting-place-container">
                                            <Row className="meeting-place-header">
                                                <Col
                                                    md={12}
                                                    className="meeting-place-label">
                                                    Meeting Place
                                                </Col>
                                            </Row>
                                            <Row className="meeting-place-inputs">
                                                <Row className="meeting-place-row w-100">
                                                    <Col
                                                        xs={12}
                                                        lg={9}
                                                        className="meeting-place-field city-field">
                                                        <Label className="meeting-place-field-label">
                                                            City
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            name="city"
                                                            value={
                                                                values.city ||
                                                                ''
                                                            }
                                                            onChange={(e) => {
                                                                setFieldValue(
                                                                    'city',
                                                                    e.target
                                                                        .value
                                                                );
                                                            }}
                                                            onBlur={() =>
                                                                setFieldTouched(
                                                                    'city',
                                                                    true
                                                                )
                                                            }
                                                            disabled={
                                                                props.disabled ||
                                                                (effectiveReadonly &&
                                                                    !isEditMode)
                                                            }
                                                            placeholder="City"
                                                            className="form-control meeting-location-input"
                                                            style={{
                                                                fontSize:
                                                                    '14px',
                                                                fontFamily:
                                                                    '"Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                                                                fontWeight: 600,
                                                            }}
                                                            invalid={
                                                                !!(
                                                                    touched.city &&
                                                                    errors.city
                                                                )
                                                            }
                                                        />
                                                    </Col>
                                                    <Col
                                                        xs={12}
                                                        lg={3}
                                                        className={`meeting-place-field state-field ${touched.state && errors.state ? 'has-error' : ''}`}>
                                                        <StateSelectField
                                                            name="state"
                                                            value={values.state}
                                                            disabled={
                                                                props.disabled ||
                                                                (effectiveReadonly &&
                                                                    !isEditMode)
                                                            }
                                                            label="State"
                                                            placeholder=".."
                                                            className="meeting-location-input"
                                                            style={{
                                                                fontSize:
                                                                    '14px',
                                                                fontFamily:
                                                                    '"Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                                                                fontWeight: 600,
                                                            }}
                                                            onChange={(e) => {
                                                                setFieldValue(
                                                                    'state',
                                                                    e.target
                                                                        .value
                                                                );
                                                                setFieldTouched(
                                                                    'state',
                                                                    true
                                                                );
                                                            }}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className="meeting-place-row w-100">
                                                    <Col
                                                        md={12}
                                                        className="meeting-place-field">
                                                        <Label className="meeting-place-field-label">
                                                            Address
                                                        </Label>
                                                        <Input
                                                            type="text"
                                                            name="address"
                                                            value={
                                                                values.address ||
                                                                ''
                                                            }
                                                            onChange={(e) => {
                                                                setFieldValue(
                                                                    'address',
                                                                    e.target
                                                                        .value
                                                                );
                                                            }}
                                                            disabled={
                                                                props.disabled ||
                                                                (effectiveReadonly &&
                                                                    !isEditMode)
                                                            }
                                                            placeholder="Address"
                                                            className="form-control meeting-location-input"
                                                            style={{
                                                                fontSize:
                                                                    '14px',
                                                                fontFamily:
                                                                    '"Segoe UI", -apple-system, BlinkMacSystemFont, "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                                                                fontWeight: 600,
                                                            }}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Row>
                                        </Row>
                                    )}

                                    {isCancellationFeesEnabled &&
                                        (props.viewMode === 'availability' ||
                                            isSellerEditingProposal ||
                                            isSellerProposingAlternative) && (
                                            <>
                                                <div className="switcher-wrapper">
                                                    <div className="d-flex align-items-center">
                                                        <ReactSwitch
                                                            className="mr-2"
                                                            offColor="#d92550"
                                                            checked={
                                                                values.hasCancellationFee ||
                                                                false
                                                            }
                                                            onChange={(
                                                                checked
                                                            ) => {
                                                                setFieldValue(
                                                                    'hasCancellationFee',
                                                                    checked
                                                                );
                                                                if (checked) {
                                                                    if (
                                                                        !values.cancellationDeadlineVirtualHours
                                                                    )
                                                                        setFieldValue(
                                                                            'cancellationDeadlineVirtualHours',
                                                                            1
                                                                        );
                                                                    if (
                                                                        !values.cancellationDeadlineInPersonDays
                                                                    )
                                                                        setFieldValue(
                                                                            'cancellationDeadlineInPersonDays',
                                                                            1
                                                                        );
                                                                    if (
                                                                        !values.cancellationDeadlineMixedDays
                                                                    )
                                                                        setFieldValue(
                                                                            'cancellationDeadlineMixedDays',
                                                                            1
                                                                        );
                                                                }
                                                            }}
                                                            height={20}
                                                            width={42}
                                                            handleDiameter={14}
                                                            disabled={
                                                                props.disabled ||
                                                                (effectiveReadonly &&
                                                                    !isEditMode)
                                                            }
                                                        />
                                                        <span className="cancellation-fee-label">
                                                            Cancellation
                                                            Deadline
                                                        </span>
                                                    </div>
                                                </div>
                                                {values.hasCancellationFee && (
                                                    <div className="cancellation-deadline-section">
                                                        <div className="cancellation-deadline-header">
                                                            <div className="cancellation-deadline-format-label">
                                                                Meeting Type
                                                            </div>
                                                            <div className="cancellation-deadline-value-label">
                                                                Cancellation
                                                                Deadline
                                                            </div>
                                                        </div>
                                                        <div className="cancellation-deadline-options">
                                                            {values.isVirtualMeeting && (
                                                                <div className="cancellation-deadline-row">
                                                                    <div className="cancellation-deadline-format-name">
                                                                        Virtual
                                                                    </div>
                                                                    <div className="cancellation-deadline-input-item">
                                                                        {effectiveReadonly &&
                                                                        !isEditMode ? (
                                                                            <span className="cancellation-deadline-readonly">
                                                                                {values.cancellationDeadlineVirtualHours ??
                                                                                    1}{' '}
                                                                                hrs
                                                                            </span>
                                                                        ) : (
                                                                            <>
                                                                                <div className="duration-input-group">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="duration-button decrement"
                                                                                        onClick={() =>
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineVirtualHours',
                                                                                                Math.max(
                                                                                                    1,
                                                                                                    (values.cancellationDeadlineVirtualHours ||
                                                                                                        1) -
                                                                                                        1
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            props.disabled ||
                                                                                            (values.cancellationDeadlineVirtualHours ||
                                                                                                1) <=
                                                                                                1
                                                                                        }>
                                                                                        −
                                                                                    </button>
                                                                                    <input
                                                                                        type="text"
                                                                                        className="duration-input"
                                                                                        value={
                                                                                            values.cancellationDeadlineVirtualHours ??
                                                                                            ''
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) => {
                                                                                            const v =
                                                                                                parseInt(
                                                                                                    e
                                                                                                        .target
                                                                                                        .value,
                                                                                                    10
                                                                                                );
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineVirtualHours',
                                                                                                isNaN(
                                                                                                    v
                                                                                                )
                                                                                                    ? null
                                                                                                    : Math.min(
                                                                                                          96,
                                                                                                          Math.max(
                                                                                                              1,
                                                                                                              v
                                                                                                          )
                                                                                                      )
                                                                                            );
                                                                                        }}
                                                                                        disabled={
                                                                                            props.disabled
                                                                                        }
                                                                                        placeholder="1"
                                                                                    />
                                                                                    <button
                                                                                        type="button"
                                                                                        className="duration-button increment"
                                                                                        onClick={() =>
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineVirtualHours',
                                                                                                Math.min(
                                                                                                    96,
                                                                                                    (values.cancellationDeadlineVirtualHours ||
                                                                                                        0) +
                                                                                                        1
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            props.disabled ||
                                                                                            (values.cancellationDeadlineVirtualHours ||
                                                                                                0) >=
                                                                                                96
                                                                                        }>
                                                                                        +
                                                                                    </button>
                                                                                </div>
                                                                                <span className="cancellation-deadline-unit">
                                                                                    hrs
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {values.isInPersonMeeting && (
                                                                <div className="cancellation-deadline-row">
                                                                    <div className="cancellation-deadline-format-name">
                                                                        In-Person
                                                                    </div>
                                                                    <div className="cancellation-deadline-input-item">
                                                                        {effectiveReadonly &&
                                                                        !isEditMode ? (
                                                                            <span className="cancellation-deadline-readonly">
                                                                                {values.cancellationDeadlineInPersonDays ??
                                                                                    1}{' '}
                                                                                days
                                                                            </span>
                                                                        ) : (
                                                                            <>
                                                                                <div className="duration-input-group">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="duration-button decrement"
                                                                                        onClick={() =>
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineInPersonDays',
                                                                                                Math.max(
                                                                                                    1,
                                                                                                    (values.cancellationDeadlineInPersonDays ||
                                                                                                        1) -
                                                                                                        1
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            props.disabled ||
                                                                                            (values.cancellationDeadlineInPersonDays ||
                                                                                                1) <=
                                                                                                1
                                                                                        }>
                                                                                        −
                                                                                    </button>
                                                                                    <input
                                                                                        type="text"
                                                                                        className="duration-input"
                                                                                        value={
                                                                                            values.cancellationDeadlineInPersonDays ??
                                                                                            ''
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) => {
                                                                                            const v =
                                                                                                parseInt(
                                                                                                    e
                                                                                                        .target
                                                                                                        .value,
                                                                                                    10
                                                                                                );
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineInPersonDays',
                                                                                                isNaN(
                                                                                                    v
                                                                                                )
                                                                                                    ? null
                                                                                                    : Math.min(
                                                                                                          14,
                                                                                                          Math.max(
                                                                                                              1,
                                                                                                              v
                                                                                                          )
                                                                                                      )
                                                                                            );
                                                                                        }}
                                                                                        disabled={
                                                                                            props.disabled
                                                                                        }
                                                                                        placeholder="1"
                                                                                    />
                                                                                    <button
                                                                                        type="button"
                                                                                        className="duration-button increment"
                                                                                        onClick={() =>
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineInPersonDays',
                                                                                                Math.min(
                                                                                                    14,
                                                                                                    (values.cancellationDeadlineInPersonDays ||
                                                                                                        0) +
                                                                                                        1
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            props.disabled ||
                                                                                            (values.cancellationDeadlineInPersonDays ||
                                                                                                0) >=
                                                                                                14
                                                                                        }>
                                                                                        +
                                                                                    </button>
                                                                                </div>
                                                                                <span className="cancellation-deadline-unit">
                                                                                    days
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {values.isMixedMeeting && (
                                                                <div className="cancellation-deadline-row">
                                                                    <div className="cancellation-deadline-format-name">
                                                                        Hybrid
                                                                    </div>
                                                                    <div className="cancellation-deadline-input-item">
                                                                        {effectiveReadonly &&
                                                                        !isEditMode ? (
                                                                            <span className="cancellation-deadline-readonly">
                                                                                {values.cancellationDeadlineMixedDays ??
                                                                                    1}{' '}
                                                                                days
                                                                            </span>
                                                                        ) : (
                                                                            <>
                                                                                <div className="duration-input-group">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="duration-button decrement"
                                                                                        onClick={() =>
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineMixedDays',
                                                                                                Math.max(
                                                                                                    1,
                                                                                                    (values.cancellationDeadlineMixedDays ||
                                                                                                        1) -
                                                                                                        1
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            props.disabled ||
                                                                                            (values.cancellationDeadlineMixedDays ||
                                                                                                1) <=
                                                                                                1
                                                                                        }>
                                                                                        −
                                                                                    </button>
                                                                                    <input
                                                                                        type="text"
                                                                                        className="duration-input"
                                                                                        value={
                                                                                            values.cancellationDeadlineMixedDays ??
                                                                                            ''
                                                                                        }
                                                                                        onChange={(
                                                                                            e
                                                                                        ) => {
                                                                                            const v =
                                                                                                parseInt(
                                                                                                    e
                                                                                                        .target
                                                                                                        .value,
                                                                                                    10
                                                                                                );
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineMixedDays',
                                                                                                isNaN(
                                                                                                    v
                                                                                                )
                                                                                                    ? null
                                                                                                    : Math.min(
                                                                                                          14,
                                                                                                          Math.max(
                                                                                                              1,
                                                                                                              v
                                                                                                          )
                                                                                                      )
                                                                                            );
                                                                                        }}
                                                                                        disabled={
                                                                                            props.disabled
                                                                                        }
                                                                                        placeholder="1"
                                                                                    />
                                                                                    <button
                                                                                        type="button"
                                                                                        className="duration-button increment"
                                                                                        onClick={() =>
                                                                                            setFieldValue(
                                                                                                'cancellationDeadlineMixedDays',
                                                                                                Math.min(
                                                                                                    14,
                                                                                                    (values.cancellationDeadlineMixedDays ||
                                                                                                        0) +
                                                                                                        1
                                                                                                )
                                                                                            )
                                                                                        }
                                                                                        disabled={
                                                                                            props.disabled ||
                                                                                            (values.cancellationDeadlineMixedDays ||
                                                                                                0) >=
                                                                                                14
                                                                                        }>
                                                                                        +
                                                                                    </button>
                                                                                </div>
                                                                                <span className="cancellation-deadline-unit">
                                                                                    days
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {!values.isVirtualMeeting &&
                                                                !values.isInPersonMeeting &&
                                                                !values.isMixedMeeting && (
                                                                    <div className="cancellation-deadline-empty">
                                                                        Please
                                                                        select
                                                                        at least
                                                                        one
                                                                        meeting
                                                                        type to
                                                                        configure
                                                                        cancellation
                                                                        deadlines.
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    {props.viewMode === 'availability' && (
                                        <>
                                            {/* New Buyers Selector */}
                                            <BuyersSelector
                                                buyers={buyers}
                                                selectAll={
                                                    isSellerEditingProposal ||
                                                    isSellerProposingAlternative
                                                        ? true
                                                        : values.isSelectAllBuyers
                                                }
                                                onBuyerChange={
                                                    handleBuyerChange
                                                }
                                                onSelectAllChange={
                                                    handleSelectAllChange
                                                }
                                                onBuyerNameClick={
                                                    handleBuyerNameClick
                                                }
                                                disabled={
                                                    isSellerEditingProposal ||
                                                    isSellerProposingAlternative ||
                                                    props.disabled ||
                                                    isGhostMeeting ||
                                                    (!canCreateOrEditAvailability &&
                                                        props.viewMode ===
                                                            'availability' &&
                                                        !isEditMode)
                                                }
                                                readonly={
                                                    isGhostMeeting ||
                                                    isSellerEditingProposal ||
                                                    isSellerProposingAlternative ||
                                                    (effectiveReadonly &&
                                                        !isEditMode)
                                                }
                                                error={
                                                    buyerSelectionError
                                                        ? 'error'
                                                        : undefined
                                                }
                                                hideSelectAll={
                                                    isSellerEditingProposal ||
                                                    isSellerProposingAlternative
                                                }
                                            />

                                            {/* KEEPING OLD BUYER SELECTION FOR REFERENCE - DELETE LATER */}
                                            <Row
                                                className="buyer-selection-container"
                                                style={{ display: 'none' }}>
                                                <Row className="buyer-selection-header">
                                                    <Col
                                                        md={12}
                                                        className="buyer-selection-label">
                                                        Buyers
                                                    </Col>
                                                </Row>
                                                <Row className="buyer-selection-inputs">
                                                    <Col
                                                        md={12}
                                                        className="buyer-selection-field">
                                                        <div className="buyer-select-all-container">
                                                            <Input
                                                                type="checkbox"
                                                                name="isSelectAllBuyers"
                                                                checked={
                                                                    values.isSelectAllBuyers ||
                                                                    false
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    setFieldValue(
                                                                        'isSelectAllBuyers',
                                                                        e.target
                                                                            .checked
                                                                    );
                                                                    setBuyerSelectionError(
                                                                        false
                                                                    );
                                                                }}
                                                                disabled={
                                                                    props.disabled ||
                                                                    effectiveReadonly
                                                                }
                                                            />
                                                            <Label className="buyer-select-all-label">
                                                                Select All
                                                            </Label>
                                                        </div>
                                                    </Col>
                                                    <Col
                                                        md={12}
                                                        className="buyer-selection-field">
                                                        <div className="multiselect">
                                                            <Multiselect
                                                                open={
                                                                    isBuyersListOpened
                                                                }
                                                                onFocus={() => {
                                                                    setIsBuyersListOpened(
                                                                        true
                                                                    );
                                                                }}
                                                                onBlur={() => {
                                                                    setIsBuyersListOpened(
                                                                        false
                                                                    );
                                                                }}
                                                                filter="contains"
                                                                selectIcon={
                                                                    <ArrowDown />
                                                                }
                                                                data={
                                                                    buyerOptions
                                                                }
                                                                value={buyerOptions.filter(
                                                                    (x) =>
                                                                        values.selectedBuyerIds?.includes(
                                                                            x.id
                                                                        )
                                                                )}
                                                                className={
                                                                    buyerSelectionError
                                                                        ? 'av-invalid is-invalid'
                                                                        : ''
                                                                }
                                                                dataKey="id"
                                                                placeholder="Select Buyers"
                                                                disabled={
                                                                    props.disabled ||
                                                                    values.isSelectAllBuyers
                                                                }
                                                                textField="name"
                                                                onChange={(
                                                                    selectedBuyers
                                                                ) => {
                                                                    const ids =
                                                                        selectedBuyers.map(
                                                                            (
                                                                                x
                                                                            ) =>
                                                                                x.id
                                                                        );
                                                                    setFieldValue(
                                                                        'selectedBuyerIds',
                                                                        ids
                                                                    );
                                                                    if (
                                                                        ids.length >
                                                                        0
                                                                    ) {
                                                                        setBuyerSelectionError(
                                                                            false
                                                                        );
                                                                    }
                                                                    if (
                                                                        ids.length ===
                                                                        buyerOptions.length
                                                                    ) {
                                                                        setIsBuyersListOpened(
                                                                            false
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        </div>
                                                        {buyerSelectionError && (
                                                            <label className="error-message">
                                                                Either select
                                                                "Select All" or
                                                                at least one
                                                                buyer
                                                            </label>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </Row>
                                        </>
                                    )}

                                    <Row className="refund-fee-container"></Row>

                                    {props.viewMode !== 'availability' && (
                                        <Row className="description-container">
                                            <Col
                                                md={12}
                                                className="description-field">
                                                <Label className="description-label">
                                                    Meeting Description
                                                </Label>
                                                {!!(
                                                    effectiveReadonly &&
                                                    !isEditMode
                                                ) ? (
                                                    <div className="description-text">
                                                        {!values.message ||
                                                        values.message.trim() ===
                                                            ''
                                                            ? 'No description'
                                                            : values.message}
                                                    </div>
                                                ) : (
                                                    <Input
                                                        type="textarea"
                                                        name="message"
                                                        value={
                                                            values.message || ''
                                                        }
                                                        onChange={(e) => {
                                                            setFieldValue(
                                                                'message',
                                                                e.target.value
                                                            );
                                                        }}
                                                        disabled={
                                                            props.disabled ||
                                                            isGhostMeeting ||
                                                            (effectiveReadonly &&
                                                                !isEditMode)
                                                        }
                                                        maxLength={140}
                                                        placeholder="Enter Comment here"
                                                        className="form-control"
                                                        rows={4}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    )}

                                    {!isAvailabilityBlockObject(
                                        props.selectedEvent
                                    ) &&
                                        (props.selectedEvent as MeetingObject)
                                            ?.files?.length > 0 && (
                                            <Row className="documents-container">
                                                <Col
                                                    md={12}
                                                    className="documents-field">
                                                    <Label className="documents-label">
                                                        Documents
                                                    </Label>
                                                    <div className="documents-list">
                                                        {(
                                                            props.selectedEvent as MeetingObject
                                                        )?.files.map(
                                                            (file, index) => (
                                                                <div
                                                                    key={`file_${index}`}
                                                                    className="document-item">
                                                                    <span className="document-name">
                                                                        {
                                                                            file.name
                                                                        }
                                                                    </span>
                                                                    {canViewRecording() && (
                                                                        <FontAwesomeIcon
                                                                            className="download-icon"
                                                                            icon={
                                                                                faDownload
                                                                            }
                                                                            onClick={() => {
                                                                                handleDownloadFile(
                                                                                    props
                                                                                        .selectedEvent
                                                                                        ?.id ||
                                                                                        0,
                                                                                    file.id
                                                                                );
                                                                            }}
                                                                            title="Download file"
                                                                        />
                                                                    )}
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}

                                    {props.selectedEvent?.engagementId &&
                                        props.mode !== 'opportunity' &&
                                        props.mode !== 'engagement' &&
                                        (props.viewMode === 'meetingSeller' ||
                                            props.viewMode ===
                                                'meetingBuyer') && (
                                            <Row>
                                                <Col>
                                                    <span
                                                        className={`link-text ${props.disabled || isGhostMeeting ? 'disabled' : ''}`}
                                                        onClick={() => {
                                                            if (
                                                                !props.disabled &&
                                                                !isGhostMeeting
                                                            ) {
                                                                navigate(
                                                                    `/marketplace/meetings?engagementId=${props.selectedEvent?.engagementId}&meetingId=${props.selectedEvent?.id}`
                                                                );
                                                            }
                                                        }}>
                                                        Go to Opportunity
                                                    </span>
                                                </Col>
                                            </Row>
                                        )}

                                    <Row className="submit-buttons-container">
                                        {!!effectiveReadonly &&
                                            (props.viewMode ===
                                                'meetingBuyer' ||
                                                props.viewMode ===
                                                    'meetingSeller' ||
                                                props.viewMode ===
                                                    'availability') &&
                                            !isGhostMeeting && (
                                                <Col
                                                    md={12}
                                                    className="buttons-wrapper mb10">
                                                    {!isEngagementLocked &&
                                                        (
                                                            props.selectedEvent as MeetingObject
                                                        )?.status ===
                                                            EngagementMeetingStatus.InProgress &&
                                                        ((props.viewMode ===
                                                            'meetingSeller' &&
                                                            !isSellerTMWithoutCommsHub) ||
                                                            (props.viewMode ===
                                                                'availability' &&
                                                                party ===
                                                                    TimelineParty.Seller &&
                                                                !isSellerTMWithoutCommsHub) ||
                                                            (props.viewMode ===
                                                                'meetingBuyer' &&
                                                                canBuyerTMJoinMeeting)) && (
                                                            <Button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                disabled={
                                                                    props.disabled
                                                                }
                                                                onClick={
                                                                    handleJoinMeeting
                                                                }>
                                                                Join Meeting
                                                            </Button>
                                                        )}
                                                    {!isEngagementLocked &&
                                                        !isAvailabilityBlockObject(
                                                            props.selectedEvent
                                                        ) &&
                                                        (
                                                            props.selectedEvent as MeetingObject
                                                        )?.status ===
                                                            EngagementMeetingStatus.Completed &&
                                                        (
                                                            props.selectedEvent as MeetingObject
                                                        )
                                                            ?.isRecordingAvailable &&
                                                        canViewRecording() &&
                                                        !isSellerTMWithoutCommsHub && (
                                                            <Button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                disabled={
                                                                    props.disabled
                                                                }
                                                                onClick={() =>
                                                                    fetchRecordingAndOpen(
                                                                        props
                                                                            .selectedEvent
                                                                            ?.id ||
                                                                            0,
                                                                        props
                                                                            .selectedEvent
                                                                            ?.engagementId
                                                                    )
                                                                }>
                                                                View Recording
                                                            </Button>
                                                        )}
                                                    {!isEngagementLocked &&
                                                        !isAvailabilityBlockObject(
                                                            props.selectedEvent
                                                        ) &&
                                                        (
                                                            props.selectedEvent as MeetingObject
                                                        )?.status ===
                                                            EngagementMeetingStatus.Completed &&
                                                        (
                                                            props.selectedEvent as MeetingObject
                                                        )
                                                            ?.isRecordingAvailable &&
                                                        canViewRecording() &&
                                                        !isSellerTMWithoutCommsHub && (
                                                            <Button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                disabled={
                                                                    props.disabled
                                                                }
                                                                onClick={() =>
                                                                    setIsClipsPopupVisible(
                                                                        true
                                                                    )
                                                                }>
                                                                See Meeting
                                                                Clips
                                                            </Button>
                                                        )}
                                                </Col>
                                            )}
                                        <Col
                                            md={12}
                                            className="buttons-wrapper">
                                            {!isGhostMeeting && (
                                                <>
                                                    {effectiveReadonly &&
                                                        !isEditMode &&
                                                        props.viewMode ===
                                                            'availability' &&
                                                        isAvailabilityBlockObject(
                                                            props.selectedEvent
                                                        ) &&
                                                        canCreateOrEditAvailability &&
                                                        !isSellerTMWithoutCommsHub && (
                                                            <>
                                                                <Button
                                                                    type="button"
                                                                    className="btn btn-delete"
                                                                    onClick={() =>
                                                                        setShowDeleteConfirmation(
                                                                            true
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        props.disabled
                                                                    }>
                                                                    <FontAwesomeIcon
                                                                        icon={
                                                                            faTrash
                                                                        }
                                                                    />
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    className="btn btn-primary"
                                                                    onClick={() =>
                                                                        setIsEditMode(
                                                                            true
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        props.disabled
                                                                    }>
                                                                    Edit
                                                                    Availability
                                                                </Button>
                                                            </>
                                                        )}
                                                    {(props.mode ===
                                                        'engagement' ||
                                                        (props.mode ===
                                                            'opportunity' &&
                                                            party ===
                                                                TimelineParty.Buyer)) &&
                                                        props.selectedEvent &&
                                                        !isAvailabilityBlockObject(
                                                            props.selectedEvent
                                                        ) &&
                                                        (
                                                            props.selectedEvent as MeetingObject
                                                        ).isMeetingProposal ===
                                                            true &&
                                                        effectiveReadonly &&
                                                        !isEditMode &&
                                                        ((party ===
                                                            TimelineParty.Seller &&
                                                            (
                                                                props.selectedEvent as MeetingObject
                                                            ).proposalStatus ===
                                                                MeetingProposalStatus.PendingSellerAnswer) ||
                                                            (party ===
                                                                TimelineParty.Buyer &&
                                                                (
                                                                    props.selectedEvent as MeetingObject
                                                                )
                                                                    .proposalStatus ===
                                                                    MeetingProposalStatus.PendingBuyerAnswer)) && (
                                                            <Button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={() =>
                                                                    setIsEditMode(
                                                                        true
                                                                    )
                                                                }
                                                                disabled={
                                                                    props.disabled
                                                                }>
                                                                Edit Proposal
                                                            </Button>
                                                        )}
                                                    {(props.mode ===
                                                        'engagement' ||
                                                        (props.mode ===
                                                            'opportunity' &&
                                                            party ===
                                                                TimelineParty.Buyer)) &&
                                                        isEditMode &&
                                                        props.selectedEvent &&
                                                        !isAvailabilityBlockObject(
                                                            props.selectedEvent
                                                        ) &&
                                                        (
                                                            props.selectedEvent as MeetingObject
                                                        ).isMeetingProposal ===
                                                            true &&
                                                        (
                                                            props.selectedEvent as MeetingObject
                                                        ).meetingProposalId &&
                                                        (!termsMatch ||
                                                            props.hasConfirmedMeeting) &&
                                                        (canBuyerTeamMemberManageMeetings(
                                                            summary,
                                                            props.engagementId
                                                        ) ||
                                                            canSellerTeamMemberManageMeetings(
                                                                summary,
                                                                props.engagementId
                                                            )) && (
                                                            <ButtonLoader
                                                                type="button"
                                                                buttonText="Propose Meeting"
                                                                loaderButtonText="Proposing..."
                                                                isLoading={
                                                                    isSubmitting
                                                                }
                                                                disabled={
                                                                    props.disabled ||
                                                                    isSubmitting ||
                                                                    !values.startTime ||
                                                                    !values.endTime ||
                                                                    !props.selectedDate
                                                                }
                                                                className="btn btn-primary btn-primary-orange"
                                                                onClick={() =>
                                                                    handleUpdateProposal()
                                                                }
                                                            />
                                                        )}
                                                    {isBuyerInAgreeOnMeetingTimesPhase &&
                                                        !(
                                                            props.mode ===
                                                                'opportunity' &&
                                                            (props.buyerHasPendingProposal ||
                                                                props.hasConfirmedMeeting) &&
                                                            effectiveReadonly &&
                                                            isAvailabilityBlockObject(
                                                                props.selectedEvent
                                                            )
                                                        ) &&
                                                        termsMatch &&
                                                        !props.hasConfirmedMeeting &&
                                                        (canBuyerTeamMemberManageMeetings(
                                                            summary,
                                                            props.engagementId
                                                        ) ||
                                                            canSellerTeamMemberManageMeetings(
                                                                summary,
                                                                props.engagementId
                                                            )) && (
                                                            <ButtonLoader
                                                                type="button"
                                                                buttonText="Schedule Meeting"
                                                                loaderButtonText="Scheduling..."
                                                                isLoading={
                                                                    isSubmitting
                                                                }
                                                                disabled={
                                                                    props.disabled ||
                                                                    isSubmitting ||
                                                                    !values.startTime ||
                                                                    !values.endTime ||
                                                                    !props.selectedDate
                                                                }
                                                                className="btn btn-primary"
                                                                onClick={() =>
                                                                    handleScheduleMeeting()
                                                                }
                                                            />
                                                        )}
                                                    {isBuyerInAgreeOnMeetingTimesPhase &&
                                                        !(
                                                            props.mode ===
                                                                'opportunity' &&
                                                            (props.buyerHasPendingProposal ||
                                                                props.hasConfirmedMeeting) &&
                                                            effectiveReadonly &&
                                                            isAvailabilityBlockObject(
                                                                props.selectedEvent
                                                            )
                                                        ) &&
                                                        (!termsMatch ||
                                                            props.hasConfirmedMeeting) &&
                                                        !(
                                                            props.selectedEvent &&
                                                            !isAvailabilityBlockObject(
                                                                props.selectedEvent
                                                            ) &&
                                                            (
                                                                props.selectedEvent as MeetingObject
                                                            )
                                                                .isMeetingProposal ===
                                                                true &&
                                                            (
                                                                props.selectedEvent as MeetingObject
                                                            ).meetingProposalId
                                                        ) &&
                                                        (canBuyerTeamMemberManageMeetings(
                                                            summary,
                                                            props.engagementId
                                                        ) ||
                                                            canSellerTeamMemberManageMeetings(
                                                                summary,
                                                                props.engagementId
                                                            )) && (
                                                            <ButtonLoader
                                                                type="button"
                                                                buttonText="Propose Meeting"
                                                                loaderButtonText="Proposing..."
                                                                isLoading={
                                                                    isSubmitting
                                                                }
                                                                disabled={
                                                                    props.disabled ||
                                                                    isSubmitting ||
                                                                    !values.startTime ||
                                                                    !values.endTime ||
                                                                    (party ===
                                                                        TimelineParty.Seller &&
                                                                        !values
                                                                            .selectedBuyerIds
                                                                            ?.length) ||
                                                                    !props.selectedDate
                                                                }
                                                                className="btn btn-primary btn-primary-orange"
                                                                onClick={() =>
                                                                    handleScheduleMeeting()
                                                                }
                                                            />
                                                        )}
                                                    {isSellerProposingAlternative && (
                                                        <ButtonLoader
                                                            type="button"
                                                            buttonText="Propose Meeting"
                                                            loaderButtonText="Proposing..."
                                                            isLoading={
                                                                isSubmitting
                                                            }
                                                            disabled={
                                                                props.disabled ||
                                                                isSubmitting ||
                                                                !values.startTime ||
                                                                !values.endTime ||
                                                                !values
                                                                    .selectedBuyerIds
                                                                    ?.length ||
                                                                !props.selectedDate
                                                            }
                                                            className="btn btn-primary btn-primary-orange"
                                                            onClick={() =>
                                                                handleScheduleMeeting()
                                                            }
                                                        />
                                                    )}
                                                    {(!effectiveReadonly ||
                                                        isEditMode) &&
                                                        props.viewMode ===
                                                            'availability' &&
                                                        canCreateOrEditAvailability &&
                                                        !isSellerTMWithoutCommsHub &&
                                                        !isSellerProposingAlternative &&
                                                        !isSellerEditingProposal && (
                                                            <ButtonLoader
                                                                type="submit"
                                                                buttonText={
                                                                    isEditMode
                                                                        ? 'Update Availability'
                                                                        : getPrimaryButtonText()
                                                                }
                                                                loaderButtonText={
                                                                    isEditMode
                                                                        ? 'Updating...'
                                                                        : 'Adding...'
                                                                }
                                                                isLoading={
                                                                    isSubmitting
                                                                }
                                                                disabled={
                                                                    props.disabled
                                                                }
                                                                className="btn btn-primary"
                                                                onClick={() => {
                                                                    formRef.current?.handleSubmit();
                                                                }}
                                                            />
                                                        )}
                                                </>
                                            )}
                                            <Button
                                                type="button"
                                                className="cancel btn btn-secondary"
                                                disabled={
                                                    props.disabled ||
                                                    isSubmitting
                                                }
                                                onClick={() => {
                                                    if (
                                                        isEditMode &&
                                                        !isGhostMeeting
                                                    ) {
                                                        // In edit mode: reset form, validation errors, and exit edit mode
                                                        resetForm();
                                                        setMeetingTypeError(
                                                            false
                                                        );
                                                        setBuyerSelectionError(
                                                            false
                                                        );
                                                        setIsEditMode(false);

                                                        // Reset date to original availability block date
                                                        const selectedEvent =
                                                            props.selectedEvent as AvailabilityBlockObject;
                                                        if (
                                                            selectedEvent?.dateStart &&
                                                            props.onDateReset
                                                        ) {
                                                            const originalDate =
                                                                new Date(
                                                                    selectedEvent.dateStart
                                                                );
                                                            props.onDateReset(
                                                                originalDate
                                                            );
                                                        }
                                                    } else {
                                                        // Not in edit mode or ghost meeting: close the editor
                                                        props.onDeselectEvent?.();
                                                        props.onDeselectDate?.();
                                                        // When seller was proposing alternative meeting, cancel that process (clear prefill)
                                                        if (
                                                            props.meetingPrefillData
                                                        ) {
                                                            props.onClearPrefillData?.();
                                                        }
                                                    }
                                                }}>
                                                {isGhostMeeting ||
                                                (effectiveReadonly &&
                                                    !isEditMode)
                                                    ? 'Close'
                                                    : 'Cancel'}
                                            </Button>
                                        </Col>
                                    </Row>
                                </>
                            );
                        }}
                    </Formik>
                </div>
            ) : null}

            {isConferenceMeetingPopupVisible && !!props.selectedEvent && (
                <ConferenceMeetingPopup
                    contextId={props.selectedEvent.engagementId || 0}
                    type={
                        props.selectedEvent.engagementId
                            ? 'marketplace'
                            : 'marketplaceSupport'
                    }
                    isAdminBuyerSupport={
                        (props.selectedEvent as MeetingObject)
                            .participantsType ===
                        MeetingParticipantsType.BuyerAdminSupport
                    }
                    conference={props.selectedEvent}
                    onClose={() => {
                        setIsConferenceMeetingPopupVisible(false);
                        if (props.onRefreshMeetings) {
                            setTimeout(() => props.onRefreshMeetings!(), 2500);
                        }
                    }}
                />
            )}

            {props.selectedEvent && !!videoSource && (
                <VideoPlayerPopup
                    title={props.selectedEvent.name}
                    src={videoSource}
                    onClose={() => {
                        setVideoSource(undefined);
                    }}
                    isRecordingEnabled={true}
                    completeRecording={(startTime: number, endTime: number) => {
                        if (Math.abs(endTime - startTime) < 15) {
                            createNotification(
                                'Minimal Video Clip duration is 15 seconds. Please try again.',
                                'error'
                            );
                            return;
                        }
                        setVideoClip({
                            timeStartSeconds: startTime,
                            timeEndSeconds: endTime,
                            conferenceId: Number(props.selectedEvent?.id),
                        });
                    }}
                />
            )}

            <MeetingClipsModal
                isVisible={isClipsPopupVisible}
                onClose={() => setIsClipsPopupVisible(false)}
                selectedEvent={props.selectedEvent}
                loadingTrigger={loadingTrigger}
                onClipSave={handleClipSave}
                isReadOnlyMode={!canViewRecording()}
            />

            {!!videoClip && (
                <VideoClipPopup
                    clip={videoClip}
                    groupId={props.selectedEvent?.engagementId || 0}
                    reset={() => setVideoClip(undefined)}
                    onClipSave={() => {
                        handleClipSave();
                    }}
                    isSupportMode={!props.selectedEvent.engagementId}
                    type="marketplace"
                />
            )}

            {isBuyerProfilePopupVisible && (
                <BuyerProfilePreviewPopup
                    buyerId={selectedBuyerId}
                    close={() => setIsBuyerProfilePopupVisible(false)}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmation && (
                <ConfirmationModal
                    isVisible={showDeleteConfirmation}
                    setIsVisible={setShowDeleteConfirmation}
                    header="Delete Availability Block"
                    confirmationText="Are you sure you want to delete this availability block? This action cannot be undone."
                    onConfirm={handleDeleteAvailabilityBlock}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    isLoading={isDeleting}
                    closeOnConfirm={false}
                    isConfirmationDanger={false}
                />
            )}

            <SaveMeetingCardModal
                isVisible={isSaveCardModalVisible}
                meetingFee={meetingFee}
                isBuyerTm={summary?.roleId === USER_ROLES.PRACTICE_SECONDARY_USER}
                onClose={() => {
                    setIsSaveCardModalVisible(false);
                    setPendingProposalBody(null);
                }}
                onAuthorize={async (data: MeetingPaymentMethodData) => {
                    setIsSaveCardModalVisible(false);
                    if (pendingProposalBody) {
                        const body = pendingProposalBody;
                        setPendingProposalBody(null);
                        const engagementId =
                            props.engagementId ?? currentEngagement?.id;
                        setIsSubmitting(true);
                        try {
                            const response = await axios.post(
                                `/api/Marketplace/Engagements/${engagementId}/MeetingScheduling`,
                                { ...body, ...data }
                            );
                            if (
                                response.status === 200 ||
                                response.status === 201
                            ) {
                                createNotification(
                                    'Meeting proposed successfully',
                                    'success'
                                );
                                const proposalId =
                                    response.data?.id != null
                                        ? String(response.data.id)
                                        : typeof response.data === 'string'
                                          ? response.data
                                          : undefined;
                                props.onRefreshMeetings?.(
                                    undefined,
                                    proposalId
                                );
                                if (!proposalId) {
                                    props.onDeselectEvent?.();
                                    props.onDeselectDate?.();
                                }
                            } else {
                                createNotification(
                                    'Could not submit meeting proposal',
                                    'error'
                                );
                            }
                        } catch {
                            createNotification(
                                'An error occurred while proposing the meeting',
                                'error'
                            );
                        } finally {
                            setIsSubmitting(false);
                        }
                    }
                }}
            />
        </div>
    );
};
